import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Trophy,
  Users,
  ClipboardList,
  Printer,
  Plus,
  Trash2,
  Medal,
  Flag,
  Share2,
  MessageCircle,
  UserPlus,
  UserMinus,
  Ban,
  Send,
  RefreshCw,
  Cloud,
  Lock,
  Unlock,
  Eraser,
  Sparkles,
  BarChart3,
  History,
  Pencil,
  ArrowLeftRight,
} from 'lucide-react';
import {
  GRUPOS_COPA_2026,
  JOGOS_COPA_2026,
  type GrupoCopaId,
  type RodadaGrupo,
  nomeTime,
  jogosDoGrupoId,
} from '../../data/copaMundo2026';
import { bandeiraTime, htmlTimeImpressao, urlBandeira } from '../../utils/copaBandeiras';
import {
  calcularClassificacaoGrupo,
  contarPalpitesGruposValidos,
  contarPalpitesMMValidos,
  fasesGruposEncerrada,
  grupoEstaCompleto,
  placarValido,
  placarEmpatadoMM,
  extrairPalpitesGrupos,
  extrairPalpitesMM,
  mesclarPalpitesGruposEMM,
  normalizarPlacaresImportados,
  pontuacaoParticipante,
  acertouVencedorMataMata,
  pontosJogo,
  pontosJogoMataMata,
  resultado1x2,
  vencedorRealMataMata,
  type LadoPlacarMM,
  type Placar,
  type PlacaresMap,
  type VencedoresPenaltisMMMap,
} from '../../utils/copaCalculo';
import { decodePalpitesUrl } from '../../utils/copaCodec';
import {
  endpointGasValido,
  fetchGasGet,
  fetchGasPost,
} from '../../utils/googleAppsScriptFetch';
import { gerarPalpitesAutomaticos } from '../../utils/autoPreencherPalpites';
import {
  calcularContagemAutoManual,
  rotuloContagem,
  tituloContagem,
} from '../../utils/contagemAutoManual';
import {
  calcularPremiacao,
  formatarReais,
} from '../../utils/copaPremiacao';
import {
  carregarHistoricoBolao,
  carregarHistoricoBolaoIDB,
  sincronizarHistoricoBolao,
} from '../../utils/historicoBolaoCopa';
import { HistoricoBolaoCopaPanel } from './HistoricoBolaoCopa';
import {
  avaliarFimBolao,
  montarMensagensFinais,
  type MensagemFinal,
} from '../../utils/mensagensFinaisBolao';
import {
  TabelaClassificacaoBolao,
  type LinhaClassificacaoBolao,
} from './TabelaClassificacaoBolao';
import {
  calcularEstatisticasPosJogo,
  type AcertoJogo,
  type EstatisticasPosJogo,
  type JogoInfoStats,
} from '../../utils/estatisticasPosJogo';
import {
  calcularEliminacaoMatematica,
  type EliminacaoMatematicaCopa,
} from '../../utils/eliminacaoMatematicaCopa';
import {
  FASES_MATA_MATA,
  JOGOS_MATA_MATA,
  NOME_FASE,
  PONTUACAO_POR_FASE,
  jogosDaFase,
  type FaseMataMata,
  type JogoMataMata,
  type OrigemLado,
} from '../../data/mataMataCopa2026';
import { nomeTime as nomeTimeBase } from '../../data/copaMundo2026';
import {
  atribuirMelhoresTerceirosAutomatico,
  listarInfoSlotsMelhor3o,
  opcoesParaSlotMelhor3o,
} from '../../utils/copaMelhoresTerceiros';
import { formatarDataHoraIsoBrt } from '../../utils/copaMataMataPublico';
import { resolverLadoMataMataDePlacares } from '../../utils/copaResolverMataMata';
import {
  carregarConfigMMLocal,
  restaurarConfigMM,
  salvarConfigMMRobusto,
} from '../../utils/adePersistenciaCopaMM';

const STORAGE_KEY = 'copa-pool-ade-v2';
const STORAGE_KEY_IGNORADOS = 'copa-ids-ignorados-ade';
/** Cache local dos palpites MM já baixados da planilha (por nome|setor). */
const STORAGE_KEY_MM_SYNC_CACHE = 'copa-palpites-mm-sync-cache-ade';

/** IndexedDB para persistência ROBUSTA do estado da Copa (participantes +
 *  resultados reais). Usado como BACKUP do localStorage — o NW.js pode
 *  resetar o localStorage em algumas situações (limpeza de cache do Chromium,
 *  troca de versão, etc.), então precisamos de uma camada mais durável. */
const COPA_IDB_NAME = 'ade-copa-db';
const COPA_IDB_VERSION = 1;
const COPA_IDB_STORE = 'copaEstado';
const COPA_IDB_KEY = 'estadoAtual';

/** Normaliza nome para comparação: remove acentos, lower, colapsa espaços. */
function normalizarNome(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

interface IdIgnorado {
  id: string;
  ignoradoEm: string; // ISO
  /** Bloqueia reimportação pelo mesmo nome+setor mesmo com id novo na planilha */
  nomeNorm?: string;
  setorNorm?: string;
  /** Rótulos amigáveis pra mostrar na UI (sem normalização) */
  nomeOriginal?: string;
  setorOriginal?: string;
  /**
   * Se true (padrão pós-fix), bloqueia para sempre até o usuário liberar manualmente.
   * Se false, usa a lógica antiga baseada em datas (reenvio depois do ignore desbloqueia).
   */
  permanente?: boolean;
}

function chaveNomeSetor(nome: string, setor?: string): string {
  return `${normalizarNome(nome)}|${normalizarNome(setor || '')}`;
}

function carregarCacheMMSync(): Record<string, PlacaresMap> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MM_SYNC_CACHE);
    if (!raw) return {};
    const p = JSON.parse(raw) as unknown;
    if (!p || typeof p !== 'object') return {};
    const out: Record<string, PlacaresMap> = {};
    Object.keys(p as Record<string, unknown>).forEach((chave) => {
      out[chave] = extrairPalpitesMM((p as Record<string, PlacaresMap>)[chave] || {});
    });
    return out;
  } catch {
    return {};
  }
}

function salvarCacheMMSync(cache: Record<string, PlacaresMap>): void {
  try {
    localStorage.setItem(STORAGE_KEY_MM_SYNC_CACHE, JSON.stringify(cache));
  } catch {
    /* quota */
  }
}

function atualizarCacheMMSync(mapaMMPorChave: Map<string, PlacaresMap>): void {
  if (mapaMMPorChave.size === 0) return;
  const cache = carregarCacheMMSync();
  mapaMMPorChave.forEach((mmPlanilha, chave) => {
    const mmNorm = extrairPalpitesMM(normalizarPlacaresImportados(mmPlanilha));
    if (Object.keys(mmNorm).length === 0) return;
    cache[chave] = { ...(cache[chave] || {}), ...mmNorm };
  });
  salvarCacheMMSync(cache);
}

function aplicarCacheMMNosParticipantes(parts: ParticipanteCopa[]): ParticipanteCopa[] {
  const cache = carregarCacheMMSync();
  if (!Object.keys(cache).length) return parts;
  return parts.map((p) => {
    const mm = cache[chaveNomeSetor(p.nome, p.setor)];
    if (!mm || Object.keys(mm).length === 0) return p;
    return {
      ...p,
      palpites: mesclarPalpitesGruposEMM(extrairPalpitesGrupos(p.palpites), mm, p.palpites),
    };
  });
}

function contarMMTotalParticipantes(parts: ParticipanteCopa[]): number {
  return parts.reduce((s, p) => s + contarPalpitesMMValidos(p.palpites), 0);
}

function fasePlanilhaNormalizada(fase?: string): string {
  return String(fase || 'grupos').trim().toLowerCase();
}

function parseEmMs(s: string): number {
  if (!s) return 0;
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function carregarIdsIgnorados(): IdIgnorado[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_IGNORADOS);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr
      .map((x) => {
        if (typeof x === 'string') return { id: x, ignoradoEm: '', permanente: true };
        if (x && typeof x === 'object' && typeof (x as IdIgnorado).id === 'string') {
          const obj = x as IdIgnorado;
          return {
            id: obj.id,
            ignoradoEm: String(obj.ignoradoEm || ''),
            nomeNorm: typeof obj.nomeNorm === 'string' ? obj.nomeNorm : undefined,
            setorNorm: typeof obj.setorNorm === 'string' ? obj.setorNorm : undefined,
            nomeOriginal:
              typeof obj.nomeOriginal === 'string' ? obj.nomeOriginal : undefined,
            setorOriginal:
              typeof obj.setorOriginal === 'string' ? obj.setorOriginal : undefined,
            permanente: obj.permanente !== false,
          };
        }
        return null;
      })
      .filter((x): x is IdIgnorado => x !== null);
  } catch {
    return [];
  }
}

function salvarIdsIgnoradosImediato(lista: IdIgnorado[]): void {
  try {
    localStorage.setItem(STORAGE_KEY_IGNORADOS, JSON.stringify(lista));
  } catch {
    /* quota */
  }
}

/** Prazo final para palpites: 11/06/2026 00:01 BRT = 03:01 UTC */
const PRAZO_PALPITES_MS = Date.UTC(2026, 5, 11, 3, 1, 0);

function palpitesEncerrados(now: number = Date.now()): boolean {
  return now >= PRAZO_PALPITES_MS;
}

function formatarPrazoBrt(): string {
  return '11/06/2026 às 00:01 (horário de Brasília)';
}
const STORAGE_KEY_LEGADO = 'copa-pool-ade-v1';
const STORAGE_KEY_QR = 'copa-qr-pagamento-ade';
const STORAGE_KEY_ORG = 'copa-organizador-ade';
/** Domínio oficial do bolão (Cloudflare Pages). */
const URL_PUBLICA_BOLAO_OFICIAL = 'https://detudoumpoucomais.com.br';
const STORAGE_KEY_CONV = 'copa-convidados-ade';
const STORAGE_KEY_SHEETS = 'copa-sheets-endpoint-ade';
const STORAGE_KEY_RECEBIDOS = 'copa-sheets-recebidos-ade';
const STORAGE_KEY_DATA_CORTE = 'copa-data-corte-ade';

function carregarDataCorte(): string {
  try {
    return localStorage.getItem(STORAGE_KEY_DATA_CORTE) || '';
  } catch {
    return '';
  }
}

function salvarDataCorte(iso: string): void {
  try {
    if (iso) localStorage.setItem(STORAGE_KEY_DATA_CORTE, iso);
    else localStorage.removeItem(STORAGE_KEY_DATA_CORTE);
  } catch {
    /* ignore */
  }
}

/** Carimbo da última versão dos resultados oficiais sincronizada com a nuvem. */
const STORAGE_KEY_RESULTADOS_NUVEM_EM = 'copa-resultados-nuvem-em-ade';

/** Jogos cujo placar o organizador digitou/corrigiu — a API não sobrescreve. */
type ResultadosManuaisMap = Record<string, boolean>;

type ResultadosNuvemPayload = {
  ok?: boolean;
  atualizadoEm?: string;
  resultadosReais?: PlacaresMap;
  vencedoresPenaltisMM?: VencedoresPenaltisMMMap;
  manuais?: ResultadosManuaisMap;
  ultimaApiEm?: string;
  ultimaApiLog?: string;
  erro?: string;
};

function carregarResultadosNuvemEm(): string {
  try {
    return localStorage.getItem(STORAGE_KEY_RESULTADOS_NUVEM_EM) || '';
  } catch {
    return '';
  }
}

function salvarResultadosNuvemEm(iso: string): void {
  try {
    if (iso) localStorage.setItem(STORAGE_KEY_RESULTADOS_NUVEM_EM, iso);
    else localStorage.removeItem(STORAGE_KEY_RESULTADOS_NUVEM_EM);
  } catch {
    /* ignore */
  }
}

function resultadosLocaisDiferemDoCloud(
  local: PlacaresMap,
  penaltis: VencedoresPenaltisMMMap,
  manuais: ResultadosManuaisMap,
  cloud: {
    resultadosReais?: PlacaresMap;
    vencedoresPenaltisMM?: VencedoresPenaltisMMMap;
    manuais?: ResultadosManuaisMap;
  }
): boolean {
  return (
    JSON.stringify(local) !== JSON.stringify(cloud.resultadosReais ?? {}) ||
    JSON.stringify(penaltis) !== JSON.stringify(cloud.vencedoresPenaltisMM ?? {}) ||
    JSON.stringify(manuais) !== JSON.stringify(cloud.manuais ?? {})
  );
}

/** Nuvem tem placar que falta no PC ou difere (exceto jogos marcados como manuais). */
function nuvemTemPlacaresNovosParaLocal(
  local: PlacaresMap,
  manuais: ResultadosManuaisMap,
  cloud: { resultadosReais?: PlacaresMap }
): boolean {
  const cr = cloud.resultadosReais ?? {};
  return Object.keys(cr).some((id) => {
    if (manuais[id]) return false;
    const c = cr[id];
    const l = local[id];
    if (!placarValido(c)) return false;
    if (!placarValido(l)) return true;
    return l.mandante !== c.mandante || l.visitante !== c.visitante;
  });
}

const criarId = () => `copa-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

interface OrganizadorInfo {
  nome: string;
  whats: string;
  /** Endereço https onde o formulário público está hospedado (obrigatório no app desktop). */
  urlPublica?: string;
}

function normalizarUrlPublica(raw: string): string {
  let u = raw.trim();
  if (!u) return '';
  if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
  return u.replace(/\/+$/, '');
}

/** Netlify antigo ou hosts obsoletos → domínio oficial. */
function hostUrlPublicaAntiga(url: string): boolean {
  const u = normalizarUrlPublica(url).toLowerCase();
  if (!u) return false;
  return u.includes('netlify.app') || u.includes('phenomenal-brioche');
}

function resolverUrlPublicaOrganizador(url: string): string {
  const n = normalizarUrlPublica(url);
  if (hostUrlPublicaAntiga(n)) return URL_PUBLICA_BOLAO_OFICIAL;
  return n;
}

/** Base do link compartilhável. No NW.js (file://) só funciona com urlPublica configurada. */
function obterBaseLinkPublico(org: OrganizadorInfo): string | null {
  const custom = resolverUrlPublicaOrganizador(org.urlPublica || '');
  if (custom) return custom;
  try {
    const origin = window.location.origin;
    if (/^https?:\/\//i.test(origin)) {
      const path = window.location.pathname
        .replace(/\/index\.html$/i, '')
        .replace(/\/+$/, '');
      return `${origin}${path}`;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export interface Convidado {
  id: string;
  nome: string;
  whats: string;
}

function carregarOrganizador(): OrganizadorInfo {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ORG);
    if (!raw) return { nome: '', whats: '' };
    const p = JSON.parse(raw) as Partial<OrganizadorInfo>;
    const urlBruta = typeof p.urlPublica === 'string' ? p.urlPublica : '';
    return {
      nome: typeof p.nome === 'string' ? p.nome : '',
      whats: typeof p.whats === 'string' ? p.whats : '',
      urlPublica: resolverUrlPublicaOrganizador(urlBruta),
    };
  } catch {
    return { nome: '', whats: '', urlPublica: '' };
  }
}

function carregarConvidados(): Convidado[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CONV);
    if (!raw) return [];
    const arr = JSON.parse(raw) as Convidado[];
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((c) => c && typeof c.nome === 'string' && typeof c.whats === 'string')
      .map((c) => ({
        id: c.id || criarId(),
        nome: c.nome,
        whats: c.whats,
      }));
  } catch {
    return [];
  }
}

function carregarSheetsEndpoint(): string {
  try {
    const v = (localStorage.getItem(STORAGE_KEY_SHEETS) ?? '').trim();
    if (v && !endpointGasValido(v)) {
      localStorage.removeItem(STORAGE_KEY_SHEETS);
      return '';
    }
    return v;
  } catch {
    return '';
  }
}

function carregarIdsRecebidos(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_RECEBIDOS);
    if (!raw) return [];
    const arr = JSON.parse(raw) as string[];
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export interface ConfigMataMata {
  /** TimeID alocado para cada slot M3-1..M3-8 dos melhores 3ºs. */
  melhoresTerceiros: Record<number, string>;
  /**
   * Sobrescrita do chaveamento: para cada jogo de 16 avos (id), permite trocar
   * o time do ladoA ou ladoB. Útil quando o organizador quer reorganizar manualmente.
   */
  cruzamentos16: Record<string, { ladoA?: string; ladoB?: string }>;
  /** Mata-mata foi liberado para palpites públicos? */
  liberadoParaPalpites: boolean;
}

function carregarConfigMM(): ConfigMataMata {
  return carregarConfigMMLocal();
}

export interface ParticipanteCopa {
  id: string;
  nome: string;
  setor?: string;
  palpites: PlacaresMap;
  criadoEm: string;
  pagou?: boolean;
  bloqueado?: boolean;
  origemId?: string;
  /** ISO do enviadoEm da ultima sync (para detectar mudancas no Sheets). */
  ultimaSyncEnviadoEm?: string;
  /** Quantidade de faltas no trabalho (1 dia = 9 horas). Usado no cálculo da premiação. */
  faltas?: number;
}

interface CopaPoolPersist {
  resultadosReais: PlacaresMap;
  participantes: ParticipanteCopa[];
  vencedoresPenaltisMM?: VencedoresPenaltisMMMap;
  resultadosManuais?: ResultadosManuaisMap;
}

const RODADAS: RodadaGrupo[] = [1, 2, 3];

function carregarEstado(): CopaPoolPersist {
  const vazio: CopaPoolPersist = {
    resultadosReais: {},
    participantes: [],
    vencedoresPenaltisMM: {},
    resultadosManuais: {},
  };
  try {
    const raw =
      localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(STORAGE_KEY_LEGADO);
    if (!raw) return vazio;
    const p = JSON.parse(raw) as CopaPoolPersist;
    return {
      resultadosReais: p.resultadosReais ?? {},
      vencedoresPenaltisMM: p.vencedoresPenaltisMM ?? {},
      resultadosManuais: p.resultadosManuais ?? {},
      participantes: aplicarCacheMMNosParticipantes(
        Array.isArray(p.participantes)
          ? p.participantes.map((x) => ({
              ...x,
              setor: typeof x.setor === 'string' ? x.setor : '',
              pagou: Boolean(x.pagou),
              bloqueado: Boolean(x.bloqueado),
              origemId: typeof x.origemId === 'string' ? x.origemId : undefined,
              faltas:
                typeof x.faltas === 'number' && x.faltas >= 0
                  ? Math.floor(x.faltas)
                  : 0,
            }))
          : []
      ),
    };
  } catch {
    return vazio;
  }
}

function salvarEstadoCopaPool(
  reais: PlacaresMap,
  parts: ParticipanteCopa[],
  penaltis: VencedoresPenaltisMMMap = {},
  manuais: ResultadosManuaisMap = {}
): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        resultadosReais: reais,
        participantes: parts,
        vencedoresPenaltisMM: penaltis,
        resultadosManuais: manuais,
      })
    );
  } catch {
    /* quota */
  }
}

/** Grava localStorage + IndexedDB na mesma hora (evita perder após fechar janela). */
function salvarEstadoCopaRobusto(
  reais: PlacaresMap,
  parts: ParticipanteCopa[],
  penaltis: VencedoresPenaltisMMMap = {},
  manuais: ResultadosManuaisMap = {}
): void {
  salvarEstadoCopaPool(reais, parts, penaltis, manuais);
  void salvarCopaIDB(reais, parts, penaltis, manuais);
}

function abrirCopaIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB indisponível'));
      return;
    }
    const req = indexedDB.open(COPA_IDB_NAME, COPA_IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(COPA_IDB_STORE)) {
        db.createObjectStore(COPA_IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function lerCopaIDB(): Promise<{
  resultadosReais: PlacaresMap;
  participantes: ParticipanteCopa[];
  vencedoresPenaltisMM: VencedoresPenaltisMMMap;
  resultadosManuais: ResultadosManuaisMap;
} | null> {
  try {
    const db = await abrirCopaIDB();
    const data = await new Promise<unknown>((resolve, reject) => {
      const tx = db.transaction(COPA_IDB_STORE, 'readonly');
      const store = tx.objectStore(COPA_IDB_STORE);
      const r = store.get(COPA_IDB_KEY);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
    db.close();
    if (!data || typeof data !== 'object') return null;
    const p = data as {
      resultadosReais?: PlacaresMap;
      participantes?: ParticipanteCopa[];
      vencedoresPenaltisMM?: VencedoresPenaltisMMMap;
      resultadosManuais?: ResultadosManuaisMap;
    };
    return {
      resultadosReais: p.resultadosReais ?? {},
      participantes: Array.isArray(p.participantes) ? p.participantes : [],
      vencedoresPenaltisMM: p.vencedoresPenaltisMM ?? {},
      resultadosManuais: p.resultadosManuais ?? {},
    };
  } catch {
    return null;
  }
}

async function salvarCopaIDB(
  reais: PlacaresMap,
  parts: ParticipanteCopa[],
  penaltis: VencedoresPenaltisMMMap = {},
  manuais: ResultadosManuaisMap = {}
): Promise<void> {
  try {
    const db = await abrirCopaIDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(COPA_IDB_STORE, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      const store = tx.objectStore(COPA_IDB_STORE);
      store.put(
        {
          resultadosReais: reais,
          participantes: parts,
          vencedoresPenaltisMM: penaltis,
          resultadosManuais: manuais,
        },
        COPA_IDB_KEY
      );
    });
    db.close();
  } catch {
    /* ignore */
  }
}

function carregarQrPagamento(): string {
  try {
    return localStorage.getItem(STORAGE_KEY_QR) ?? '';
  } catch {
    return '';
  }
}

type AbaCopa =
  | 'oficial'
  | 'participantes'
  | 'palpites'
  | 'placar'
  | 'premiacao'
  | 'historico'
  | 'matamata'
  | 'comparar'
  | 'estatisticas';

export function NomeComBandeira({
  timeId,
  alinhado = 'esquerda',
  className = '',
}: {
  timeId: string;
  alinhado?: 'esquerda' | 'direita';
  className?: string;
}) {
  const urlImg = urlBandeira(timeId, 40);
  const emoji = bandeiraTime(timeId);
  const [imgFalhou, setImgFalhou] = useState(false);
  const mostrarImg = Boolean(urlImg) && !imgFalhou;
  return (
    <span
      className={`inline-flex items-center gap-1 min-w-0 ${alinhado === 'direita' ? 'justify-end' : ''} ${className}`}
      title={nomeTime(timeId)}
    >
      {mostrarImg ? (
        <img
          src={urlImg}
          alt=""
          width={20}
          height={14}
          loading="lazy"
          className="shrink-0 inline-block object-cover rounded-sm border border-neutral-200"
          style={{ width: 20, height: 14 }}
          onError={() => setImgFalhou(true)}
        />
      ) : emoji ? (
        <span className="shrink-0 text-base leading-none" aria-hidden>
          {emoji}
        </span>
      ) : null}
      <span className="truncate">{nomeTime(timeId)}</span>
    </span>
  );
}

export function InputPlacar({
  valor,
  onChange,
  disabled,
}: {
  valor: Placar | null | undefined;
  onChange: (p: Placar | null) => void;
  disabled?: boolean;
}) {
  // Estado local de texto para cada caixa, independente. Assim digitar em um
  // lado NÃO força "0" no outro: a caixa não tocada fica vazia até a pessoa
  // digitar. Só vira um palpite válido quando AS DUAS estão preenchidas.
  const [mandStr, setMandStr] = useState<string>(
    valor?.mandante != null ? String(valor.mandante) : ''
  );
  const [visStr, setVisStr] = useState<string>(
    valor?.visitante != null ? String(valor.visitante) : ''
  );

  // Guarda o último valor que NÓS emitimos, para ignorar o "eco" quando o pai
  // re-renderiza com o mesmo valor (evita apagar o que a pessoa digitou).
  const ultimoEmitido = useRef<Placar | null>(valor ?? null);

  useEffect(() => {
    const iguais =
      (valor?.mandante ?? null) === (ultimoEmitido.current?.mandante ?? null) &&
      (valor?.visitante ?? null) === (ultimoEmitido.current?.visitante ?? null);
    if (iguais) return; // mudança veio de nós mesmos → não mexe nas caixas
    // Mudança externa (auto-preencher, limpar, sincronização): sincroniza.
    setMandStr(valor?.mandante != null ? String(valor.mandante) : '');
    setVisStr(valor?.visitante != null ? String(valor.visitante) : '');
    ultimoEmitido.current = valor ?? null;
  }, [valor]);

  const emitir = (mStr: string, vStr: string) => {
    let out: Placar | null;
    if (mStr === '' || vStr === '') {
      // Incompleto: ainda não é um palpite (não inventa 0 no lado vazio).
      out = null;
    } else {
      out = { mandante: Math.max(0, Number(mStr)), visitante: Math.max(0, Number(vStr)) };
    }
    ultimoEmitido.current = out;
    onChange(out);
  };

  const limparNaoNumerico = (s: string) => s.replace(/[^\d]/g, '');

  return (
    <div className="flex items-center justify-center gap-1">
      <input
        type="number"
        min={0}
        inputMode="numeric"
        pattern="[0-9]*"
        disabled={disabled}
        value={mandStr}
        onChange={(e) => {
          const s = limparNaoNumerico(e.target.value);
          setMandStr(s);
          emitir(s, visStr);
        }}
        className="w-10 rounded border border-[#009b3a]/30 bg-white px-1 py-0.5 text-center text-sm font-bold"
      />
      <span className="text-xs font-bold text-neutral-500">×</span>
      <input
        type="number"
        min={0}
        inputMode="numeric"
        pattern="[0-9]*"
        disabled={disabled}
        value={visStr}
        onChange={(e) => {
          const s = limparNaoNumerico(e.target.value);
          setVisStr(s);
          emitir(mandStr, s);
        }}
        className="w-10 rounded border border-[#009b3a]/30 bg-white px-1 py-0.5 text-center text-sm font-bold"
      />
    </div>
  );
}

export function CardGrupo({
  grupoId,
  placares,
  onPlacarChange,
  somenteLeitura,
}: {
  grupoId: GrupoCopaId;
  placares: PlacaresMap;
  onPlacarChange?: (jogoId: string, p: Placar | null) => void;
  somenteLeitura?: boolean;
}) {
  const todosJogos = jogosDoGrupoId(grupoId);
  const rodadas: RodadaGrupo[] = [1, 2, 3];
  const classificacao = useMemo(
    () => calcularClassificacaoGrupo(grupoId, placares),
    [grupoId, placares]
  );
  const completo = grupoEstaCompleto(grupoId, placares);

  return (
    <div className="rounded-xl border-2 border-[#009b3a]/20 bg-[#fafbfc] overflow-hidden flex flex-col min-h-[320px]">
      <div className="bg-[#009b3a] text-white px-3 py-2 flex items-center justify-between gap-2">
        <span className="font-bold text-sm">Grupo {grupoId}</span>
        <span className="text-[11px] font-semibold text-white/80">Todos os jogos</span>
      </div>
      <div className="p-3 flex-1 space-y-3">
        {rodadas.map((rod) => {
          const jogosRodada = todosJogos.filter((j) => j.rodada === rod);
          if (jogosRodada.length === 0) return null;
          return (
            <div key={rod} className="space-y-2">
              <p className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide border-b border-[#009b3a]/10 pb-0.5">
                Rodada {rod}
              </p>
              {jogosRodada.map((jogo) => (
                <div
                  key={jogo.id}
                  className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-xs sm:text-sm"
                >
                  <NomeComBandeira timeId={jogo.mandanteId} alinhado="direita" className="w-full font-medium" />
                  {somenteLeitura || !onPlacarChange ? (
                    <span className="font-mono font-bold text-center min-w-[3.5rem]">
                      {placarValido(placares[jogo.id])
                        ? `${placares[jogo.id]!.mandante} × ${placares[jogo.id]!.visitante}`
                        : '—'}
                    </span>
                  ) : (
                    <InputPlacar
                      valor={placares[jogo.id]}
                      onChange={(p) => onPlacarChange(jogo.id, p)}
                    />
                  )}
                  <NomeComBandeira timeId={jogo.visitanteId} className="w-full font-medium" />
                </div>
              ))}
            </div>
          );
        })}
      </div>
      <div className="border-t border-[#009b3a]/15 bg-[#eef0f4] p-2">
        <p className="text-[10px] font-semibold text-neutral-600 mb-1 uppercase tracking-wide">
          Classificação {completo ? '' : '(parcial)'}
        </p>
        <table className="w-full text-[10px] sm:text-xs">
          <thead>
            <tr className="text-neutral-600">
              <th className="text-left py-0.5">#</th>
              <th className="text-left">Seleção</th>
              <th className="text-center">Pts</th>
              <th className="text-center">SG</th>
            </tr>
          </thead>
          <tbody>
            {classificacao.map((l) => (
              <tr
                key={l.timeId}
                className={
                  l.posicao <= 2 && completo
                    ? 'bg-[#e8f5ec] font-semibold text-[#007a2e]'
                    : l.posicao > 2 && completo
                      ? 'text-neutral-500'
                      : ''
                }
              >
                <td className="py-0.5">{l.posicao}º</td>
                <td className="truncate max-w-[5rem] sm:max-w-none">
                  <span className="inline-flex items-center gap-1">
                    {urlBandeira(l.timeId, 20) ? (
                      <img
                        src={urlBandeira(l.timeId, 20)}
                        alt=""
                        width={16}
                        height={11}
                        loading="lazy"
                        className="shrink-0 inline-block object-cover rounded-sm border border-neutral-200"
                        style={{ width: 16, height: 11 }}
                      />
                    ) : (
                      <span className="shrink-0 text-sm" aria-hidden>
                        {bandeiraTime(l.timeId)}
                      </span>
                    )}
                    {l.nome}
                  </span>
                </td>
                <td className="text-center">{l.pts}</td>
                <td className="text-center">{l.sg > 0 ? `+${l.sg}` : l.sg}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {completo && (
          <p className="text-[9px] text-[#007a2e] mt-1 font-medium">
            Destaque: 1º e 2º seguem (regra simplificada).
          </p>
        )}
      </div>
    </div>
  );
}

export function JogoMataMataCard({
  jogo,
  ladoAtimeId,
  ladoArotulo,
  ladoBtimeId,
  ladoBrotulo,
  placar,
  onChange,
  vencedorPenaltis,
  onVencedorPenaltisChange,
  somenteLeitura,
}: {
  jogo: JogoMataMata;
  ladoAtimeId: string | null;
  ladoArotulo: string;
  ladoBtimeId: string | null;
  ladoBrotulo: string;
  placar: Placar | null | undefined;
  onChange: (p: Placar | null) => void;
  vencedorPenaltis?: LadoPlacarMM | null;
  onVencedorPenaltisChange?: (lado: LadoPlacarMM | null) => void;
  somenteLeitura?: boolean;
}) {
  const ladoVisual = (timeId: string | null, rotulo: string, alinhado: 'esquerda' | 'direita') => {
    if (timeId) {
      return <NomeComBandeira timeId={timeId} alinhado={alinhado} className="font-semibold" />;
    }
    return (
      <span
        className={`font-medium text-neutral-500 truncate ${
          alinhado === 'direita' ? 'text-right' : ''
        }`}
        title={rotulo}
      >
        {rotulo}
      </span>
    );
  };

  const dataHoraLabel = formatarDataHoraIsoBrt(jogo.dataHoraBrt, { comAno: true });

  return (
    <div className="rounded-lg border-2 border-[#009b3a]/20 bg-[#fafbfc] p-2">
      <div className="text-[10px] text-neutral-500 font-semibold tracking-wide mb-1">
        <span className="text-[#009b3a]">{dataHoraLabel}</span>
        <span className="text-neutral-400 mx-1">·</span>
        <span className="uppercase">FIFA {jogo.numeroFifa}</span>
        {jogo.cidade ? (
          <>
            <span className="text-neutral-400 mx-1">·</span>
            <span className="normal-case text-neutral-600">{jogo.cidade}</span>
          </>
        ) : null}
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-xs">
        {ladoVisual(ladoAtimeId, ladoArotulo, 'direita')}
        {somenteLeitura ? (
          <span className="font-mono font-bold text-center min-w-[3.5rem]">
            {placarValido(placar)
              ? `${placar!.mandante} × ${placar!.visitante}`
              : '—'}
          </span>
        ) : (
          <InputPlacar valor={placar} onChange={onChange} />
        )}
        {ladoVisual(ladoBtimeId, ladoBrotulo, 'esquerda')}
      </div>
      {placarEmpatadoMM(placar) && (
        <div className="mt-2 pt-2 border-t border-amber-300 bg-amber-50 rounded px-2 py-1.5">
          {somenteLeitura ? (
            <p className="text-[10px] text-amber-900">
              {vencedorPenaltis ? (
                <>
                  Pênaltis:{' '}
                  <strong>
                    {vencedorPenaltis === 'mandante' ? ladoArotulo : ladoBrotulo}
                  </strong>{' '}
                  avança
                </>
              ) : (
                <span className="text-rose-700 font-medium">
                  Empate sem vencedor nos pênaltis — próxima fase indefinida
                </span>
              )}
            </p>
          ) : (
            <>
              <p className="text-[10px] font-semibold text-amber-900 mb-1">
                Empate — quem vence nos pênaltis e avança?
              </p>
              <div className="flex flex-wrap gap-1.5">
                {(['mandante', 'visitante'] as const).map((lado) => {
                  const rotulo = lado === 'mandante' ? ladoArotulo : ladoBrotulo;
                  const ativo = vencedorPenaltis === lado;
                  return (
                    <button
                      key={lado}
                      type="button"
                      onClick={() => onVencedorPenaltisChange?.(lado)}
                      className={`px-2 py-1 rounded text-[10px] font-bold border transition-colors ${
                        ativo
                          ? 'bg-[#009b3a] text-white border-[#007a2e]'
                          : 'bg-white text-neutral-800 border-amber-400 hover:bg-amber-100'
                      }`}
                      title={rotulo}
                    >
                      {rotulo}
                    </button>
                  );
                })}
              </div>
              {!vencedorPenaltis ? (
                <p className="text-[9px] text-rose-700 mt-1 font-medium">
                  Marque o vencedor para liberar confrontos da fase seguinte.
                </p>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Lista os 32 timeIds classificados (24 dos top-2 + 8 melhores 3ºs já escolhidos). */
function listarClassificadosMataMata(
  resultadosReais: PlacaresMap,
  config: ConfigMataMata
): { timeId: string; origem: string }[] {
  const out: { timeId: string; origem: string }[] = [];
  GRUPOS_COPA_2026.forEach((g) => {
    const cls = calcularClassificacaoGrupo(g.id, resultadosReais);
    if (cls[0]) out.push({ timeId: cls[0].timeId, origem: `1º Grupo ${g.id}` });
    if (cls[1]) out.push({ timeId: cls[1].timeId, origem: `2º Grupo ${g.id}` });
  });
  for (let i = 1; i <= 8; i++) {
    const tId = config.melhoresTerceiros[i];
    if (tId) out.push({ timeId: tId, origem: `Melhor 3º #${i}` });
  }
  return out;
}

function MelhoresTerceirosCard({
  placaresGrupos,
  melhoresTerceiros,
  onMelhoresTerceirosChange,
  faseGruposCompleta,
}: {
  placaresGrupos: PlacaresMap;
  melhoresTerceiros: Record<number, string>;
  onMelhoresTerceirosChange: (v: Record<number, string>) => void;
  /** true = 72/72 oficiais (habilita sugestão FIFA automática). */
  faseGruposCompleta: boolean;
}) {
  const slotsInfo = useMemo(() => listarInfoSlotsMelhor3o(), []);
  const setSlot = (slot: number, timeId: string) => {
    const novo = { ...melhoresTerceiros };
    if (timeId) novo[slot] = timeId;
    else delete novo[slot];
    onMelhoresTerceirosChange(novo);
  };
  const idsUsados = new Set(Object.values(melhoresTerceiros));
  const completos =
    [1, 2, 3, 4, 5, 6, 7, 8].every((s) => melhoresTerceiros[s]) &&
    new Set(Object.values(melhoresTerceiros)).size === 8;

  const rotuloAdversario = (info: (typeof slotsInfo)[0]) => {
    if (!info.adversarioGrupo || !info.adversarioPosicao) return 'adversário do 16 avos';
    if (grupoEstaCompleto(info.adversarioGrupo, placaresGrupos)) {
      const cls = calcularClassificacaoGrupo(info.adversarioGrupo, placaresGrupos);
      const adv = cls[info.adversarioPosicao - 1];
      if (adv) return adv.nome;
    }
    return `${info.adversarioPosicao}º do Grupo ${info.adversarioGrupo}`;
  };

  return (
    <div className="ade-neu-card p-3 border border-[#009b3a]/15">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <h4 className="font-bold text-black">8 melhores 3º colocados</h4>
        {completos ? (
          <span className="text-[11px] text-emerald-700 font-semibold">
            ✓ Todos os 8 slots preenchidos
          </span>
        ) : (
          <span className="text-[11px] text-amber-700 font-semibold">
            Escolha os 8 times abaixo
          </span>
        )}
      </div>
      {!faseGruposCompleta && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-2">
          A fase de grupos ainda não encerrou — você já pode definir manualmente cada slot
          (ex.: <strong>Bósnia no M3-5</strong> enfrenta o 1º do Grupo D). A sugestão automática
          FIFA só fica disponível com os 72 resultados oficiais.
        </p>
      )}
      <p className="text-xs text-neutral-600 mb-2">
        Escolha quais seleções ocupam cada slot <strong>M3-1 a M3-8</strong> no chaveamento. Só{' '}
        <strong>8 entram</strong> no mata-mata — cada slot só aceita times dos grupos elegíveis
        daquele confronto.
      </p>
      {faseGruposCompleta && (
        <div className="mb-2">
          <button
            type="button"
            onClick={() => {
              const auto = atribuirMelhoresTerceirosAutomatico(placaresGrupos);
              if (auto) onMelhoresTerceirosChange(auto);
            }}
            className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300"
          >
            Sugerir pela regra FIFA
          </button>
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        {slotsInfo.map((info) => {
          const opcoes = opcoesParaSlotMelhor3o(info.slot, placaresGrupos);
          const selecionado = melhoresTerceiros[info.slot];
          if (selecionado && !opcoes.some((o) => o.timeId === selecionado)) {
            const gr =
              GRUPOS_COPA_2026.find((g) => g.times.some((t) => t.id === selecionado))?.id ||
              'A';
            opcoes.unshift({
              timeId: selecionado,
              nome: nomeTimeBase(selecionado),
              pts: 0,
              sg: 0,
              gp: 0,
              grupo: gr,
            });
          }
          return (
            <div key={info.slot} className="rounded-lg border border-[#009b3a]/15 p-2 bg-white/50">
              <div className="flex items-center gap-2 mb-1">
                <label className="text-xs font-bold text-neutral-800 shrink-0">
                  M3-{info.slot}
                </label>
                <select
                  value={melhoresTerceiros[info.slot] || ''}
                  onChange={(e) => setSlot(info.slot, e.target.value)}
                  className="flex-1 px-2 py-1 text-xs rounded border border-[#009b3a]/25 min-w-0"
                >
                  <option value="">Selecione…</option>
                  {opcoes.map((t) => {
                    const usadoEmOutroSlot =
                      idsUsados.has(t.timeId) && melhoresTerceiros[info.slot] !== t.timeId;
                    const temStats = t.pts > 0 || t.sg !== 0 || t.gp > 0;
                    return (
                      <option key={t.timeId} value={t.timeId} disabled={usadoEmOutroSlot}>
                        {t.nome} (Gr. {t.grupo}
                        {temStats
                          ? ` · ${t.pts}pts, SG ${t.sg >= 0 ? '+' : ''}${t.sg}`
                          : ''}
                        )
                        {usadoEmOutroSlot ? ' — já alocado' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
              <p className="text-[10px] text-neutral-500 pl-0.5">
                Jogo {info.jogoFifa} · enfrenta <strong>{rotuloAdversario(info)}</strong> · grupos
                elegíveis: {info.gruposElegiveis.join(', ')}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Organizador: remove palpite(s) MM no servidor OU autoriza envio atrasado de jogo faltante. */
function LiberarPalpitesMMCard({
  participante,
  sheetsEndpoint,
  resolverLado,
  onLiberar,
  onAutorizarAtrasado,
  onRegistrarFaltante,
}: {
  participante: ParticipanteCopa;
  sheetsEndpoint: string;
  resolverLado: (jogoId: string, qualLado: 'A' | 'B') => { rotulo: string };
  onLiberar: (jogoIds: string[]) => Promise<void>;
  onAutorizarAtrasado: (jogoIds: string[]) => Promise<void>;
  onRegistrarFaltante: (
    jogoId: string,
    placar: { mandante: number; visitante: number }
  ) => Promise<void>;
}) {
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [selecionadosFaltantes, setSelecionadosFaltantes] = useState<Set<string>>(new Set());
  const [salvando, setSalvando] = useState(false);
  const [aberto, setAberto] = useState(true);
  const [placarFaltante, setPlacarFaltante] = useState<
    Record<string, { mandante: string; visitante: string }>
  >({});

  const jogosComPalpite = useMemo(() => {
    return JOGOS_MATA_MATA.filter((j) => placarValido(participante.palpites[j.id])).map((j) => {
      const p = participante.palpites[j.id]!;
      return {
        jogo: j,
        palpite: p,
        mandante: resolverLado(j.id, 'A').rotulo,
        visitante: resolverLado(j.id, 'B').rotulo,
      };
    });
  }, [participante.palpites, resolverLado]);

  const jogosSemPalpite = useMemo(() => {
    return JOGOS_MATA_MATA.filter((j) => !placarValido(participante.palpites[j.id])).map((j) => ({
      jogo: j,
      mandante: resolverLado(j.id, 'A').rotulo,
      visitante: resolverLado(j.id, 'B').rotulo,
      iniciou: Date.now() >= Date.parse(j.dataHoraBrt),
    }));
  }, [participante.palpites, resolverLado]);

  if (!participante.bloqueado) return null;
  if (jogosComPalpite.length === 0 && jogosSemPalpite.length === 0) return null;

  const toggle = (jid: string, faltante = false) => {
    const setter = faltante ? setSelecionadosFaltantes : setSelecionados;
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(jid)) next.delete(jid);
      else next.add(jid);
      return next;
    });
  };

  const liberar = async (ids: string[]) => {
    if (ids.length === 0) return;
    if (!sheetsEndpoint) {
      alert('Configure a planilha (Google Apps Script v7.5+) antes de liberar palpites.');
      return;
    }
    const ok = confirm(
      `Liberar ${ids.length} jogo${ids.length === 1 ? '' : 's'} de mata-mata para "${participante.nome}"?\n\n` +
        `O palpite será APAGADO no servidor. A pessoa poderá palpitar de novo pelo link ` +
        `(desde que o confronto esteja publicado e o jogo não tenha começado).\n\n` +
        `Peça para ela abrir o link do mata-mata e clicar em Atualizar.`
    );
    if (!ok) return;
    setSalvando(true);
    try {
      await onLiberar(ids);
      setSelecionados(new Set());
    } finally {
      setSalvando(false);
    }
  };

  const autorizarAtrasado = async (ids: string[]) => {
    if (ids.length === 0) return;
    if (!sheetsEndpoint) {
      alert('Configure a planilha (Google Apps Script v7.5+) antes de autorizar.');
      return;
    }
    const ok = confirm(
      `Autorizar envio ATRASADO de ${ids.length} jogo(s) para "${participante.nome}"?\n\n` +
        `A pessoa poderá abrir o link do mata-mata e enviar ESSE(S) jogo(s) uma vez, ` +
        `mesmo que o jogo já tenha começado.\n\n` +
        `Requer script v7.5+ implantado (Nova versão).`
    );
    if (!ok) return;
    setSalvando(true);
    try {
      await onAutorizarAtrasado(ids);
      setSelecionadosFaltantes(new Set());
    } finally {
      setSalvando(false);
    }
  };

  const registrarUm = async (jogoId: string) => {
    const raw = placarFaltante[jogoId] || { mandante: '', visitante: '' };
    const m = Number(raw.mandante);
    const v = Number(raw.visitante);
    if (!Number.isFinite(m) || !Number.isFinite(v) || raw.mandante === '' || raw.visitante === '') {
      alert('Informe o placar (mandante e visitante).');
      return;
    }
    if (!sheetsEndpoint) {
      alert('Configure a planilha antes de registrar.');
      return;
    }
    const rotA = resolverLado(jogoId, 'A').rotulo;
    const rotB = resolverLado(jogoId, 'B').rotulo;
    const ok = confirm(
      `Registrar no servidor o palpite de "${participante.nome}"?\n\n` +
        `${rotA} ${m} × ${v} ${rotB}\n\n` +
        `Isso grava direto na planilha (não precisa o participante enviar).`
    );
    if (!ok) return;
    setSalvando(true);
    try {
      await onRegistrarFaltante(jogoId, { mandante: m, visitante: v });
      setPlacarFaltante((prev) => {
        const next = { ...prev };
        delete next[jogoId];
        return next;
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="ade-neu-card p-3 border border-blue-300 bg-blue-50/50">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <span className="font-bold text-sm text-blue-900 inline-flex items-center gap-1.5">
          <Unlock className="w-4 h-4" />
          Liberar / autorizar / registrar palpites de mata-mata
        </span>
        <span className="text-xs text-blue-700">{aberto ? '▲' : '▼'}</span>
      </button>
      {aberto && (
        <div className="mt-2 space-y-3">
          {jogosSemPalpite.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-amber-950 font-semibold">
                Jogos SEM palpite ({jogosSemPalpite.length})
              </p>
              <p className="text-xs text-amber-900">
                Se o jogo já começou: autorize o envio no link ou registre o placar aqui.
              </p>
              <ul className="max-h-56 overflow-auto rounded border border-amber-200 bg-white divide-y divide-amber-100">
                {jogosSemPalpite.map(({ jogo, mandante, visitante, iniciou }) => {
                  const pl = placarFaltante[jogo.id] || { mandante: '', visitante: '' };
                  return (
                    <li key={jogo.id} className="flex flex-col gap-1.5 px-2 py-2 text-xs">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selecionadosFaltantes.has(jogo.id)}
                          onChange={() => toggle(jogo.id, true)}
                          className="shrink-0"
                        />
                        <span className="flex-1 min-w-0">
                          <span className="font-semibold text-neutral-800">
                            FIFA {jogo.numeroFifa}
                          </span>
                          <span className="text-neutral-600">
                            {' '}
                            · {mandante} × {visitante}
                          </span>
                          {iniciou && (
                            <span className="ml-1 text-[10px] font-bold text-red-700">
                              (já iniciou)
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 pl-6">
                        <input
                          type="number"
                          min={0}
                          className="w-12 border rounded px-1 py-0.5 text-center font-mono"
                          placeholder="M"
                          value={pl.mandante}
                          onChange={(e) =>
                            setPlacarFaltante((prev) => ({
                              ...prev,
                              [jogo.id]: { ...pl, mandante: e.target.value },
                            }))
                          }
                        />
                        <span className="font-bold">×</span>
                        <input
                          type="number"
                          min={0}
                          className="w-12 border rounded px-1 py-0.5 text-center font-mono"
                          placeholder="V"
                          value={pl.visitante}
                          onChange={(e) =>
                            setPlacarFaltante((prev) => ({
                              ...prev,
                              [jogo.id]: { ...pl, visitante: e.target.value },
                            }))
                          }
                        />
                        <button
                          type="button"
                          disabled={salvando}
                          onClick={() => void registrarUm(jogo.id)}
                          className="px-2 py-1 rounded text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                        >
                          Registrar no servidor
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  disabled={salvando || selecionadosFaltantes.size === 0}
                  onClick={() => void autorizarAtrasado([...selecionadosFaltantes])}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <Unlock className="w-3.5 h-3.5" />
                  {salvando
                    ? 'Autorizando…'
                    : `Autorizar envio no link (${selecionadosFaltantes.size})`}
                </button>
              </div>
            </div>
          )}

          {jogosComPalpite.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-blue-900">
                Já enviados — use para <strong>apagar</strong> e permitir palpitar de novo (só
                antes do apito).
              </p>
              <ul className="max-h-48 overflow-auto rounded border border-blue-200 bg-white divide-y divide-blue-100">
                {jogosComPalpite.map(({ jogo, palpite, mandante, visitante }) => (
                  <li key={jogo.id} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={selecionados.has(jogo.id)}
                      onChange={() => toggle(jogo.id)}
                      className="shrink-0"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="font-semibold text-neutral-800">
                        FIFA {jogo.numeroFifa}
                      </span>
                      <span className="text-neutral-600">
                        {' '}
                        · {mandante} × {visitante}
                      </span>
                    </span>
                    <span className="font-mono font-bold text-blue-800 shrink-0">
                      {palpite.mandante}×{palpite.visitante}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  disabled={salvando}
                  onClick={() => setSelecionados(new Set(jogosComPalpite.map((x) => x.jogo.id)))}
                  className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-white border border-blue-300 text-blue-800 hover:bg-blue-50 disabled:opacity-50"
                >
                  Selecionar todos
                </button>
                <button
                  type="button"
                  disabled={salvando || selecionados.size === 0}
                  onClick={() => void liberar([...selecionados])}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <Unlock className="w-3.5 h-3.5" />
                  {salvando
                    ? 'Liberando…'
                    : `Apagar e liberar de novo (${selecionados.size})`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReorganizarCruzamentosCard({
  configMM,
  onChange,
  resolverLado,
}: {
  configMM: ConfigMataMata;
  onChange: (c: ConfigMataMata) => void;
  resolverLado: (jogoId: string, qualLado: 'A' | 'B') => {
    timeId: string | null;
    rotulo: string;
  };
}) {
  const [aberto, setAberto] = useState(false);
  const jogos16 = useMemo(() => jogosDaFase('16avos'), []);

  const sobrescrever = (jogoId: string, lado: 'ladoA' | 'ladoB', timeId: string) => {
    const novo = { ...configMM.cruzamentos16 };
    const atual = { ...(novo[jogoId] || {}) };
    if (timeId) atual[lado] = timeId;
    else delete atual[lado];
    if (atual.ladoA || atual.ladoB) novo[jogoId] = atual;
    else delete novo[jogoId];
    onChange({ ...configMM, cruzamentos16: novo });
  };

  const limparOverrides = () => {
    const ok = confirm('Limpar todas as alterações manuais do chaveamento de 16 avos?');
    if (!ok) return;
    onChange({ ...configMM, cruzamentos16: {} });
  };

  // Calcula todos os 32 times disponíveis (top-2 + 8 M3) para uso nos dropdowns
  const todosTimes = useMemo(() => {
    const set = new Set<string>();
    jogos16.forEach((j) => {
      const a = resolverLado(j.id, 'A');
      const b = resolverLado(j.id, 'B');
      if (a.timeId) set.add(a.timeId);
      if (b.timeId) set.add(b.timeId);
    });
    return [...set].sort((x, y) => nomeTimeBase(x).localeCompare(nomeTimeBase(y), 'pt-BR'));
  }, [jogos16, resolverLado]);

  const overridesCount = Object.keys(configMM.cruzamentos16).length;

  return (
    <div className="ade-neu-card p-3 border border-[#009b3a]/15">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="w-full flex items-center justify-between gap-2"
      >
        <h4 className="font-bold text-black text-left">
          Reorganizar chaveamento das 16 avos{' '}
          {overridesCount > 0 && (
            <span className="text-[10px] font-normal text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded ml-1">
              {overridesCount} jogo{overridesCount === 1 ? '' : 's'} alterado{overridesCount === 1 ? '' : 's'}
            </span>
          )}
        </h4>
        <span className="text-xs text-[#009b3a] font-semibold">
          {aberto ? 'Fechar' : 'Editar manualmente'}
        </span>
      </button>
      {aberto && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-neutral-600">
            O chaveamento padrão segue a tabela da FIFA. Use isto se precisar trocar manualmente
            quem joga contra quem em algum jogo das 16 avos.
          </p>
          <div className="grid gap-2 lg:grid-cols-2">
            {jogos16.map((j) => {
              const aRes = resolverLado(j.id, 'A');
              const bRes = resolverLado(j.id, 'B');
              const overA = configMM.cruzamentos16[j.id]?.ladoA;
              const overB = configMM.cruzamentos16[j.id]?.ladoB;
              return (
                <div key={j.id} className="border border-neutral-200 rounded p-2 bg-white">
                  <div className="text-[10px] font-semibold text-neutral-500 mb-1">
                    {formatarDataHoraIsoBrt(j.dataHoraBrt, { comAno: true })} · FIFA {j.numeroFifa}
                  </div>
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-1 items-center text-xs">
                    <select
                      value={overA || ''}
                      onChange={(e) => sobrescrever(j.id, 'ladoA', e.target.value)}
                      className="px-1 py-0.5 rounded border border-neutral-300 text-xs min-w-0"
                      title={overA ? 'Manual' : aRes.rotulo}
                    >
                      <option value="">{aRes.rotulo} (auto)</option>
                      {todosTimes.map((t) => (
                        <option key={t} value={t}>
                          {nomeTimeBase(t)}
                        </option>
                      ))}
                    </select>
                    <span className="text-neutral-400 text-xs">×</span>
                    <select
                      value={overB || ''}
                      onChange={(e) => sobrescrever(j.id, 'ladoB', e.target.value)}
                      className="px-1 py-0.5 rounded border border-neutral-300 text-xs min-w-0"
                      title={overB ? 'Manual' : bRes.rotulo}
                    >
                      <option value="">{bRes.rotulo} (auto)</option>
                      {todosTimes.map((t) => (
                        <option key={t} value={t}>
                          {nomeTimeBase(t)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
          {overridesCount > 0 && (
            <button
              type="button"
              onClick={limparOverrides}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-neutral-100 hover:bg-neutral-200 text-neutral-700 border border-neutral-300"
            >
              Limpar alterações manuais
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CompartilharMataMataCard({
  configMM,
  organizador,
  resolverLado,
  onImprimir,
}: {
  configMM: ConfigMataMata;
  organizador: OrganizadorInfo;
  resolverLado: (
    jogoId: string,
    qualLado: 'A' | 'B'
  ) => { timeId: string | null; rotulo: string };
  onImprimir: () => void;
}) {
  const slotsOk = [1, 2, 3, 4, 5, 6, 7, 8].every((s) => configMM.melhoresTerceiros[s]);
  const jogos16 = useMemo(() => jogosDaFase('16avos'), []);
  const todosJogosResolvidos = jogos16.every(
    (j) => resolverLado(j.id, 'A').timeId && resolverLado(j.id, 'B').timeId
  );

  const textoTabela = useMemo(() => {
    const linhas: string[] = [];
    linhas.push(`*Mata-mata Copa 2026 — palpites*`);
    if (organizador.nome) linhas.push(`Organizador: ${organizador.nome}`);
    linhas.push('');
    linhas.push(`*16 AVOS DE FINAL* (acerto exato 6 pts / vencedor 2 pts)`);
    jogos16.forEach((j) => {
      const a = resolverLado(j.id, 'A');
      const b = resolverLado(j.id, 'B');
      const quando = formatarDataHoraIsoBrt(j.dataHoraBrt, { comAno: true });
      linhas.push(`${quando} — ${a.rotulo}  ___ x ___  ${b.rotulo}`);
    });
    linhas.push('');
    linhas.push(`*OITAVAS* (9/3)  *QUARTAS* (12/4)  *SEMI* (15/5)  *FINAL e 3º lugar* (18/6)`);
    linhas.push(`Para essas fases, escreva o vencedor e o placar de cada jogo.`);
    linhas.push('');
    linhas.push(`Envie sua resposta para ${organizador.nome || 'o organizador'}.`);
    return linhas.join('\n');
  }, [jogos16, organizador.nome, resolverLado]);

  const compartilharWhats = () => {
    if (!slotsOk || !todosJogosResolvidos) {
      alert(
        'Preencha os 8 melhores 3ºs e confira o chaveamento antes de compartilhar a tabela.'
      );
      return;
    }
    const texto = encodeURIComponent(textoTabela);
    const whats = (organizador.whats || '').replace(/\D/g, '');
    const url = whats ? `https://wa.me/?text=${texto}` : `https://wa.me/?text=${texto}`;
    void whats;
    window.open(url, '_blank', 'noopener');
  };

  const copiarTexto = () => {
    if (!slotsOk || !todosJogosResolvidos) {
      alert(
        'Preencha os 8 melhores 3ºs e confira o chaveamento antes de copiar a tabela.'
      );
      return;
    }
    navigator.clipboard
      ?.writeText(textoTabela)
      .then(() => alert('Texto da tabela copiado! Cole no WhatsApp/onde quiser.'))
      .catch(() => prompt('Copie o texto da tabela:', textoTabela));
  };

  return (
    <div className="ade-neu-card p-3 border-2 border-[#25D366]/40">
      <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
        <div className="flex-1 min-w-[200px]">
          <h4 className="font-bold text-black flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#25D366]" />
            Enviar tabela do mata-mata para o pessoal
          </h4>
          <p className="text-xs text-neutral-600 mt-1">
            Depois que os 8 melhores 3ºs estiverem escolhidos e o chaveamento conferido, você
            pode imprimir uma folha para distribuir ou compartilhar o texto da tabela no
            WhatsApp para o pessoal devolver os palpites.
          </p>
          {(!slotsOk || !todosJogosResolvidos) && (
            <p className="text-[11px] text-amber-700 mt-1 font-semibold">
              Conclua os 8 slots de M3 e o chaveamento antes de enviar a tabela.
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={onImprimir}
          disabled={!slotsOk || !todosJogosResolvidos}
          className={`px-3 py-2 rounded-lg text-xs font-bold inline-flex items-center gap-1 ${
            slotsOk && todosJogosResolvidos
              ? 'bg-[#009b3a] hover:bg-[#007a2e] text-white'
              : 'bg-neutral-300 text-neutral-600 cursor-not-allowed'
          }`}
        >
          Imprimir folha (mata-mata)
        </button>
        <button
          type="button"
          onClick={compartilharWhats}
          disabled={!slotsOk || !todosJogosResolvidos}
          className={`px-3 py-2 rounded-lg text-xs font-semibold inline-flex items-center gap-1 ${
            slotsOk && todosJogosResolvidos
              ? 'bg-[#25D366] hover:bg-[#1ea952] text-white'
              : 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
          }`}
        >
          <MessageCircle className="w-3 h-3" />
          Compartilhar no WhatsApp
        </button>
        <button
          type="button"
          onClick={copiarTexto}
          disabled={!slotsOk || !todosJogosResolvidos}
          className={`px-3 py-2 rounded-lg text-xs font-semibold inline-flex items-center gap-1 ${
            slotsOk && todosJogosResolvidos
              ? 'bg-neutral-100 hover:bg-neutral-200 border border-neutral-300'
              : 'bg-neutral-200 text-neutral-500 cursor-not-allowed border border-neutral-300'
          }`}
        >
          Copiar texto
        </button>
      </div>
    </div>
  );
}

function fmtPlacar(p: { mandante: number; visitante: number }): string {
  return `${p.mandante}×${p.visitante}`;
}

/** Agrupa o jogo numa "fase" pra filtrar a lista de acertos. */
function categoriaAcerto(j: AcertoJogo): string {
  const m = j.contexto.match(/Rodada\s*(\d)/i);
  if (m) return `Grupos · Rodada ${m[1]}`;
  const base = j.contexto.split('·')[0].trim();
  return base || 'Outros';
}

/**
 * Lista, jogo a jogo, quantos e QUEM cravou o placar exato e quantos
 * acertaram o vencedor/empate. Substitui as antigas seções "Zebras" e
 * "Vexame". Tem um filtro por rodada/fase pra navegar com muitos jogos.
 */
function AcertosPorJogoLista({ acertos }: { acertos: AcertoJogo[] }) {
  const categorias = useMemo(() => {
    const ordem: string[] = [];
    acertos.forEach((j) => {
      const c = categoriaAcerto(j);
      if (!ordem.includes(c)) ordem.push(c);
    });
    return ordem;
  }, [acertos]);

  const [filtro, setFiltro] = useState<string>('todos');
  const filtroAtual = filtro === 'todos' || categorias.includes(filtro) ? filtro : 'todos';

  const visiveis = useMemo(
    () =>
      filtroAtual === 'todos'
        ? acertos
        : acertos.filter((j) => categoriaAcerto(j) === filtroAtual),
    [acertos, filtroAtual]
  );

  if (acertos.length === 0) return null;

  return (
    <div className="ade-neu-card p-4 border border-[#009b3a]/15">
      <h4 className="font-bold text-black flex items-center gap-2 mb-1">
        <ClipboardList className="w-4 h-4 text-[#009b3a]" />
        Acertos por jogo
      </h4>
      <p className="text-[11px] text-neutral-600 mb-3">
        Em cada jogo: quantos e quem cravou o placar exato, e quantos acertaram o
        vencedor/empate.
      </p>

      {categorias.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {['todos', ...categorias].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setFiltro(c)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${
                filtroAtual === c
                  ? 'bg-[#009b3a] text-white border-[#009b3a]'
                  : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50'
              }`}
            >
              {c === 'todos' ? 'Todas as fases' : c}
            </button>
          ))}
        </div>
      )}

      <ul className="space-y-2">
        {visiveis.map((j) => {
          const totalVencedor = j.cravaramExato.length + j.acertaramSoVencedor.length;
          return (
            <li
              key={j.jogoId}
              className="rounded-lg border border-neutral-200 bg-white px-3 py-2"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-semibold text-black text-sm">{j.rotulo}</span>
                <span className="font-mono font-black text-[#009b3a] text-sm">
                  {fmtPlacar(j.real)}
                </span>
              </div>
              <div className="text-[11px] text-neutral-500">
                {j.contexto} · {j.totalPalpitou} palpite(s)
              </div>

              <div className="mt-2 text-xs">
                <span className="inline-flex items-center gap-1 font-bold text-amber-600">
                  <Trophy className="w-3.5 h-3.5" />
                  Cravaram o placar: {j.cravaramExato.length}
                </span>
                {j.cravaramExato.length > 0 ? (
                  <span className="text-neutral-700"> — {j.cravaramExato.join(', ')}</span>
                ) : (
                  <span className="text-neutral-400"> — ninguém</span>
                )}
              </div>

              <div className="mt-1 text-xs">
                <span className="font-bold text-[#009b3a]">
                  Acertaram o vencedor/empate: {totalVencedor}/{j.totalPalpitou}
                </span>
                {j.acertaramSoVencedor.length > 0 && (
                  <span className="text-neutral-500">
                    {' '}(só o resultado: {j.acertaramSoVencedor.join(', ')})
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Aba de estatísticas pós-jogo (cravadores + acertos por jogo). */
function EstatisticasCard({
  estatisticas,
  eliminacao,
}: {
  estatisticas: EstatisticasPosJogo | null;
  eliminacao: EliminacaoMatematicaCopa | null;
}) {
  if (!estatisticas) {
    return (
      <p className="text-center text-neutral-600 py-8 text-sm">
        Carregando estatísticas…
      </p>
    );
  }

  const s = estatisticas;

  if (s.totalJogosComResultado === 0) {
    return (
      <div className="ade-neu-card p-6 border border-amber-200 bg-amber-50/60 text-center">
        <BarChart3 className="w-10 h-10 text-amber-600 mx-auto mb-2" />
        <p className="font-semibold text-amber-900">Nenhum resultado lançado ainda</p>
        <p className="text-xs text-amber-800 mt-1">
          Preencha os <strong>resultados reais</strong> (grupos e mata-mata) para ver placares
          exatos, zebras e os piores palpites.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="ade-neu-card p-4 border border-[#009b3a]/20 text-center">
          <p className="text-[10px] uppercase font-bold text-neutral-500">Jogos com resultado</p>
          <p className="text-2xl font-black text-[#009b3a]">{s.totalJogosComResultado}</p>
        </div>
        <div className="ade-neu-card p-4 border border-[#009b3a]/20 text-center">
          <p className="text-[10px] uppercase font-bold text-neutral-500">Palpites avaliados</p>
          <p className="text-2xl font-black text-[#009b3a]">{s.totalPalpitesAvaliados}</p>
        </div>
        <div className="ade-neu-card p-4 border border-amber-300/50 text-center">
          <p className="text-[10px] uppercase font-bold text-neutral-500">Placares exatos (total)</p>
          <p className="text-2xl font-black text-amber-600">{s.placaresExatosTotais}</p>
        </div>
        <div className="ade-neu-card p-4 border border-[#009b3a]/20 text-center">
          <p className="text-[10px] uppercase font-bold text-neutral-500">Jogos com cravada</p>
          <p className="text-2xl font-black text-[#009b3a]">
            {s.acertosPorJogo.filter((j) => j.cravaramExato.length > 0).length}
          </p>
        </div>
      </div>

      {(s.maiorGoleada || s.jogoMaisGols) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {s.maiorGoleada && s.maiorGoleada.saldo > 0 && (
            <div className="ade-neu-card p-3 border border-red-200 bg-red-50/40">
              <p className="text-[10px] font-bold text-red-700 uppercase">Maior goleada real</p>
              <p className="font-semibold text-sm text-black mt-1">{s.maiorGoleada.rotulo}</p>
              <p className="text-xs text-neutral-600">{s.maiorGoleada.contexto}</p>
              <p className="text-lg font-black text-red-700 mt-1">
                {fmtPlacar(s.maiorGoleada.real)} (saldo {s.maiorGoleada.saldo})
              </p>
            </div>
          )}
          {s.jogoMaisGols && s.jogoMaisGols.gols > 0 && (
            <div className="ade-neu-card p-3 border border-blue-200 bg-blue-50/40">
              <p className="text-[10px] font-bold text-blue-700 uppercase">Jogo com mais gols</p>
              <p className="font-semibold text-sm text-black mt-1">{s.jogoMaisGols.rotulo}</p>
              <p className="text-xs text-neutral-600">{s.jogoMaisGols.contexto}</p>
              <p className="text-lg font-black text-blue-700 mt-1">
                {fmtPlacar(s.jogoMaisGols.real)} ({s.jogoMaisGols.gols} gols)
              </p>
            </div>
          )}
        </div>
      )}

      {s.menosCravadores.length > 0 && (
        <div className="ade-neu-card p-4 border border-orange-200 bg-orange-50/40">
          <h4 className="font-bold text-black flex items-center gap-2 mb-2">
            <UserMinus className="w-4 h-4 text-orange-600" />
            Menos placares exatos
            {s.menosCravadores.length > 1 && (
              <span className="text-xs font-semibold text-orange-700 normal-case">(empate)</span>
            )}
          </h4>
          <p className="text-xs text-neutral-600 mb-3">
            {s.menosCravadores.length === 1
              ? 'Participante com menor número de resultados cravados até agora.'
              : `${s.menosCravadores.length} participantes empatados com o menor número de cravadas.`}
          </p>
          <ul className="space-y-2">
            {s.menosCravadores.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between gap-2 text-sm border-b border-orange-100 pb-2 last:border-0"
              >
                <span className="font-semibold truncate">{c.nome}</span>
                <span className="shrink-0 text-xs text-neutral-600">
                  <strong className="text-orange-700">{c.placaresExatos}</strong> exato
                  {c.placaresExatos !== 1 ? 's' : ''}
                  {c.acertosVencedor > 0 && <> · {c.acertosVencedor} só vencedor</>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {eliminacao && eliminacao.jogosSemResultado > 0 && (
        <div className="ade-neu-card p-4 border border-red-200 bg-red-50/30 space-y-4">
          <div>
            <h4 className="font-bold text-black flex items-center gap-2 mb-2">
              <Ban className="w-4 h-4 text-red-600" />
              Situação matemática — prêmios (1º e 2º)
            </h4>
            <p className="text-xs text-neutral-600">
              Líder:{' '}
              <strong className="text-red-800">
                {eliminacao.nomesLideres.join(', ')} ({eliminacao.pontosLider} pts)
              </strong>
              {eliminacao.pontosBarreiraPremio !== eliminacao.pontosLider && (
                <>
                  {' '}
                  · Barreira mínima para prêmio:{' '}
                  <strong className="text-red-800">
                    {eliminacao.nomesBarreiraPremio.join(', ')} (
                    {eliminacao.pontosBarreiraPremio} pts)
                  </strong>
                </>
              )}
              {eliminacao.empateNoTopoOcupaPremios && (
                <>
                  {' '}
                  · <strong>Empate no topo</strong> ocupa 1º e 2º — só quem alcançar{' '}
                  {eliminacao.pontosLider} pts ainda pode premiar.
                </>
              )}
              . Mesma pontuação das regras (grupos até 3 pts; mata-mata 6/9/12/15/18 por fase).
              O máximo considera <strong>todos</strong> os jogos sem resultado (melhor cenário),
              mesmo sem palpite preenchido — ex.: 16 avos ainda abertos = até +96 pts.
              Restam {eliminacao.jogosSemResultado} jogos sem resultado.
            </p>
          </div>

          {eliminacao.semPremio.length > 0 && (
            <div className="rounded-lg border border-red-300 bg-red-50/80 p-3">
              <p className="text-xs font-bold text-red-800 uppercase mb-2">
                Sem chance alguma de prêmio ({eliminacao.semPremio.length})
              </p>
              <ul className="space-y-2">
                {eliminacao.semPremio.map((e) => (
                  <li
                    key={e.id}
                    className="flex items-center justify-between gap-2 text-sm border-b border-red-100 pb-2 last:border-0"
                  >
                    <span className="font-semibold truncate">{e.nome}</span>
                    <span className="shrink-0 text-xs text-neutral-600 text-right">
                      <strong className="text-red-700">{e.pontosAtuais}</strong> pts · máx{' '}
                      <strong className="text-red-700">{e.maxPossivelFinal}</strong>
                      {e.pontosRestantesMax > 0 && (
                        <> (+{e.pontosRestantesMax})</>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {eliminacao.soSegundo.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50/80 p-3">
              <p className="text-xs font-bold text-amber-900 uppercase mb-2">
                Só pode ser 2º — sem chance de 1º ({eliminacao.soSegundo.length})
              </p>
              <ul className="space-y-2">
                {eliminacao.soSegundo.map((e) => (
                  <li
                    key={e.id}
                    className="flex items-center justify-between gap-2 text-sm border-b border-amber-100 pb-2 last:border-0"
                  >
                    <span className="font-semibold truncate">{e.nome}</span>
                    <span className="shrink-0 text-xs text-neutral-600 text-right">
                      <strong className="text-amber-800">{e.pontosAtuais}</strong> pts · máx{' '}
                      <strong className="text-amber-800">{e.maxPossivelFinal}</strong>
                      {e.pontosRestantesMax > 0 && (
                        <> (+{e.pontosRestantesMax})</>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {eliminacao.semPremio.length === 0 && eliminacao.soSegundo.length === 0 && (
            <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              Todos ainda disputam o 1º lugar ou podem alcançar a barreira mínima de prêmio.
            </p>
          )}
        </div>
      )}

      {eliminacao && eliminacao.jogosSemResultado === 0 && (
        <div className="ade-neu-card p-4 border border-neutral-200 bg-neutral-50/60 text-center">
          <p className="text-sm font-semibold text-neutral-800">
            Todos os jogos têm resultado — classificação definitiva.
          </p>
        </div>
      )}

      {s.topCravadores.length > 0 && (
        <div className="ade-neu-card p-4 border border-[#009b3a]/15">
          <h4 className="font-bold text-black flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-amber-500" />
            Top cravadores (placar exato)
          </h4>
          <ol className="space-y-2">
            {s.topCravadores.slice(0, 10).map((c, i) => (
              <li
                key={c.id}
                className="flex items-center justify-between gap-2 text-sm border-b border-neutral-100 pb-2 last:border-0"
              >
                <span className="font-semibold truncate">
                  {i + 1}. {c.nome}
                </span>
                <span className="shrink-0 text-xs text-neutral-600">
                  <strong className="text-[#009b3a]">{c.placaresExatos}</strong> exatos
                  {c.acertosVencedor > 0 && (
                    <> · {c.acertosVencedor} só vencedor</>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <AcertosPorJogoLista acertos={s.acertosPorJogo} />

      {s.totalPalpitesAvaliados === 0 && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Há resultados reais, mas ainda não há palpites de participantes para comparar.
        </p>
      )}
    </div>
  );
}

type RodadaSelecao =
  | { tipo: 'grupos'; rodada: RodadaGrupo }
  | { tipo: 'mm'; fase: FaseMataMata };

interface JogoComparado {
  id: string;
  mandante: string;
  visitante: string;
  contexto: string;
  ehMataMata: boolean;
  vencedorPenaltis: LadoPlacarMM | null;
}

type StatusPalpite = 'exato' | 'vencedor' | 'errou' | 'pendente' | 'sem';

function statusDoPalpite(
  jogo: JogoComparado,
  palpite: Placar | null | undefined,
  real: Placar | null | undefined
): StatusPalpite {
  if (!placarValido(palpite)) return 'sem';
  if (!placarValido(real)) return 'pendente';
  if (palpite.mandante === real.mandante && palpite.visitante === real.visitante) return 'exato';
  if (jogo.ehMataMata) {
    return acertouVencedorMataMata(palpite, real, jogo.vencedorPenaltis) ? 'vencedor' : 'errou';
  }
  return resultado1x2(palpite) === resultado1x2(real) ? 'vencedor' : 'errou';
}

const ESTILO_STATUS: Record<StatusPalpite, string> = {
  exato: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  vencedor: 'bg-amber-100 text-amber-800 border-amber-300',
  errou: 'bg-red-50 text-red-700 border-red-200',
  pendente: 'bg-neutral-100 text-neutral-600 border-neutral-300',
  sem: 'bg-neutral-50 text-neutral-400 border-neutral-200',
};

const LABEL_STATUS: Record<StatusPalpite, string> = {
  exato: 'Cravou',
  vencedor: 'Acertou o vencedor',
  errou: 'Errou',
  pendente: 'Aguardando',
  sem: 'Sem palpite',
};

interface ResumoPontosRodada {
  cravadas: Record<number, number>;
  acertosVencedor: Record<number, number>;
}

function resumirPontosRodada(
  jogos: JogoComparado[],
  palpites: PlacaresMap,
  resultadosReais: PlacaresMap,
  rodadaSel: RodadaSelecao
): ResumoPontosRodada {
  const cravadas: Record<number, number> = {};
  const acertosVencedor: Record<number, number> = {};
  jogos.forEach((j) => {
    const pal = palpites[j.id];
    const real = resultadosReais[j.id];
    if (!placarValido(pal) || !placarValido(real)) return;
    if (rodadaSel.tipo === 'grupos') {
      const pts = pontosJogo(pal, real);
      if (pts === 3) cravadas[3] = (cravadas[3] || 0) + 1;
      else if (pts === 1) acertosVencedor[1] = (acertosVencedor[1] || 0) + 1;
      return;
    }
    const pts = pontosJogoMataMata(pal, real, rodadaSel.fase, j.vencedorPenaltis);
    const ref = PONTUACAO_POR_FASE[rodadaSel.fase];
    if (pts === ref.placarExato) {
      cravadas[ref.placarExato] = (cravadas[ref.placarExato] || 0) + 1;
    } else if (pts === ref.acertoVencedor) {
      acertosVencedor[ref.acertoVencedor] = (acertosVencedor[ref.acertoVencedor] || 0) + 1;
    }
  });
  return { cravadas, acertosVencedor };
}

function formatarResumoPontos(resumo: ResumoPontosRodada): string {
  const crav = Object.keys(resumo.cravadas)
    .map(Number)
    .sort((a, b) => a - b)
    .map((pts) => {
      const q = resumo.cravadas[pts];
      return `${q} cravada${q === 1 ? '' : 's'} de ${pts} ponto${pts === 1 ? '' : 's'}`;
    });
  const ac = Object.keys(resumo.acertosVencedor)
    .map(Number)
    .sort((a, b) => a - b)
    .map((pts) => {
      const q = resumo.acertosVencedor[pts];
      return `${q} acerto${q === 1 ? '' : 's'} de ${pts} ponto${pts === 1 ? '' : 's'}`;
    });
  if (crav.length === 0 && ac.length === 0) {
    return 'Sem acertos (ou jogos ainda sem resultado)';
  }
  const partes: string[] = [];
  if (crav.length) partes.push(crav.join(', '));
  if (ac.length) partes.push(ac.join(', '));
  return partes.join(' · ');
}

function ChipPalpite({
  placar,
  status,
}: {
  placar: Placar | null | undefined;
  status: StatusPalpite;
}) {
  return (
    <div className={`rounded-lg border px-2 py-1.5 text-center ${ESTILO_STATUS[status]}`}>
      <div className="font-mono font-bold text-sm">
        {placarValido(placar) ? `${placar.mandante} × ${placar.visitante}` : '—'}
      </div>
      <div className="text-[9px] font-semibold uppercase tracking-wide mt-0.5">
        {LABEL_STATUS[status]}
      </div>
    </div>
  );
}

/**
 * Aba "Comparar palpites": escolhe uma rodada/fase e dois participantes e
 * mostra os palpites lado a lado (pareados). Quando o jogo já tem resultado
 * real, marca quem cravou e quem acertou só o vencedor.
 */
function ComparacaoParticipantesCard({
  participantes,
  resultadosReais,
  vencedoresPenaltisMM,
  resolverLadoMataMata,
}: {
  participantes: ParticipanteCopa[];
  resultadosReais: PlacaresMap;
  vencedoresPenaltisMM: VencedoresPenaltisMMMap;
  resolverLadoMataMata: (
    jogoId: string,
    qualLado: 'A' | 'B'
  ) => { timeId: string | null; rotulo: string };
}) {
  const opcoesRodada = useMemo(() => {
    const lista: { key: string; label: string; sel: RodadaSelecao }[] = [
      { key: 'g1', label: 'Grupos · Rodada 1', sel: { tipo: 'grupos', rodada: 1 } },
      { key: 'g2', label: 'Grupos · Rodada 2', sel: { tipo: 'grupos', rodada: 2 } },
      { key: 'g3', label: 'Grupos · Rodada 3', sel: { tipo: 'grupos', rodada: 3 } },
    ];
    FASES_MATA_MATA.forEach((f) => {
      lista.push({ key: `mm-${f}`, label: NOME_FASE[f], sel: { tipo: 'mm', fase: f } });
    });
    return lista;
  }, []);

  const [rodadaKey, setRodadaKey] = useState('g1');
  const [idA, setIdA] = useState('');
  const [idB, setIdB] = useState('');

  useEffect(() => {
    setIdA((atual) => (atual || participantes[0]?.id || ''));
    setIdB((atual) => (atual || participantes[1]?.id || participantes[0]?.id || ''));
  }, [participantes]);

  const rodadaSel = (opcoesRodada.find((o) => o.key === rodadaKey) ?? opcoesRodada[0]).sel;

  const jogos = useMemo<JogoComparado[]>(() => {
    if (rodadaSel.tipo === 'grupos') {
      return JOGOS_COPA_2026.filter((j) => j.rodada === rodadaSel.rodada).map((j) => ({
        id: j.id,
        mandante: nomeTimeBase(j.mandanteId),
        visitante: nomeTimeBase(j.visitanteId),
        contexto: `Grupo ${j.grupoId}`,
        ehMataMata: false,
        vencedorPenaltis: null,
      }));
    }
    return jogosDaFase(rodadaSel.fase).map((j) => ({
      id: j.id,
      mandante: resolverLadoMataMata(j.id, 'A').rotulo,
      visitante: resolverLadoMataMata(j.id, 'B').rotulo,
      contexto: `${formatarDataHoraIsoBrt(j.dataHoraBrt, { comAno: true })} · FIFA ${j.numeroFifa}`,
      ehMataMata: true,
      vencedorPenaltis: vencedoresPenaltisMM[j.id] ?? null,
    }));
  }, [rodadaSel, resolverLadoMataMata, vencedoresPenaltisMM]);

  const partA = participantes.find((p) => p.id === idA) ?? null;
  const partB = participantes.find((p) => p.id === idB) ?? null;

  const resumoA = useMemo(
    () =>
      partA
        ? resumirPontosRodada(jogos, partA.palpites, resultadosReais, rodadaSel)
        : null,
    [partA, jogos, resultadosReais, rodadaSel]
  );
  const resumoB = useMemo(
    () =>
      partB
        ? resumirPontosRodada(jogos, partB.palpites, resultadosReais, rodadaSel)
        : null,
    [partB, jogos, resultadosReais, rodadaSel]
  );

  if (participantes.length === 0) {
    return (
      <div className="ade-neu-card p-6 border border-amber-200 bg-amber-50/60 text-center">
        <Users className="w-10 h-10 text-amber-600 mx-auto mb-2" />
        <p className="font-semibold text-amber-900">Nenhum participante cadastrado</p>
        <p className="text-xs text-amber-800 mt-1">
          Cadastre participantes (ou baixe os palpites) para comparar.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="ade-neu-card p-4 border border-[#009b3a]/15 space-y-3">
        <h4 className="font-bold text-black flex items-center gap-2">
          <ArrowLeftRight className="w-4 h-4 text-[#009b3a]" />
          Comparar palpites por rodada
        </h4>
        <p className="text-[11px] text-neutral-600">
          Escolha a rodada/fase e dois participantes. Os palpites ficam lado a lado;
          quando o jogo já tem resultado, marcamos quem cravou (verde) e quem acertou
          só o vencedor (amarelo).
        </p>

        <div className="grid gap-2 sm:grid-cols-3">
          <label className="text-xs font-semibold text-neutral-700">
            Rodada / fase
            <select
              value={rodadaKey}
              onChange={(e) => setRodadaKey(e.target.value)}
              className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-sm"
            >
              {opcoesRodada.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-semibold text-emerald-700">
            Participante 1 (você)
            <select
              value={idA}
              onChange={(e) => setIdA(e.target.value)}
              className="mt-1 w-full rounded-lg border border-emerald-300 bg-white px-2 py-1.5 text-sm"
            >
              <option value="">—</option>
              {participantes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                  {p.setor ? ` (${p.setor})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-semibold text-blue-700">
            Participante 2
            <select
              value={idB}
              onChange={(e) => setIdB(e.target.value)}
              className="mt-1 w-full rounded-lg border border-blue-300 bg-white px-2 py-1.5 text-sm"
            >
              <option value="">—</option>
              {participantes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                  {p.setor ? ` (${p.setor})` : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {!partA || !partB ? (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Escolha os dois participantes para ver a comparação.
        </p>
      ) : partA.id === partB.id ? (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Escolha dois participantes diferentes para comparar.
        </p>
      ) : (
        <div className="ade-neu-card p-3 border border-[#009b3a]/15">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-start pb-2 border-b border-neutral-200 mb-2">
            <div className="text-center min-w-0">
              <span className="text-xs font-bold text-emerald-700 truncate block">
                {partA.nome}
              </span>
              {resumoA && (
                <p className="text-[10px] text-emerald-800/90 mt-1 leading-snug">
                  {formatarResumoPontos(resumoA)}
                </p>
              )}
            </div>
            <span className="text-[10px] font-bold text-neutral-400 uppercase pt-0.5">jogo</span>
            <div className="text-center min-w-0">
              <span className="text-xs font-bold text-blue-700 truncate block">
                {partB.nome}
              </span>
              {resumoB && (
                <p className="text-[10px] text-blue-800/90 mt-1 leading-snug">
                  {formatarResumoPontos(resumoB)}
                </p>
              )}
            </div>
          </div>
          <ul className="space-y-2">
            {jogos.map((j) => {
              const real = resultadosReais[j.id];
              const pa = partA.palpites[j.id];
              const pb = partB.palpites[j.id];
              return (
                <li
                  key={j.id}
                  className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center text-xs"
                >
                  <ChipPalpite placar={pa} status={statusDoPalpite(j, pa, real)} />
                  <div className="text-center px-1 min-w-[6.5rem]">
                    <div className="font-semibold text-black leading-tight">{j.mandante}</div>
                    <div className="text-[10px] text-neutral-400">x</div>
                    <div className="font-semibold text-black leading-tight">{j.visitante}</div>
                    <div className="text-[9px] text-neutral-400 mt-0.5">{j.contexto}</div>
                    {placarValido(real) ? (
                      <div className="mt-1 font-mono font-black text-[#009b3a]">
                        {fmtPlacar(real)}
                      </div>
                    ) : (
                      <div className="mt-1 text-[10px] text-neutral-400">sem resultado</div>
                    )}
                  </div>
                  <ChipPalpite placar={pb} status={statusDoPalpite(j, pb, real)} />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Card mostrado no fim do bolão com mensagens prontas para cada
 * participante. Só aparece quando todos os 104 jogos têm resultado
 * lançado (e empates do mata-mata têm pênaltis resolvidos).
 */
function MensagensFinaisCard({
  fimBolao,
  mensagens,
}: {
  fimBolao: { bolaoEncerrado: boolean; jogosMMComPlacar: number; empatesSemPenaltis: number; jogosGruposComPlacar: number };
  mensagens: MensagemFinal[];
}) {
  const [copiados, setCopiados] = useState<Record<string, number>>({});

  const marcarCopiado = (id: string) => {
    setCopiados((prev) => ({ ...prev, [id]: Date.now() }));
    window.setTimeout(() => {
      setCopiados((prev) => {
        const { [id]: _omit, ...rest } = prev;
        return rest;
      });
    }, 1800);
  };

  const copiarMensagem = async (m: MensagemFinal) => {
    try {
      await navigator.clipboard.writeText(m.texto);
      marcarCopiado(m.participanteId);
    } catch {
      window.prompt('Copie manualmente (Ctrl+C):', m.texto);
    }
  };

  const copiarTodas = async () => {
    const txt = mensagens
      .map(
        (m) =>
          `=== ${m.posicao}º · ${m.nome} (${m.categoria === 'campeao' ? 'CAMPEÃO' : m.categoria === 'premiado' ? 'PREMIADO' : 'sem prêmio'}) ===\n${m.texto}`
      )
      .join('\n\n──────────\n\n');
    try {
      await navigator.clipboard.writeText(txt);
      alert('Todas as mensagens foram copiadas (separadas por divisor).');
    } catch {
      window.prompt('Copie manualmente (Ctrl+C):', txt);
    }
  };

  const abrirWhatsApp = (m: MensagemFinal) => {
    const texto = encodeURIComponent(m.texto);
    window.open(`https://wa.me/?text=${texto}`, '_blank', 'noopener');
  };

  if (!fimBolao.bolaoEncerrado) {
    const totalJogos = 72 + 32;
    const feitos = fimBolao.jogosGruposComPlacar + fimBolao.jogosMMComPlacar;
    return (
      <div className="ade-neu-card p-3 border-2 border-dashed border-neutral-300 bg-neutral-50/40">
        <p className="text-xs font-semibold text-neutral-700 flex items-center gap-1.5">
          <MessageCircle className="w-4 h-4 text-neutral-500" />
          Mensagens finais para os participantes
        </p>
        <p className="text-[11px] text-neutral-600 mt-1">
          Quando todos os <strong>{totalJogos}</strong> jogos tiverem resultado lançado, este
          card vai gerar uma mensagem personalizada pra cada um (campeão, premiados, sem prêmio).
          Faltam <strong>{totalJogos - feitos}</strong> jogo(s)
          {fimBolao.empatesSemPenaltis > 0
            ? ` · ${fimBolao.empatesSemPenaltis} empate(s) do mata-mata sem vencedor de pênaltis marcado.`
            : '.'}
        </p>
      </div>
    );
  }

  if (mensagens.length === 0) return null;

  const corCategoria = (cat: MensagemFinal['categoria']) =>
    cat === 'campeao'
      ? 'bg-amber-100 text-amber-900 border-amber-400'
      : cat === 'premiado'
        ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
        : 'bg-neutral-100 text-neutral-700 border-neutral-300';

  const rotuloCategoria = (cat: MensagemFinal['categoria']) =>
    cat === 'campeao' ? 'Campeão' : cat === 'premiado' ? 'Premiado' : 'Sem prêmio';

  return (
    <div className="ade-neu-card p-4 border-2 border-amber-300 bg-amber-50/40">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h4 className="font-bold text-black flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            Bolão encerrado — mensagens prontas pros participantes
          </h4>
          <p className="text-[11px] text-neutral-700 mt-0.5">
            Todos os 104 jogos têm resultado. Copie a mensagem de cada um e mande no WhatsApp.
          </p>
        </div>
        <button
          type="button"
          onClick={copiarTodas}
          className="px-3 py-1.5 rounded-lg text-xs font-bold bg-[#009b3a] hover:bg-[#007a2e] text-white inline-flex items-center gap-1.5"
          title="Copia todas as mensagens em um único bloco, separadas por divisor"
        >
          <Send className="w-3.5 h-3.5" />
          Copiar todas
        </button>
      </div>

      <ul className="space-y-2">
        {mensagens.map((m) => {
          const copiadoAgora = Boolean(copiados[m.participanteId]);
          return (
            <li
              key={m.participanteId}
              className={`rounded-lg border-2 p-3 bg-white ${
                m.categoria === 'campeao'
                  ? 'border-amber-400'
                  : m.categoria === 'premiado'
                    ? 'border-emerald-300'
                    : 'border-neutral-200'
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-black text-sm">
                    {m.posicao}º · {m.nome}
                  </span>
                  <span
                    className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${corCategoria(m.categoria)}`}
                  >
                    {rotuloCategoria(m.categoria)}
                  </span>
                  {m.premioFinal > 0 && (
                    <span className="text-[10px] font-semibold text-emerald-700">
                      {formatarReais(m.premioFinal)}
                    </span>
                  )}
                </div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => copiarMensagem(m)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold inline-flex items-center gap-1 ${
                      copiadoAgora
                        ? 'bg-emerald-600 text-white'
                        : 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200 border border-neutral-300'
                    }`}
                  >
                    {copiadoAgora ? '✓ copiado' : 'Copiar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => abrirWhatsApp(m)}
                    className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-[#25D366] hover:bg-[#1ebe5b] text-white inline-flex items-center gap-1"
                    title="Abrir WhatsApp Web já com a mensagem (escolha o contato na hora)"
                  >
                    WhatsApp
                  </button>
                </div>
              </div>
              <pre className="whitespace-pre-wrap text-[12px] text-neutral-800 font-sans leading-snug">
                {m.texto}
              </pre>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function ResultadosCopa() {
  const [aba, setAba] = useState<AbaCopa>('oficial');
  const [expandidoPlacarId, setExpandidoPlacarId] = useState('');
  const [tamanhoFonteRegras, setTamanhoFonteRegras] = useState<string>('14');
  const estadoInicial = carregarEstado();
  const [resultadosReais, setResultadosReais] = useState<PlacaresMap>(
    () => estadoInicial.resultadosReais
  );
  const [vencedoresPenaltisMM, setVencedoresPenaltisMM] = useState<VencedoresPenaltisMMMap>(
    () => estadoInicial.vencedoresPenaltisMM ?? {}
  );
  const [resultadosManuais, setResultadosManuais] = useState<ResultadosManuaisMap>(
    () => estadoInicial.resultadosManuais ?? {}
  );
  const [participantes, setParticipantes] = useState<ParticipanteCopa[]>(
    () => estadoInicial.participantes
  );
  const [participanteAtivoId, setParticipanteAtivoId] = useState<string | null>(null);
  const [palpitesSubVisao, setPalpitesSubVisao] = useState<'grupos' | 'matamata'>('grupos');
  const [palpitesMmFaseFiltro, setPalpitesMmFaseFiltro] = useState<FaseMataMata | 'todas'>('todas');
  const [palpitesMmStatusFiltro, setPalpitesMmStatusFiltro] = useState<
    'todos' | 'preenchidos' | 'vazios'
  >('todos');
  // Correção de placar pelo organizador (grava na planilha sem mexer na
  // pontuação). Guarda o id do participante em correção e um backup dos
  // palpites para o botão "Cancelar".
  const [corrigindoServidorId, setCorrigindoServidorId] = useState<string | null>(null);
  const [snapshotCorrecao, setSnapshotCorrecao] = useState<PlacaresMap | null>(null);
  const [salvandoCorrecao, setSalvandoCorrecao] = useState(false);
  const [novoNome, setNovoNome] = useState('');
  const [qrPagamento, setQrPagamento] = useState<string>(() => carregarQrPagamento());
  const [organizador, setOrganizador] = useState<OrganizadorInfo>(() => carregarOrganizador());
  const [modalOrgAberto, setModalOrgAberto] = useState(false);
  const [orgNomeInput, setOrgNomeInput] = useState('');
  const [orgWhatsInput, setOrgWhatsInput] = useState('');
  const [orgUrlPublicaInput, setOrgUrlPublicaInput] = useState('');
  const [convidados, setConvidados] = useState<Convidado[]>(() => carregarConvidados());
  const [novoConvidadoNome, setNovoConvidadoNome] = useState('');
  const [novoConvidadoWhats, setNovoConvidadoWhats] = useState('');
  const [sheetsEndpoint, setSheetsEndpoint] = useState<string>(() => carregarSheetsEndpoint());
  const [sheetsEndpointInput, setSheetsEndpointInput] = useState<string>('');
  const [modalSheetsAberto, setModalSheetsAberto] = useState(false);
  const [idsRecebidos, setIdsRecebidos] = useState<string[]>(() => carregarIdsRecebidos());
  const [idsIgnorados, setIdsIgnorados] = useState<IdIgnorado[]>(() =>
    carregarIdsIgnorados()
  );
  const [dataCorte, setDataCorte] = useState<string>(() => carregarDataCorte());
  const [sincronizando, setSincronizando] = useState(false);
  const [ultimaSync, setUltimaSync] = useState<string>('');
  /** Hora da última checagem automática dos placares oficiais (API na nuvem). */
  const [ultimaSyncResultados, setUltimaSyncResultados] = useState<string>('');
  /** origemId dos participantes que ENTRARAM na última sincronização.
   *  Usado para pintar o card de dourado. Some ao atualizar de novo. */
  const [idsNovosParticipantes, setIdsNovosParticipantes] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('copa-novos-participantes-ade');
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr.filter((x) => typeof x === 'string');
      }
    } catch {
      /* ignore */
    }
    return [];
  });
  const registrarNovosParticipantes = useCallback((ids: string[]) => {
    const limpos = Array.from(new Set(ids.filter(Boolean)));
    setIdsNovosParticipantes(limpos);
    try {
      localStorage.setItem('copa-novos-participantes-ade', JSON.stringify(limpos));
    } catch {
      /* quota */
    }
  }, []);
  const [configMM, setConfigMM] = useState<ConfigMataMata>(() => carregarConfigMM());
  const importacaoTentadaRef = useRef(false);
  const [publicandoTabela, setPublicandoTabela] = useState(false);
  const [tabelaPublicadaEm, setTabelaPublicadaEm] = useState<string>(() => {
    try {
      return localStorage.getItem('copa-tabela-publicada-em-ade') || '';
    } catch {
      return '';
    }
  });
  const [historicoBolao, setHistoricoBolao] = useState(() => carregarHistoricoBolao());
  /** Refs para salvar estado ao desmontar e evitar closure vazia na sincronização */
  const resultadosReaisRef = useRef(resultadosReais);
  const vencedoresPenaltisMMRef = useRef(vencedoresPenaltisMM);
  const resultadosManuaisRef = useRef(resultadosManuais);
  const participantesRef = useRef(participantes);
  useEffect(() => {
    resultadosReaisRef.current = resultadosReais;
  }, [resultadosReais]);
  useEffect(() => {
    vencedoresPenaltisMMRef.current = vencedoresPenaltisMM;
  }, [vencedoresPenaltisMM]);
  useEffect(() => {
    resultadosManuaisRef.current = resultadosManuais;
  }, [resultadosManuais]);
  useEffect(() => {
    participantesRef.current = participantes;
  }, [participantes]);

  /** Evita loop ao aplicar resultados vindos da nuvem. */
  const pulandoSaveNuvemRef = useRef(false);
  const timerSaveNuvemRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enviarResultadosNuvem = useCallback(
    async (
      reais: PlacaresMap,
      penaltis: VencedoresPenaltisMMMap,
      manuais: ResultadosManuaisMap
    ): Promise<boolean> => {
      if (!sheetsEndpoint || !endpointGasValido(sheetsEndpoint)) return false;
      const atualizadoEm = new Date().toISOString();
      try {
        const resp = await fetch(sheetsEndpoint, {
          method: 'POST',
          redirect: 'follow',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify({
            acao: 'salvarResultados',
            resultadosReais: reais,
            vencedoresPenaltisMM: penaltis,
            manuais,
            atualizadoEm,
          }),
        });
        if (!resp.ok) return false;
        const json = (await resp.json()) as { ok?: boolean; atualizadoEm?: string };
        if (!json.ok) return false;
        salvarResultadosNuvemEm(json.atualizadoEm || atualizadoEm);
        return true;
      } catch {
        return false;
      }
    },
    [sheetsEndpoint]
  );

  const aplicarResultadosDaNuvem = useCallback(
    (payload: ResultadosNuvemPayload): boolean => {
      const cloudEm = payload.atualizadoEm || '';
      const localEm = carregarResultadosNuvemEm();
      const localReais = resultadosReaisRef.current;
      const localPenaltis = vencedoresPenaltisMMRef.current;
      const localManuais = resultadosManuaisRef.current;
      const cloudTemNovidade = nuvemTemPlacaresNovosParaLocal(
        localReais,
        localManuais,
        payload
      );
      if (cloudEm && localEm && cloudEm <= localEm && !cloudTemNovidade) return false;
      const mergedReais = { ...(payload.resultadosReais ?? {}) };
      const mergedPenaltis = { ...(payload.vencedoresPenaltisMM ?? {}) };
      const mergedManuais = { ...(payload.manuais ?? {}), ...localManuais };
      Object.keys(localManuais).forEach((jogoId) => {
        if (localReais[jogoId]) mergedReais[jogoId] = localReais[jogoId];
        if (localPenaltis[jogoId]) mergedPenaltis[jogoId] = localPenaltis[jogoId];
      });
      pulandoSaveNuvemRef.current = true;
      setResultadosReais(mergedReais);
      setVencedoresPenaltisMM(mergedPenaltis);
      setResultadosManuais(mergedManuais);
      if (cloudEm) salvarResultadosNuvemEm(cloudEm);
      window.setTimeout(() => {
        pulandoSaveNuvemRef.current = false;
      }, 150);
      return true;
    },
    []
  );

  const sincronizarResultadosComNuvem = useCallback(async (): Promise<string> => {
    if (!sheetsEndpoint || !endpointGasValido(sheetsEndpoint)) return '';
    try {
      const jr = await fetchGasGet<ResultadosNuvemPayload>(sheetsEndpoint, {
        acao: 'resultados',
      });
      if (!jr?.ok) return '';
      const localEm = carregarResultadosNuvemEm();
      const cloudEm = jr.atualizadoEm || '';
      const reaisLocal = resultadosReaisRef.current;
      const penaltisLocal = vencedoresPenaltisMMRef.current;
      const manuaisLocal = resultadosManuaisRef.current;
      const cloudTemNovidade = nuvemTemPlacaresNovosParaLocal(
        reaisLocal,
        manuaisLocal,
        jr
      );
      if (
        (cloudEm && (!localEm || cloudEm > localEm)) ||
        cloudTemNovidade
      ) {
        const extra =
          jr.ultimaApiEm && jr.ultimaApiLog
            ? ` API: ${jr.ultimaApiLog} (${new Date(jr.ultimaApiEm).toLocaleString('pt-BR')}).`
            : '';
        return aplicarResultadosDaNuvem(jr)
          ? `Resultados oficiais baixados da nuvem.${extra}`
          : '';
      }
      if (
        resultadosLocaisDiferemDoCloud(reaisLocal, penaltisLocal, manuaisLocal, jr) ||
        (!cloudEm && Object.keys(reaisLocal).length > 0)
      ) {
        const ok = await enviarResultadosNuvem(reaisLocal, penaltisLocal, manuaisLocal);
        return ok ? 'Resultados oficiais enviados para a nuvem.' : '';
      }
      return '';
    } catch {
      return '';
    }
  }, [sheetsEndpoint, aplicarResultadosDaNuvem, enviarResultadosNuvem]);

  /**
   * Busca automática dos placares oficiais (preenchidos pela API no Apps Script).
   * Roda ao abrir e a cada 3 minutos, sem precisar clicar em "Atualizar palpites".
   * Usa um ref para sempre chamar a versão mais recente sem recriar o intervalo.
   */
  const sincronizarResultadosRef = useRef(sincronizarResultadosComNuvem);
  useEffect(() => {
    sincronizarResultadosRef.current = sincronizarResultadosComNuvem;
  }, [sincronizarResultadosComNuvem]);
  useEffect(() => {
    if (!sheetsEndpoint || !endpointGasValido(sheetsEndpoint)) return;
    let cancelado = false;
    const rodar = () => {
      if (cancelado) return;
      void sincronizarResultadosRef.current().then((msg) => {
        if (!cancelado && msg) {
          setUltimaSyncResultados(new Date().toLocaleTimeString('pt-BR'));
        }
      });
    };
    rodar();
    const id = window.setInterval(rodar, 3 * 60 * 1000);
    return () => {
      cancelado = true;
      window.clearInterval(id);
    };
  }, [sheetsEndpoint]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_ORG, JSON.stringify(organizador));
    } catch {
      /* quota */
    }
  }, [organizador]);

  /** Ao abrir a Copa, grava no storage se ainda tinha URL do Netlify antigo. */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_ORG);
      if (!raw) return;
      const p = JSON.parse(raw) as Partial<OrganizadorInfo>;
      const urlBruta = typeof p.urlPublica === 'string' ? p.urlPublica : '';
      const urlNova = resolverUrlPublicaOrganizador(urlBruta);
      if (normalizarUrlPublica(urlBruta) === urlNova) return;
      const next: OrganizadorInfo = {
        nome: typeof p.nome === 'string' ? p.nome : '',
        whats: typeof p.whats === 'string' ? p.whats : '',
        urlPublica: urlNova,
      };
      localStorage.setItem(STORAGE_KEY_ORG, JSON.stringify(next));
      setOrganizador((prev) => ({ ...prev, urlPublica: urlNova }));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_CONV, JSON.stringify(convidados));
    } catch {
      /* quota */
    }
  }, [convidados]);

  useEffect(() => {
    try {
      if (sheetsEndpoint) localStorage.setItem(STORAGE_KEY_SHEETS, sheetsEndpoint);
      else localStorage.removeItem(STORAGE_KEY_SHEETS);
    } catch {
      /* quota */
    }
  }, [sheetsEndpoint]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_RECEBIDOS, JSON.stringify(idsRecebidos));
    } catch {
      /* quota */
    }
  }, [idsRecebidos]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_IGNORADOS, JSON.stringify(idsIgnorados));
    } catch {
      /* quota */
    }
  }, [idsIgnorados]);

  const restauradoMMRef = useRef(false);
  const [prontoSalvarMM, setProntoSalvarMM] = useState(false);
  useEffect(() => {
    if (restauradoMMRef.current) return;
    restauradoMMRef.current = true;
    (async () => {
      try {
        const mesclado = await restaurarConfigMM();
        setConfigMM((prev) => {
          const slotsPrev = [1, 2, 3, 4, 5, 6, 7, 8].filter((s) => prev.melhoresTerceiros[s]).length;
          const slotsNovo = [1, 2, 3, 4, 5, 6, 7, 8].filter((s) => mesclado.melhoresTerceiros[s]).length;
          if (slotsNovo > slotsPrev || Object.keys(mesclado.cruzamentos16).length > Object.keys(prev.cruzamentos16).length) {
            return mesclado;
          }
          return prev;
        });
      } finally {
        setProntoSalvarMM(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!prontoSalvarMM) return;
    void salvarConfigMMRobusto(configMM);
  }, [configMM, prontoSalvarMM]);

  useEffect(() => {
    if (importacaoTentadaRef.current) return;
    importacaoTentadaRef.current = true;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('modo') !== 'importar') return;
    const dados = params.get('dados') || '';
    const limparUrl = () => {
      try {
        window.history.replaceState({}, '', window.location.pathname);
      } catch {
        /* ignore */
      }
    };
    if (!dados) {
      alert('Link sem palpites.');
      limparUrl();
      return;
    }
    const decoded = decodePalpitesUrl(dados);
    if (!decoded || !decoded.nome) {
      alert('Link de palpite inválido ou corrompido.');
      limparUrl();
      return;
    }
    setAba('participantes');
    const identificacao = decoded.setor
      ? `${decoded.nome} (Setor: ${decoded.setor})`
      : decoded.nome;
    const ok = confirm(
      `Importar palpites de "${identificacao}"?\n\nSerão criados/atualizados os palpites desse participante.`
    );
    if (!ok) {
      limparUrl();
      return;
    }
    setParticipantes((prev) => {
      const idx = prev.findIndex(
        (p) => p.nome.trim().toLowerCase() === decoded.nome.trim().toLowerCase()
      );
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = {
          ...next[idx],
          setor: decoded.setor || next[idx].setor || '',
          palpites: mesclarPalpitesGruposEMM(
            normalizarPlacaresImportados(decoded.palpites),
            {},
            next[idx].palpites
          ),
        };
        return next;
      }
      return [
        ...prev,
        {
          id: criarId(),
          nome: decoded.nome,
          setor: decoded.setor || '',
          palpites: decoded.palpites,
          criadoEm: new Date().toLocaleString('pt-BR'),
          pagou: false,
        },
      ];
    });
    setTimeout(() => alert(`Palpites de "${identificacao}" importados com sucesso!`), 50);
    limparUrl();
  }, []);

  const abrirModalOrganizador = () => {
    setOrgNomeInput(organizador.nome);
    setOrgWhatsInput(organizador.whats);
    setOrgUrlPublicaInput(organizador.urlPublica || '');
    setModalOrgAberto(true);
  };

  const salvarOrganizador = () => {
    const nomeLimpo = orgNomeInput.trim();
    const whatsLimpo = orgWhatsInput.replace(/\D/g, '');
    const urlLimpa = resolverUrlPublicaOrganizador(normalizarUrlPublica(orgUrlPublicaInput));
    if (whatsLimpo && whatsLimpo.length < 10) {
      alert('Número do WhatsApp incompleto. Use DDD + número (ex.: 62999998888).');
      return;
    }
    if (urlLimpa && !/^https:\/\/.+/i.test(urlLimpa)) {
      alert('A URL pública deve começar com https:// (ex.: https://seusite.com/bolao)');
      return;
    }
    const next: OrganizadorInfo = { nome: nomeLimpo, whats: whatsLimpo, urlPublica: urlLimpa };
    setOrganizador(next);
    try {
      localStorage.setItem(STORAGE_KEY_ORG, JSON.stringify(next));
    } catch {
      /* quota */
    }
    setModalOrgAberto(false);
  };

  const aplicarUrlOficialBolao = () => {
    setOrgUrlPublicaInput(URL_PUBLICA_BOLAO_OFICIAL);
  };

  const construirLinkPalpitesBase = useCallback(
    (opts?: { novo?: boolean }): string => {
      const base = obterBaseLinkPublico(organizador);
      if (!base) return '';
      const partes: string[] = ['modo=palpitar'];
      if (organizador.nome) partes.push(`para=${encodeURIComponent(organizador.nome)}`);
      if (organizador.whats) partes.push(`whats=${organizador.whats}`);
      if (sheetsEndpoint && endpointGasValido(sheetsEndpoint)) {
        partes.push(`api=${encodeURIComponent(sheetsEndpoint)}`);
      }
      if (opts?.novo) partes.push('novo=1');
      const d = new Date();
      const dia = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`;
      const hora = `${String(d.getUTCHours()).padStart(2, '0')}${String(d.getUTCMinutes()).padStart(2, '0')}`;
      partes.push(`v=${dia}${hora}`);
      return `${base}/?${partes.join('&')}`;
    },
    [organizador, sheetsEndpoint]
  );

  const linkPalpitesPublico = useCallback(
    () => construirLinkPalpitesBase(),
    [construirLinkPalpitesBase]
  );

  const linkPalpitesPublicoNovo = useCallback(
    () => construirLinkPalpitesBase({ novo: true }),
    [construirLinkPalpitesBase]
  );

  const copiarLinkComResetCopia = () => {
    const link = linkPalpitesPublicoNovo();
    if (!link || !link.startsWith('https://')) {
      avisoUrlPublica();
      return;
    }
    navigator.clipboard
      ?.writeText(link)
      .then(() =>
        alert(
          'Link de "começar do zero" copiado!\n\n' +
            'Mande esse link para a pessoa que precisa refazer o palpite — quando ela abrir, ' +
            'a cópia antiga no celular dela é apagada e ela vê o formulário em branco.'
        )
      )
      .catch(() => {
        prompt('Copie o link:', link);
      });
  };

  /** Link de ENVIO EXTRA (pós-prazo) para liberar 1 reenvio de grupos a um
   *  participante específico. Só funciona se você rodar liberarPalpiteExtra()
   *  no Apps Script com o mesmo nome+setor. Trava de novo após o envio. */
  const copiarLinkEnvioExtra = () => {
    const base = linkPalpitesPublicoNovo();
    if (!base || !base.startsWith('https://')) {
      avisoUrlPublica();
      return;
    }
    const link = `${base}${base.includes('?') ? '&' : '?'}extra=1`;
    navigator.clipboard
      ?.writeText(link)
      .then(() =>
        alert(
          'Link de ENVIO EXTRA copiado!\n\n' +
            '1. No Apps Script da planilha, rode a função "liberarPalpiteExtra" (ajuste o nome/setor dentro dela).\n' +
            '2. Mande ESTE link para a pessoa liberada.\n' +
            '3. Ela preenche e envia (com o mesmo nome/setor). Assim que enviar, trava de novo sozinho.\n\n' +
            'Obs.: só a pessoa liberada consegue enviar — para os demais continua encerrado.'
        )
      )
      .catch(() => {
        prompt('Copie o link de envio extra:', link);
      });
  };

  const avisoUrlPublica = () => {
    alert(
      'Configure a URL pública do bolão para o link ficar azul no WhatsApp.\n\n' +
        '1. Publique a pasta dist/ na internet (site, GitHub Pages, etc.).\n' +
        '2. Em "Configurar organizador", cole o endereço https://...\n' +
        '3. Copie a mensagem de novo e envie.\n\n' +
        'No app pelo nw.exe o link vira file:// e o WhatsApp não reconhece.'
    );
    abrirModalOrganizador();
  };

  const mensagemPalpite = useCallback(
    (nomeConvidado?: string) => {
      const link = linkPalpitesPublico();
      const saudacao = nomeConvidado ? `Olá ${nomeConvidado}!` : 'Olá!';
      const instrucao = sheetsEndpoint
        ? 'Basta preencher e clicar em Enviar.'
        : 'Preencha e me envie de volta pelo WhatsApp.';
      return (
        `${saudacao}\n\n` +
        `Bolão Copa do Mundo 2026 — preencha seus palpites no link abaixo:\n` +
        `${link}\n\n` +
        instrucao
      );
    },
    [linkPalpitesPublico, sheetsEndpoint]
  );

  const copiarParaWhatsApp = (soLink = false) => {
    const link = linkPalpitesPublico();
    if (!link || !link.startsWith('https://')) {
      avisoUrlPublica();
      return;
    }
    const texto = soLink ? link : mensagemPalpite();
    navigator.clipboard
      ?.writeText(texto)
      .then(() =>
        alert(
          soLink
            ? 'Link copiado! Cole no WhatsApp — deve aparecer em azul.'
            : 'Mensagem copiada! Cole no WhatsApp — o link deve aparecer em azul.'
        )
      )
      .catch(() => {
        prompt(soLink ? 'Copie o link:' : 'Copie a mensagem:', texto);
      });
  };

  /** Link público para palpitar mata-mata (?modo=palpitar-mm). */
  const linkMataMataPublico = useCallback((): string => {
    const base = obterBaseLinkPublico(organizador);
    if (!base) return '';
    const partes: string[] = ['modo=palpitar-mm'];
    if (organizador.nome) partes.push(`para=${encodeURIComponent(organizador.nome)}`);
    if (organizador.whats) partes.push(`whats=${organizador.whats}`);
    if (sheetsEndpoint && endpointGasValido(sheetsEndpoint)) {
      partes.push(`api=${encodeURIComponent(sheetsEndpoint)}`);
    }
    const d = new Date();
    const dia = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`;
    const hora = `${String(d.getUTCHours()).padStart(2, '0')}${String(d.getUTCMinutes()).padStart(2, '0')}`;
    partes.push(`v=${dia}${hora}`);
    return `${base}/?${partes.join('&')}`;
  }, [organizador, sheetsEndpoint]);

  const mensagemPalpiteMataMata = useCallback(
    (nomeConvidado?: string) => {
      const link = linkMataMataPublico();
      const saudacao = nomeConvidado ? `Olá ${nomeConvidado}!` : 'Olá!';
      return (
        `${saudacao}\n\n` +
        `Bolão Copa do Mundo 2026 — preencha seus palpites no link abaixo:\n` +
        `${link}\n\n` +
        `Basta preencher e clicar em Enviar.`
      );
    },
    [linkMataMataPublico]
  );

  const copiarMataMataParaWhatsApp = (soLink = false) => {
    const link = linkMataMataPublico();
    if (!link || !link.startsWith('https://')) {
      avisoUrlPublica();
      return;
    }
    if (!sheetsEndpoint || !endpointGasValido(sheetsEndpoint)) {
      alert(
        'O mata-mata só funciona em modo direto (com Google Apps Script).\n\n' +
          'Configure a URL do Apps Script em Resultados Copa → Participantes → "configurar".'
      );
      return;
    }
    const texto = soLink ? link : mensagemPalpiteMataMata();
    navigator.clipboard
      ?.writeText(texto)
      .then(() =>
        alert(
          soLink
            ? 'Link do mata-mata copiado! Cole no WhatsApp.'
            : 'Mensagem do mata-mata copiada! Cole no WhatsApp.'
        )
      )
      .catch(() => {
        prompt(soLink ? 'Copie o link:' : 'Copie a mensagem:', texto);
      });
  };


  const compartilharLinkPalpites = () => {
    if (!organizador.whats) {
      abrirModalOrganizador();
      return;
    }
    const link = linkPalpitesPublico();
    if (!link || !link.startsWith('https://')) {
      avisoUrlPublica();
      return;
    }
    const texto = encodeURIComponent(mensagemPalpite());
    window.open(`https://wa.me/?text=${texto}`, '_blank', 'noopener');
  };

  const limparWhats = (w: string) => w.replace(/\D/g, '');

  const adicionarConvidado = () => {
    const nomeLimpo = novoConvidadoNome.trim();
    const whatsLimpo = limparWhats(novoConvidadoWhats);
    if (!nomeLimpo) {
      alert('Informe o nome do convidado.');
      return;
    }
    if (whatsLimpo.length < 10) {
      alert('Informe o WhatsApp do convidado com DDD (ex.: 62999998888).');
      return;
    }
    if (
      convidados.some(
        (c) => c.nome.toLowerCase() === nomeLimpo.toLowerCase() || c.whats === whatsLimpo
      )
    ) {
      alert('Esse convidado já está na lista.');
      return;
    }
    setConvidados((prev) =>
      [...prev, { id: criarId(), nome: nomeLimpo, whats: whatsLimpo }].sort((a, b) =>
        a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' })
      )
    );
    setNovoConvidadoNome('');
    setNovoConvidadoWhats('');
  };

  const removerConvidado = (id: string) => {
    setConvidados((prev) => prev.filter((c) => c.id !== id));
  };

  const enviarLinkParaConvidado = (c: Convidado) => {
    const link = linkPalpitesPublico();
    if (!link || !link.startsWith('https://')) {
      avisoUrlPublica();
      return;
    }
    const texto = encodeURIComponent(mensagemPalpite(c.nome.split(' ')[0]));
    window.open(`https://wa.me/${c.whats}?text=${texto}`, '_blank', 'noopener');
  };

  const enviarLinkParaTodos = () => {
    if (convidados.length === 0) {
      alert('Adicione convidados primeiro.');
      return;
    }
    const link = linkPalpitesPublico();
    if (!link || !link.startsWith('https://')) {
      avisoUrlPublica();
      return;
    }
    const ok = confirm(
      `Abrir ${convidados.length} abas do WhatsApp Web (uma por convidado)?\n\nVocê só precisa clicar em "Enviar" em cada uma.`
    );
    if (!ok) return;
    convidados.forEach((c, i) => {
      setTimeout(() => {
        const texto = encodeURIComponent(mensagemPalpite(c.nome.split(' ')[0]));
        window.open(`https://wa.me/${c.whats}?text=${texto}`, '_blank', 'noopener');
      }, i * 350);
    });
  };

  const abrirModalSheets = () => {
    setSheetsEndpointInput(sheetsEndpoint);
    setModalSheetsAberto(true);
  };

  const salvarSheetsEndpoint = () => {
    const v = sheetsEndpointInput.trim();
    if (v && !endpointGasValido(v)) {
      alert(
        'Cole a URL do Google Apps Script que termina em /exec\n(ex.: https://script.google.com/macros/s/XXXX/exec)'
      );
      return;
    }
    setSheetsEndpoint(v);
    setModalSheetsAberto(false);
  };

  const sincronizarPalpitesRecebidos = useCallback(async () => {
    if (!sheetsEndpoint) {
      abrirModalSheets();
      return;
    }
    setSincronizando(true);
    try {
      // Lê do disco na hora (evita palpite apagado voltar se clicar sync logo após apagar)
      const ignoradosAtual = carregarIdsIgnorados();
      setIdsIgnorados(ignoradosAtual);
      const dataCorteAtual = carregarDataCorte();
      const dataCorteMs = parseEmMs(dataCorteAtual);

      const json = await fetchGasGet<{
        ok?: boolean;
        palpites?: Array<{
          id: string;
          nome: string;
          setor?: string;
          fase?: string;
          palpites: PlacaresMap;
          enviadoEm?: string;
        }>;
        erro?: string;
      }>(sheetsEndpoint, { acao: 'listar' });
      if (!json.ok) throw new Error(json.erro || 'Resposta inválida');

      const mapaIgnorado = new Map(ignoradosAtual.map((x) => [x.id, x.ignoradoEm]));
      const mapaIgnoradoChave = new Map<string, string>();
      ignoradosAtual.forEach((x) => {
        if (x.nomeNorm !== undefined) {
          mapaIgnoradoChave.set(
            `${x.nomeNorm}|${x.setorNorm || ''}`,
            x.ignoradoEm
          );
        }
      });

      const mapaPermanenteId = new Set(
        ignoradosAtual.filter((x) => x.permanente !== false).map((x) => x.id)
      );
      const mapaPermanenteChave = new Set(
        ignoradosAtual
          .filter((x) => x.permanente !== false && x.nomeNorm !== undefined)
          .map((x) => `${x.nomeNorm}|${x.setorNorm || ''}`)
      );

      const isIgnoradoAgora = (
        id: string,
        enviadoEm: string | undefined,
        nome: string,
        setor?: string
      ): boolean => {
        const chave = chaveNomeSetor(nome, setor);
        // 1. Bloqueio permanente (padrão) → sempre ignora
        if (mapaPermanenteId.has(id)) return true;
        if (mapaPermanenteChave.has(chave)) return true;
        // 2. Bloqueio temporário (legado) → ignora se enviadoEm <= ignoradoEm
        const envMs = parseEmMs(enviadoEm || '');
        const checar = (ignoradoEm: string): boolean => {
          if (!ignoradoEm) return true;
          const ignMs = parseEmMs(ignoradoEm);
          if (!ignMs) return true;
          if (!envMs) return true;
          return envMs <= ignMs;
        };
        if (mapaIgnorado.has(id) && checar(mapaIgnorado.get(id) || '')) return true;
        if (mapaIgnoradoChave.has(chave) && checar(mapaIgnoradoChave.get(chave) || '')) {
          return true;
        }
        return false;
      };

      // Detecção automática: palpites que estão em idsRecebidos, sem participante
      // correspondente, e NÃO estão ignorados → foram apagados antes (sem registrar).
      // Palpites antes da dataCorte são tratados separadamente (não bloqueia).
      // Usa ref + disco: evita marcar todos como excluídos se a closure estiver vazia
      // (ex.: após trocar de aba do app antes da sincronização terminar).
      const participantesParaLogica =
        participantesRef.current.length > 0
          ? participantesRef.current
          : carregarEstado().participantes;
      const origemIdsAtuais = new Set(
        participantesParaLogica.map((p) => p.origemId).filter(Boolean) as string[]
      );
      const dataCorteMsLocal = dataCorteMs;
      const novosIgnorados: IdIgnorado[] = [];
      (json.palpites || []).forEach((r) => {
        const envMs = parseEmMs(r.enviadoEm || '');
        const antesCorte = dataCorteMsLocal && envMs && envMs <= dataCorteMsLocal;
        if (antesCorte) return;
        if (
          idsRecebidos.includes(r.id) &&
          !origemIdsAtuais.has(r.id) &&
          !mapaIgnorado.has(r.id)
        ) {
          // Marca com data atual — se houver reenvio depois, vamos reimportar
          const ignoradoEmAuto = new Date().toISOString();
          novosIgnorados.push({
            id: r.id,
            ignoradoEm: ignoradoEmAuto,
            nomeNorm: normalizarNome(r.nome),
            setorNorm: normalizarNome(r.setor || ''),
            nomeOriginal: r.nome,
            setorOriginal: r.setor || '',
            permanente: true,
          });
          mapaIgnorado.set(r.id, ignoradoEmAuto);
          mapaPermanenteId.add(r.id);
          mapaPermanenteChave.add(
            `${normalizarNome(r.nome)}|${normalizarNome(r.setor || '')}`
          );
        }
      });
      if (novosIgnorados.length) {
        const merged = [...ignoradosAtual, ...novosIgnorados];
        salvarIdsIgnoradosImediato(merged);
        setIdsIgnorados(merged);
      }

      // Bloqueio permanente: NUNCA designora automaticamente.
      // Pra liberar reenvio, o usuário precisa clicar em "Permitir reenvio"
      // no histórico de palpites apagados.
      const designorarIds = new Set<string>();

      const antesDoCorte = (enviadoEm?: string): boolean => {
        if (!dataCorteMs) return false;
        const envMs = parseEmMs(enviadoEm || '');
        if (!envMs) return true; // sem data → trata como antigo
        return envMs <= dataCorteMs;
      };

      const recebidos = (json.palpites || []).filter(
        (r) =>
          fasePlanilhaNormalizada(r.fase) === 'grupos' &&
          !antesDoCorte(r.enviadoEm) &&
          !isIgnoradoAgora(r.id, r.enviadoEm, r.nome, r.setor)
      );

      /** Palpites do mata-mata ficam em outra linha da planilha (fase=matamata). */
      const mapaMMPorChave = new Map<string, PlacaresMap>();
      (json.palpites || []).forEach((r) => {
        if (fasePlanilhaNormalizada(r.fase) !== 'matamata') return;
        if (antesDoCorte(r.enviadoEm)) return;
        if (isIgnoradoAgora(r.id, r.enviadoEm, r.nome, r.setor)) return;
        const chave = chaveNomeSetor(r.nome, r.setor);
        const atual = mapaMMPorChave.get(chave) || {};
        const mmNorm = normalizarPlacaresImportados(r.palpites || {});
        mapaMMPorChave.set(chave, { ...atual, ...mmNorm });
      });
      atualizarCacheMMSync(mapaMMPorChave);
      const mmCacheLocal = carregarCacheMMSync();
      const novosIds: string[] = [];
      let qtdNovos = 0;
      let qtdAtualizados = 0;
      let qtdIgnorados = 0;
      let qtdAntesDoCorte = 0;
      (json.palpites || []).forEach((r) => {
        if (fasePlanilhaNormalizada(r.fase) !== 'grupos') return;
        if (antesDoCorte(r.enviadoEm)) {
          qtdAntesDoCorte++;
        } else if (isIgnoradoAgora(r.id, r.enviadoEm, r.nome, r.setor)) {
          qtdIgnorados++;
        }
      });
      let qtdReimportados = designorarIds.size;
      const palpitesEncerradosAgora = palpitesEncerrados();
      /** IDs que ainda estão ATIVOS na planilha (não excluídos). Se um
       *  participante local tem origemId que NÃO está aqui, significa que o
       *  palpite foi excluído na planilha — seja por você no admin, seja pela
       *  pessoa clicando em "Começar do zero" no celular — então removemos
       *  localmente também. */
      const idsAtivosPlanilha = new Set<string>();
      (json.palpites || []).forEach((r) => {
        if (fasePlanilhaNormalizada(r.fase) !== 'grupos') return;
        if (antesDoCorte(r.enviadoEm)) return;
        idsAtivosPlanilha.add(r.id);
      });
      let qtdRemovidosForaDoApp = 0;
      // Detecta quem ENTROU agora (origem/nome que não existia antes desta
      // sincronização). Calculado a partir do snapshot anterior, FORA do updater
      // do setParticipantes — assim não depende do timing do React (createRoot
      // faz batching e o updater pode rodar depois desta linha).
      const novosOrigemIds: string[] = recebidos
        .filter((r) => {
          const nomeNorm = normalizarNome(r.nome);
          const setorNorm = normalizarNome(r.setor || '');
          const jaExiste = participantesParaLogica.some(
            (p) =>
              p.origemId === r.id ||
              (normalizarNome(p.nome) === nomeNorm &&
                normalizarNome(p.setor || '') === setorNorm)
          );
          return !jaExiste;
        })
        .map((r) => r.id);
      setParticipantes((prev) => {
        let next = prev.slice();
        recebidos.forEach((r) => {
          const nomeNorm = normalizarNome(r.nome);
          const setorNorm = normalizarNome(r.setor || '');
          const chave = chaveNomeSetor(r.nome, r.setor);
          const mmPlanilha = {
            ...(mmCacheLocal[chave] || {}),
            ...(mapaMMPorChave.get(chave) || {}),
          };
          const idx = next.findIndex(
            (p) =>
              p.origemId === r.id ||
              (normalizarNome(p.nome) === nomeNorm &&
                normalizarNome(p.setor || '') === setorNorm)
          );
          if (idx >= 0) {
            const enviadoAnterior = next[idx].ultimaSyncEnviadoEm || '';
            const enviadoAtual = r.enviadoEm || '';
            const palpitesNovos = mesclarPalpitesGruposEMM(
              normalizarPlacaresImportados(r.palpites || {}),
              mmPlanilha,
              next[idx].palpites
            );
            const palpitesMudaram =
              JSON.stringify(next[idx].palpites) !== JSON.stringify(palpitesNovos);
            const enviadoMudou = Boolean(
              enviadoAtual && enviadoAtual !== enviadoAnterior
            );
            if (!palpitesMudaram && !enviadoMudou) {
              // Sem mudança nos palpites. Mesmo assim, depois do prazo,
              // garantimos que quem veio do link fique TRAVADO e com origemId
              // (necessário para a correção de placar no servidor). Sem isso,
              // quem não reenviou ficaria editável e sem o botão de correção.
              const precisaTravar =
                palpitesEncerradosAgora && !next[idx].bloqueado;
              const precisaOrigem = !next[idx].origemId;
              if (precisaTravar || precisaOrigem) {
                next[idx] = {
                  ...next[idx],
                  bloqueado: next[idx].bloqueado || palpitesEncerradosAgora,
                  origemId: next[idx].origemId || r.id,
                  ultimaSyncEnviadoEm:
                    next[idx].ultimaSyncEnviadoEm || enviadoAtual,
                };
                qtdAtualizados++;
              }
              return; // sem mudanca de placar na planilha
            }
            const dataExibicao = enviadoAtual
              ? new Date(enviadoAtual).toLocaleString('pt-BR')
              : new Date().toLocaleString('pt-BR');
            next[idx] = {
              ...next[idx],
              nome: r.nome,
              setor: r.setor || next[idx].setor || '',
              palpites: palpitesNovos,
              // Data exibida no card = ultimo envio na planilha (reenvio atualiza)
              criadoEm: dataExibicao,
              bloqueado: palpitesEncerradosAgora,
              origemId: r.id,
              ultimaSyncEnviadoEm: enviadoAtual,
            };
            qtdAtualizados++;
            if (!idsRecebidos.includes(r.id)) novosIds.push(r.id);
          } else {
            const palpitesNovos = mesclarPalpitesGruposEMM(
              normalizarPlacaresImportados(r.palpites || {}),
              mmPlanilha
            );
            next = [
              ...next,
              {
                id: criarId(),
                nome: r.nome,
                setor: r.setor || '',
                palpites: palpitesNovos,
                criadoEm: r.enviadoEm
                  ? new Date(r.enviadoEm).toLocaleString('pt-BR')
                  : new Date().toLocaleString('pt-BR'),
                pagou: false,
                bloqueado: palpitesEncerradosAgora,
                origemId: r.id,
                ultimaSyncEnviadoEm: r.enviadoEm || '',
              },
            ];
            qtdNovos++;
            novosIds.push(r.id);
          }
        });

        // Só mata-mata mudou na planilha (linha separada) — mescla sem esperar reenvio de grupos
        mapaMMPorChave.forEach((mmPlanilhaSheet, chave) => {
          const idx = next.findIndex(
            (p) => chaveNomeSetor(p.nome, p.setor) === chave
          );
          if (idx < 0) return;
          const mmPlanilha = {
            ...(mmCacheLocal[chave] || {}),
            ...mmPlanilhaSheet,
          };
          const palpitesNovos = mesclarPalpitesGruposEMM(
            extrairPalpitesGrupos(next[idx].palpites),
            mmPlanilha,
            next[idx].palpites
          );
          if (JSON.stringify(next[idx].palpites) === JSON.stringify(palpitesNovos)) {
            return;
          }
          next[idx] = { ...next[idx], palpites: palpitesNovos };
          qtdAtualizados++;
        });

        // Garante MM do cache local (útil se a planilha não devolveu alguma linha)
        next = aplicarCacheMMNosParticipantes(next);

        // Remove participantes cujo palpite original foi excluído na planilha
        // (ex.: a pessoa clicou em "Começar do zero" no celular). Mantém
        // participantes adicionados manualmente (sem origemId).
        const antes = next.length;
        next = next.filter((p) => {
          if (!p.origemId) return true; // adicionado manualmente, preserva
          return idsAtivosPlanilha.has(p.origemId);
        });
        qtdRemovidosForaDoApp = antes - next.length;
        salvarEstadoCopaRobusto(
          resultadosReaisRef.current,
          next,
          vencedoresPenaltisMMRef.current,
          resultadosManuaisRef.current
        );
        return next;
      });
      if (novosIds.length) {
        setIdsRecebidos((prev) =>
          [...prev, ...novosIds.filter((id) => !prev.includes(id))]
        );
      }
      setUltimaSync(new Date().toLocaleTimeString('pt-BR'));
      const msgResultadosNuvem = await sincronizarResultadosComNuvem();
      // Pinta de dourado só quem ENTROU agora. Se ninguém novo entrou,
      // limpa o destaque (cards voltam à cor padrão).
      registrarNovosParticipantes(novosOrigemIds);
      // Reimportados entram em qtdAtualizados/qtdNovos quando a iteração principal rodou
      void qtdReimportados;
      const partesMsg: string[] = [];
      if (qtdNovos > 0) partesMsg.push(`${qtdNovos} novo(s) palpite(s)`);
      if (qtdAtualizados > 0) partesMsg.push(`${qtdAtualizados} atualizado(s)`);
      if (qtdRemovidosForaDoApp > 0)
        partesMsg.push(
          `${qtdRemovidosForaDoApp} removido(s) (palpite cancelado/excluído na planilha)`
        );
      if (qtdIgnorados > 0)
        partesMsg.push(`${qtdIgnorados} bloqueado(s) (excluídos)`);
      if (qtdAntesDoCorte > 0)
        partesMsg.push(
          `${qtdAntesDoCorte} antigo(s) ignorado(s) (envio anterior ao "Limpar todos")`
        );
      const totalRelevante =
        qtdNovos + qtdAtualizados + qtdIgnorados + qtdAntesDoCorte + qtdRemovidosForaDoApp;
      const sufixoResultados = msgResultadosNuvem ? `\n\n${msgResultadosNuvem}` : '';
      if (totalRelevante === 0) {
        alert(
          msgResultadosNuvem
            ? `Nenhum palpite novo. Lista já está em dia.${sufixoResultados}`
            : 'Nenhum palpite novo. Lista já está em dia.'
        );
      } else if (qtdNovos + qtdAtualizados === 0) {
        alert(
          `Sincronizado! Nenhum palpite novo.\n\n${partesMsg.join('\n')}\n\nSe alguém precisar mandar de novo, peça pra enviar pelo link — o app vai aceitar normalmente.${sufixoResultados}`
        );
      } else {
        alert(`Sincronizado!\n${partesMsg.join('\n')}.${sufixoResultados}`);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(
        `Não foi possível buscar os palpites.\n\nDetalhe: ${msg}\n\nVerifique se o link do Google Apps Script está correto e publicado.`
      );
    } finally {
      setSincronizando(false);
    }
  }, [
    sheetsEndpoint,
    idsRecebidos,
    idsIgnorados,
    dataCorte,
    registrarNovosParticipantes,
    sincronizarResultadosComNuvem,
  ]);

  useEffect(() => {
    try {
      if (qrPagamento) localStorage.setItem(STORAGE_KEY_QR, qrPagamento);
      else localStorage.removeItem(STORAGE_KEY_QR);
    } catch {
      /* quota */
    }
  }, [qrPagamento]);

  const onSelecionarQrPagamento = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Selecione uma imagem (PNG ou JPG) do QR Code.');
      return;
    }
    if (file.size > 1.5 * 1024 * 1024) {
      alert('Imagem muito grande. Use uma imagem do QR Code com até ~1,5 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (result) setQrPagamento(result);
    };
    reader.onerror = () => alert('Não foi possível ler a imagem.');
    reader.readAsDataURL(file);
  };

  const toggleParticipantePagou = (id: string) => {
    setParticipantes((prev) =>
      prev.map((p) => (p.id === id ? { ...p, pagou: !p.pagou } : p))
    );
  };

  const setFaltasParticipante = (id: string, faltas: number) => {
    const valor = Math.max(0, Math.floor(Number(faltas) || 0));
    setParticipantes((prev) =>
      prev.map((p) => (p.id === id ? { ...p, faltas: valor } : p))
    );
  };

  /**
   * Restaura do IndexedDB na montagem se o localStorage estiver vazio ou
   * desatualizado. NW.js pode resetar o localStorage entre versões/limpezas
   * de cache do Chromium, mas o IndexedDB persiste com mais segurança.
   *
   * IMPORTANTE: este efeito precisa rodar ANTES do efeito de salvar,
   * caso contrário o save sobrescreve o IDB com `[]` antes do read
   * concluir — foi exatamente o bug que existia até a v0.0.336.
   * Por isso o efeito de salvar abaixo é "gated" pelo `prontoParaSalvar`.
   */
  const restauradoIDBRef = useRef(false);
  const ignorarSaveInicialCopaRef = useRef(false);
  const [prontoParaSalvar, setProntoParaSalvar] = useState(false);
  useEffect(() => {
    if (restauradoIDBRef.current) return;
    restauradoIDBRef.current = true;
    (async () => {
      try {
        const local = carregarEstado();
        const idb = await lerCopaIDB();
        const localN = local.participantes.length;
        const idbN = idb?.participantes?.length ?? 0;
        const mmLocal = contarMMTotalParticipantes(local.participantes);
        const mmIdb = contarMMTotalParticipantes(idb?.participantes ?? []);
        const usarIdb =
          idb &&
          (idbN > localN ||
            mmIdb > mmLocal ||
            (localN === 0 &&
              (idbN > 0 || Object.keys(idb.resultadosReais || {}).length > 0)));

        if (usarIdb && idb) {
          ignorarSaveInicialCopaRef.current = true;
          setParticipantes(aplicarCacheMMNosParticipantes(idb.participantes));
          setResultadosReais(idb.resultadosReais ?? {});
          setVencedoresPenaltisMM(idb.vencedoresPenaltisMM ?? {});
          setResultadosManuais(idb.resultadosManuais ?? {});
          if (idbN > localN || mmIdb > mmLocal) {
            salvarEstadoCopaRobusto(
              idb.resultadosReais ?? {},
              aplicarCacheMMNosParticipantes(idb.participantes),
              idb.vencedoresPenaltisMM ?? {},
              idb.resultadosManuais ?? {}
            );
          }
        } else if (
          local.participantes.length > 0 ||
          Object.keys(local.resultadosReais || {}).length > 0
        ) {
          void salvarCopaIDB(
            local.resultadosReais,
            local.participantes,
            local.vencedoresPenaltisMM ?? {},
            local.resultadosManuais ?? {}
          );
        }

        const histLocal = carregarHistoricoBolao();
        if (histLocal.length === 0) {
          const histIdb = await carregarHistoricoBolaoIDB();
          if (histIdb?.length) {
            try {
              localStorage.setItem('copa-historico-bolao-ade', JSON.stringify(histIdb));
            } catch {
              /* quota */
            }
            setHistoricoBolao(histIdb);
          }
        }
      } finally {
        setProntoParaSalvar(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!prontoParaSalvar) return;
    if (ignorarSaveInicialCopaRef.current) {
      ignorarSaveInicialCopaRef.current = false;
      return;
    }
    void (async () => {
      if (participantes.length === 0) {
        const idb = await lerCopaIDB();
        if (idb && (idb.participantes?.length ?? 0) > 0) return;
      }
      salvarEstadoCopaRobusto(
        resultadosReais,
        participantes,
        vencedoresPenaltisMM,
        resultadosManuais
      );
    })();
  }, [resultadosReais, participantes, vencedoresPenaltisMM, resultadosManuais, prontoParaSalvar]);

  /** Salva resultados oficiais na nuvem ~2s após cada alteração (casa/trabalho). */
  useEffect(() => {
    if (!prontoParaSalvar || !sheetsEndpoint) return;
    if (pulandoSaveNuvemRef.current) return;
    if (timerSaveNuvemRef.current) clearTimeout(timerSaveNuvemRef.current);
    timerSaveNuvemRef.current = setTimeout(() => {
      void enviarResultadosNuvem(
        resultadosReaisRef.current,
        vencedoresPenaltisMMRef.current,
        resultadosManuaisRef.current
      );
    }, 2000);
    return () => {
      if (timerSaveNuvemRef.current) clearTimeout(timerSaveNuvemRef.current);
    };
  }, [
    resultadosReais,
    vencedoresPenaltisMM,
    resultadosManuais,
    prontoParaSalvar,
    sheetsEndpoint,
    enviarResultadosNuvem,
  ]);

  /** Garante gravação ao sair da aba / desmontar o componente */
  useEffect(() => {
    return () => {
      if (!restauradoIDBRef.current) return;
      salvarEstadoCopaRobusto(
        resultadosReaisRef.current,
        participantesRef.current,
        vencedoresPenaltisMMRef.current,
        resultadosManuaisRef.current
      );
    };
  }, []);

  /**
   * Garante gravação ao FECHAR o app/aba (beforeunload, pagehide).
   * Importante para NW.js — quando o usuário clica em X, o processo termina
   * abruptamente. Sem isso, mudanças recentes podem se perder.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const persistirAgora = () => {
      if (!restauradoIDBRef.current) return;
      try {
        salvarEstadoCopaRobusto(
          resultadosReaisRef.current,
          participantesRef.current,
          vencedoresPenaltisMMRef.current,
          resultadosManuaisRef.current
        );
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('beforeunload', persistirAgora);
    window.addEventListener('pagehide', persistirAgora);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') persistirAgora();
    });
    return () => {
      window.removeEventListener('beforeunload', persistirAgora);
      window.removeEventListener('pagehide', persistirAgora);
    };
  }, []);

  const participanteAtivo = participantes.find((p) => p.id === participanteAtivoId);

  const participantesOrdemAlfabetica = useMemo(
    () =>
      [...participantes].sort((a, b) =>
        a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' })
      ),
    [participantes]
  );

  const placarGeral = useMemo(() => {
    return participantes
      .map((p) => {
        const calc = pontuacaoParticipante(
          p.palpites,
          resultadosReais,
          JOGOS_COPA_2026,
          JOGOS_MATA_MATA,
          vencedoresPenaltisMM
        );
        return { participante: p, ...calc };
      })
      .sort((a, b) => b.total - a.total);
  }, [participantes, resultadosReais, vencedoresPenaltisMM]);

  const linhasClassificacao = useMemo<LinhaClassificacaoBolao[]>(
    () =>
      placarGeral.map((item, idx) => ({
        id: item.participante.id,
        nome: item.participante.nome,
        setor: item.participante.setor,
        pontos: item.total,
        posicao: idx + 1,
        faltas: item.participante.faltas,
        pagou: item.participante.pagou,
        detalheGrupos: item.jogos,
        placarExato: item.placarExato,
        resultado1x2: item.resultado1x2,
        mataMata: item.mataMata,
        placarExatoMM: item.placarExatoMM,
        acertoVencedorMM: item.acertoVencedorMM,
      })),
    [placarGeral]
  );

  const premiacao = useMemo(() => {
    const entradas = placarGeral.map((x) => ({
      id: x.participante.id,
      nome: x.participante.nome,
      faltas: x.participante.faltas ?? 0,
      pontos: x.total,
    }));
    return calcularPremiacao(entradas);
  }, [placarGeral]);

  /** Avalia se o bolão chegou ao fim (todos os 104 jogos resolvidos). */
  const fimBolao = useMemo(
    () => avaliarFimBolao(resultadosReais, vencedoresPenaltisMM),
    [resultadosReais, vencedoresPenaltisMM]
  );

  /** Mensagens prontas pra cada participante quando o bolão termina. */
  const mensagensFinais = useMemo<MensagemFinal[]>(() => {
    if (!fimBolao.bolaoEncerrado) return [];
    if (placarGeral.length === 0) return [];
    const campeao = placarGeral[0]?.participante.nome || '';
    const entradas = placarGeral.map((item, idx) => ({
      id: item.participante.id,
      nome: item.participante.nome,
      posicao: idx + 1,
      pontos: item.total,
      premioFinal: premiacao[idx]?.premioFinal ?? 0,
      campeao,
    }));
    return montarMensagensFinais(entradas);
  }, [fimBolao.bolaoEncerrado, placarGeral, premiacao]);

  const gruposCompletos = useMemo(
    () => fasesGruposEncerrada(resultadosReais),
    [resultadosReais]
  );

  /** Classificação de grupos para montar opções dos slots M3 (oficial parcial ou simulação). */
  const placaresParaMelhoresTerceiros = useMemo(() => {
    if (Object.keys(resultadosReais).some((k) => placarValido(resultadosReais[k]))) {
      return resultadosReais;
    }
    if (participanteAtivo) {
      const gruposPart = extrairPalpitesGrupos(participanteAtivo.palpites);
      if (Object.keys(gruposPart).some((k) => placarValido(gruposPart[k]))) return gruposPart;
    }
    return {};
  }, [resultadosReais, participanteAtivo]);

  const gruposProntosParaMelhoresTerceiros = useMemo(
    () => fasesGruposEncerrada(placaresParaMelhoresTerceiros),
    [placaresParaMelhoresTerceiros]
  );

  /** Sugere os 8 melhores 3ºs só quando os slots ainda estão vazios (não sobrescreve manual). */
  useEffect(() => {
    if (!gruposCompletos) return;
    const auto = atribuirMelhoresTerceirosAutomatico(resultadosReais);
    if (!auto) return;
    setConfigMM((prev) => {
      const temAlgum = [1, 2, 3, 4, 5, 6, 7, 8].some((s) => prev.melhoresTerceiros[s]);
      if (temAlgum) return prev;
      return { ...prev, melhoresTerceiros: auto };
    });
  }, [gruposCompletos, resultadosReais]);

  const resolverLadoMataMata = useCallback(
    (jogoId: string, qualLado: 'A' | 'B') =>
      resolverLadoMataMataDePlacares(jogoId, qualLado, {
        placares: resultadosReais,
        melhoresTerceiros: configMM.melhoresTerceiros,
        cruzamentos16: configMM.cruzamentos16,
        vencedoresPenaltis: vencedoresPenaltisMM,
      }),
    [resultadosReais, configMM, vencedoresPenaltisMM]
  );

  /** Simulação na aba Palpites: grupos + MM do participante; resultados oficiais preenchem lacunas. */
  const resolverLadoPalpitesParticipante = useCallback(
    (jogoId: string, qualLado: 'A' | 'B', palpitesParticipante: PlacaresMap) => {
      const placaresSim: PlacaresMap = {
        ...extrairPalpitesGrupos(palpitesParticipante),
        ...extrairPalpitesMM(palpitesParticipante),
        ...resultadosReais,
      };
      return resolverLadoMataMataDePlacares(jogoId, qualLado, {
        placares: placaresSim,
        melhoresTerceiros: configMM.melhoresTerceiros,
        cruzamentos16: configMM.cruzamentos16,
        vencedoresPenaltis: vencedoresPenaltisMM,
      });
    },
    [resultadosReais, configMM, vencedoresPenaltisMM]
  );

  /**
   * Estatísticas pós-jogo (aba "Estatísticas"). Monta a lista de jogos com
   * times já resolvidos (grupos = fixos; mata-mata = via resolverLadoMataMata)
   * e delega o cálculo pesado pra função pura. Só roda quando a aba está ativa
   * pra não pesar à toa.
   */
  const dadosEstatisticas = useMemo(() => {
    if (aba !== 'estatisticas') return null;

    const jogosStats: JogoInfoStats[] = [];

    JOGOS_COPA_2026.forEach((j) => {
      jogosStats.push({
        id: j.id,
        rotulo: `${nomeTimeBase(j.mandanteId)} x ${nomeTimeBase(j.visitanteId)}`,
        contexto: `Grupo ${j.grupoId} · Rodada ${j.rodada}`,
        ehMataMata: false,
      });
    });

    JOGOS_MATA_MATA.forEach((j) => {
      const ladoA = resolverLadoMataMata(j.id, 'A');
      const ladoB = resolverLadoMataMata(j.id, 'B');
      jogosStats.push({
        id: j.id,
        rotulo: `${ladoA.rotulo} x ${ladoB.rotulo}`,
        contexto: `${NOME_FASE[j.fase]} · Jogo ${j.ordem}`,
        ehMataMata: true,
        vencedorPenaltis: vencedoresPenaltisMM[j.id] ?? null,
      });
    });

    const parts = participantes.map((p) => ({
      id: p.id,
      nome: p.nome,
      palpites: p.palpites,
    }));

    return {
      estatisticas: calcularEstatisticasPosJogo(jogosStats, parts, resultadosReais),
      eliminacao: calcularEliminacaoMatematica(parts, resultadosReais, vencedoresPenaltisMM),
    };
  }, [aba, participantes, resultadosReais, vencedoresPenaltisMM, resolverLadoMataMata]);

  /** Estado de publicação de cada fase no link público. */
  const [statusPublicacaoFaseMM, setStatusPublicacaoFaseMM] = useState<
    Record<FaseMataMata, { status: 'idle' | 'publicando' | 'ok' | 'erro'; msg?: string; em?: string }>
  >(() => ({
    '16avos': { status: 'idle' },
    oitavas: { status: 'idle' },
    quartas: { status: 'idle' },
    semi: { status: 'idle' },
    terceiro: { status: 'idle' },
    final: { status: 'idle' },
  }));

  // Sondagem inicial: ao abrir o app, descobrir quais fases JÁ estão publicadas
  // no servidor (mesmo que esta sessão não tenha clicado em "Publicar"). Isso
  // permite mostrar o botão "Despublicar" para fases publicadas em sessões
  // anteriores. Roda uma vez quando muda o sheetsEndpoint.
  useEffect(() => {
    if (!sheetsEndpoint || !endpointGasValido(sheetsEndpoint)) return;
    let cancelado = false;
    (async () => {
      try {
        const url =
          sheetsEndpoint +
          (sheetsEndpoint.indexOf('?') === -1 ? '?' : '&') +
          'acao=jogosFaseMM&_t=' +
          Date.now();
        const resp = await fetch(url);
        if (!resp.ok) return;
        const txt = await resp.text();
        let json: { ok?: boolean; fases?: Array<{ fase?: string; jogos?: unknown[]; publicadoEm?: string }> };
        try {
          json = JSON.parse(txt);
        } catch {
          return; // não é JSON (script desatualizado)
        }
        if (cancelado || !json.ok) return;
        const fases = json.fases || [];
        setStatusPublicacaoFaseMM((prev) => {
          const next = { ...prev };
          fases.forEach((f) => {
            const k = String(f.fase || '').trim() as FaseMataMata;
            if (!k) return;
            const qtd = Array.isArray(f.jogos) ? f.jogos.length : 0;
            // Só marca como 'ok' se ainda não houve interação local nesta sessão
            // (não sobrescreve um 'erro' ou 'publicando' que esteja em curso).
            if (next[k] && next[k].status === 'idle' && qtd > 0) {
              next[k] = {
                status: 'ok',
                em: f.publicadoEm || '',
                msg: `${qtd} jogo${qtd === 1 ? '' : 's'} publicado${qtd === 1 ? '' : 's'} (sessão anterior).`,
              };
            }
          });
          return next;
        });
      } catch {
        /* offline ou erro de rede — silencioso */
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [sheetsEndpoint]);

  /** Envia para o Apps Script a lista atual de confrontos de uma fase. Pode ser
   *  vazia (caso de "despublicar"). Atualiza o status da fase. */
  const enviarPublicacaoFaseMM = useCallback(
    async (
      fase: FaseMataMata,
      jogosParaEnviar: Array<{
        id: string;
        ladoA: { timeId: string; rotulo: string };
        ladoB: { timeId: string; rotulo: string };
      }>,
      msgSucesso: string
    ) => {
      setStatusPublicacaoFaseMM((prev) => ({ ...prev, [fase]: { status: 'publicando' } }));
      try {
        const resp = await fetch(sheetsEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify({ acao: 'publicarFaseMM', fase, jogos: jogosParaEnviar }),
          redirect: 'follow',
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = (await resp.json()) as {
          ok?: boolean;
          erro?: string;
          publicadoEm?: string;
          qtd?: number;
        };
        if (!json.ok) throw new Error(json.erro || 'Resposta inválida.');
        setStatusPublicacaoFaseMM((prev) => ({
          ...prev,
          [fase]: {
            status: 'ok',
            em: json.publicadoEm || new Date().toISOString(),
            msg: msgSucesso,
          },
        }));
        return true;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setStatusPublicacaoFaseMM((prev) => ({
          ...prev,
          [fase]: {
            status: 'erro',
            msg:
              `Erro ao publicar: ${msg}.\n\n` +
              'Verifique se o Apps Script está na versão 6.3 (cole o código novo e ' +
              'crie uma "Nova versão" em Implantar → Gerenciar implantações).',
          },
        }));
        return false;
      }
    },
    [sheetsEndpoint]
  );

  /** Publica os matchups de uma fase do mata-mata para o link público (Apps Script v6.3+). */
  const publicarFaseMataMata = useCallback(
    async (fase: FaseMataMata) => {
      if (!sheetsEndpoint || !endpointGasValido(sheetsEndpoint)) {
        alert(
          'Você precisa configurar a URL do Google Apps Script (v6.3+) primeiro.\n\n' +
            'Em Resultados Copa → Participantes, clique em "configurar" no modo de envio.'
        );
        return;
      }
      const jogos = JOGOS_MATA_MATA.filter((j) => j.fase === fase);
      // Publica APENAS confrontos já totalmente definidos (ambos os lados com timeId).
      // Assim, conforme grupos vão terminando, o organizador clica de novo em "Publicar"
      // e mais jogos aparecem no link público — sem aparecer placeholder do tipo
      // "2º Grupo A x 2º Grupo B".
      const jogosDefinidos = jogos
        .map((j) => {
          const a = resolverLadoMataMata(j.id, 'A');
          const b = resolverLadoMataMata(j.id, 'B');
          return {
            id: j.id,
            ladoA: { timeId: a.timeId || '', rotulo: a.rotulo },
            ladoB: { timeId: b.timeId || '', rotulo: b.rotulo },
          };
        })
        .filter((j) => j.ladoA.timeId && j.ladoB.timeId);
      const completos = jogosDefinidos.length;
      const jaPublicada = statusPublicacaoFaseMM[fase]?.status === 'ok';

      // Caso especial: nenhum confronto definido agora.
      // Se a fase já estava publicada antes (ex.: usuário simulou e depois apagou
      // os resultados), oferecemos DESPUBLICAR — manda lista vazia pro servidor
      // pra que os participantes parem de ver confrontos antigos.
      if (completos === 0) {
        if (jaPublicada) {
          const ok = confirm(
            `A fase "${NOME_FASE[fase]}" não tem mais nenhum confronto totalmente definido.\n\n` +
              'Mas no servidor ainda existe uma publicação ANTIGA dessa fase — os participantes ' +
              'continuam vendo aqueles confrontos no link.\n\n' +
              'Deseja DESPUBLICAR a fase agora? (limpa todos os confrontos do link público)\n\n' +
              'Você pode publicar de novo a qualquer momento, basta clicar em "Publicar no link público".'
          );
          if (!ok) return;
          await enviarPublicacaoFaseMM(fase, [], 'Fase despublicada (link limpo).');
          return;
        }
        alert(
          `Nenhum confronto da fase "${NOME_FASE[fase]}" está com os dois times definidos. ` +
            'Preencha mais resultados de grupos (ou de fases anteriores) antes de publicar.'
        );
        return;
      }

      const parciais = jogos.length - completos;
      const ok = confirm(
        `Publicar ${NOME_FASE[fase]} no link público?\n\n` +
          `Confrontos prontos para palpitar: ${completos}/${jogos.length}.` +
          (parciais > 0
            ? `\n${parciais} confronto${parciais === 1 ? '' : 's'} ainda não ${parciais === 1 ? 'tem' : 'têm'} ` +
              `os dois times definidos — ${parciais === 1 ? 'esse' : 'esses'} ficará${parciais === 1 ? '' : 'ão'} de fora ` +
              `desta publicação. Quando mais grupos terminarem, é só clicar em ` +
              `"Republicar" para liberar os novos.`
            : '') +
          `\n\nOs participantes que abrirem o link de mata-mata vão ver esses ${completos} jogo${completos === 1 ? '' : 's'} ` +
          'e poderão palpitar até o horário oficial de cada um.'
      );
      if (!ok) return;
      await enviarPublicacaoFaseMM(
        fase,
        jogosDefinidos,
        `${completos} jogo${completos === 1 ? '' : 's'} publicado${completos === 1 ? '' : 's'}.`
      );
    },
    [sheetsEndpoint, resolverLadoMataMata, enviarPublicacaoFaseMM, statusPublicacaoFaseMM]
  );

  /** Despublica uma fase do mata-mata (limpa do link público). */
  const despublicarFaseMataMata = useCallback(
    async (fase: FaseMataMata) => {
      if (!sheetsEndpoint || !endpointGasValido(sheetsEndpoint)) {
        alert('Configure a URL do Apps Script primeiro.');
        return;
      }
      const ok = confirm(
        `DESPUBLICAR a fase "${NOME_FASE[fase]}" do link público?\n\n` +
          'Os participantes vão deixar de ver os confrontos dessa fase no link. ' +
          'Os palpites já enviados ficam guardados na planilha (não são apagados).\n\n' +
          'Você pode publicar de novo a qualquer momento.'
      );
      if (!ok) return;
      await enviarPublicacaoFaseMM(fase, [], 'Fase despublicada (link limpo).');
    },
    [sheetsEndpoint, enviarPublicacaoFaseMM]
  );

  const setPlacarReal = (jogoId: string, p: Placar | null) => {
    setResultadosReais((prev) => {
      const next = { ...prev };
      if (p == null) delete next[jogoId];
      else next[jogoId] = p;
      return next;
    });
    setResultadosManuais((prev) => {
      if (p == null) {
        if (!prev[jogoId]) return prev;
        const next = { ...prev };
        delete next[jogoId];
        return next;
      }
      if (prev[jogoId]) return prev;
      return { ...prev, [jogoId]: true };
    });
    if (p == null || !placarEmpatadoMM(p)) {
      setVencedoresPenaltisMM((prev) => {
        if (!prev[jogoId]) return prev;
        const next = { ...prev };
        delete next[jogoId];
        return next;
      });
    }
  };

  const setVencedorPenaltisReal = (jogoId: string, lado: LadoPlacarMM | null) => {
    setVencedoresPenaltisMM((prev) => {
      const next = { ...prev };
      if (lado == null) delete next[jogoId];
      else next[jogoId] = lado;
      return next;
    });
    if (lado != null) {
      setResultadosManuais((prev) =>
        prev[jogoId] ? prev : { ...prev, [jogoId]: true }
      );
    }
  };

  const setPalpiteParticipante = (participanteId: string, jogoId: string, p: Placar | null) => {
    setParticipantes((prev) =>
      prev.map((part) => {
        if (part.id !== participanteId) return part;
        const palpites = { ...part.palpites };
        if (p == null) delete palpites[jogoId];
        else palpites[jogoId] = p;
        return { ...part, palpites };
      })
    );
  };

  /**
   * Correção de placar pelo organizador (ex.: participante digitou 31x1 e o
   * certo era 3x1). Entra em "modo correção": libera a edição dos placares
   * travados e, ao salvar, grava na planilha (ação 'corrigir' do Apps Script).
   * NÃO altera nada da lógica de pontuação/premiação — só troca o valor do
   * jogo, que passa a ser usado no cálculo normalmente.
   */
  const iniciarCorrecaoServidor = (participanteId: string) => {
    const part = participantes.find((p) => p.id === participanteId);
    if (!part) return;
    setSnapshotCorrecao({ ...part.palpites });
    setCorrigindoServidorId(participanteId);
  };

  const cancelarCorrecaoServidor = () => {
    if (corrigindoServidorId && snapshotCorrecao) {
      const id = corrigindoServidorId;
      const snap = snapshotCorrecao;
      setParticipantes((prev) =>
        prev.map((part) => (part.id === id ? { ...part, palpites: { ...snap } } : part))
      );
    }
    setCorrigindoServidorId(null);
    setSnapshotCorrecao(null);
  };

  const salvarCorrecaoNoServidor = async (participanteId: string) => {
    const part = participantes.find((p) => p.id === participanteId);
    if (!part) return;
    if (!sheetsEndpoint) {
      alert('Configure a planilha (Google Sheets) antes de corrigir no servidor.');
      return;
    }
    // Envia só os jogos da FASE DE GRUPOS (ids de JOGOS_COPA_2026), para não
    // misturar com mata-mata na linha de grupos da planilha.
    const idsGrupos = new Set(JOGOS_COPA_2026.map((j) => j.id));
    const correcoes = Object.keys(part.palpites)
      .filter((jid) => idsGrupos.has(jid) && placarValido(part.palpites[jid]))
      .map((jid) => ({
        jogoId: jid,
        mandante: part.palpites[jid].mandante,
        visitante: part.palpites[jid].visitante,
      }));
    if (correcoes.length === 0) {
      alert('Nenhum placar de fase de grupos preenchido para corrigir.');
      return;
    }
    setSalvandoCorrecao(true);
    try {
      const resp = await fetch(sheetsEndpoint, {
        method: 'POST',
        redirect: 'follow',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          acao: 'corrigir',
          id: part.origemId || '',
          nome: part.nome,
          setor: part.setor || '',
          fase: 'grupos',
          palpites: correcoes,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = (await resp.json()) as { ok?: boolean; erro?: string };
      if (!json.ok) throw new Error(json.erro || 'Resposta inválida do servidor.');
      setCorrigindoServidorId(null);
      setSnapshotCorrecao(null);
      alert(
        `Correção salva no servidor para "${part.nome}".\n\n` +
          `O novo placar já entra no cálculo da pontuação e aparece para todos no link público.`
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(`Não foi possível salvar a correção. (${msg})\n\nTente de novo em alguns segundos.`);
    } finally {
      setSalvandoCorrecao(false);
    }
  };

  /** Remove palpite(s) MM na planilha — participante pode enviar de novo pelo link. */
  const liberarPalpitesMMNoServidor = async (
    participanteId: string,
    jogoIds: string[]
  ): Promise<void> => {
    const part = participantes.find((p) => p.id === participanteId);
    if (!part || jogoIds.length === 0) return;
    if (!sheetsEndpoint) {
      alert('Configure a planilha (Google Apps Script v7.3+) antes de liberar palpites.');
      return;
    }
    const payloadBase = {
      nome: part.nome,
      setor: part.setor || '',
      id: part.origemId || '',
    };
    type RespLiberar = {
      ok?: boolean;
      erro?: string;
      removidos?: string[];
      qtd?: number;
      scriptVersao?: string;
      acaoRecebida?: string;
    };
    try {
      let json: RespLiberar = await fetchGasPost<RespLiberar>(sheetsEndpoint, {
        acao: 'liberarmm',
        ...payloadBase,
        jogoIds,
      });
      if (
        !json.ok &&
        (json.erro === 'Ação inválida.' || json.erro === 'Acao invalida.')
      ) {
        json = await fetchGasPost<RespLiberar>(sheetsEndpoint, {
          acao: 'corrigir',
          ...payloadBase,
          fase: 'matamata',
          palpites: jogoIds.map((jogoId) => ({ jogoId, remover: true })),
        });
      }
      if (!json.ok) {
        let versaoScript = json.scriptVersao || '';
        if (!versaoScript) {
          try {
            const st = await fetchGasGet<{ scriptVersao?: string }>(sheetsEndpoint, {
              acao: 'status',
            });
            versaoScript = st.scriptVersao || '(desconhecida — anterior à v7.3)';
          } catch {
            versaoScript = '(não foi possível consultar)';
          }
        }
        throw new Error(
          `${json.erro || 'Resposta inválida do servidor.'}\n` +
            `Script implantado: v${versaoScript}\n` +
            `Necessário: v7.3+ (liberarmm / corrigir com remover).`
        );
      }
      const removidos = json.removidos || jogoIds;
      setParticipantes((prev) =>
        prev.map((p) => {
          if (p.id !== participanteId) return p;
          const palpites = { ...p.palpites };
          removidos.forEach((jid) => delete palpites[jid]);
          return { ...p, palpites };
        })
      );
      alert(
        `Liberado(s) ${removidos.length} jogo(s) para "${part.nome}".\n\n` +
          `Peça para a pessoa abrir o link do mata-mata e clicar em **Atualizar** — ` +
          `o jogo volta sem placar e ela pode palpitar de novo.`
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(
        `Não foi possível liberar.\n\n${msg}\n\n` +
          `Se o script já foi colado: Apps Script → Implantar → Gerenciar implantações → ` +
          `lápis ✏️ → Nova versão → Implantar.\n` +
          `(Só salvar o código NÃO atualiza a URL /exec.)`
      );
      throw e;
    }
  };

  /** Autoriza 1 envio atrasado no link público (GAS v7.5+). */
  const autorizarPalpiteMMAtrasadoNoServidor = async (
    participanteId: string,
    jogoIds: string[]
  ): Promise<void> => {
    const part = participantes.find((p) => p.id === participanteId);
    if (!part || jogoIds.length === 0) return;
    if (!sheetsEndpoint) {
      alert('Configure a planilha (Google Apps Script v7.5+) antes.');
      return;
    }
    try {
      const json = await fetchGasPost<{
        ok?: boolean;
        erro?: string;
        jogos?: string[];
        scriptVersao?: string;
      }>(sheetsEndpoint, {
        acao: 'autorizarmm',
        nome: part.nome,
        setor: part.setor || '',
        jogoIds,
      });
      if (!json.ok) {
        throw new Error(
          `${json.erro || 'Falha ao autorizar.'}\nScript: v${json.scriptVersao || '?'}\nNecessário: v7.5+`
        );
      }
      alert(
        `Autorizado para "${part.nome}": ${jogoIds.join(', ')}.\n\n` +
          `Peça para abrir o link do mata-mata → Atualizar → digitar o placar → Enviar.`
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(
        `Não foi possível autorizar.\n\n${msg}\n\n` +
          `Cole o Apps Script atualizado e faça Nova versão na implantação.`
      );
      throw e;
    }
  };

  /** Grava palpite MM faltante direto na planilha (corrigir). */
  const registrarPalpiteMMFaltanteNoServidor = async (
    participanteId: string,
    jogoId: string,
    placar: { mandante: number; visitante: number }
  ): Promise<void> => {
    const part = participantes.find((p) => p.id === participanteId);
    if (!part) return;
    if (!sheetsEndpoint) {
      alert('Configure a planilha antes de registrar.');
      return;
    }
    try {
      const json = await fetchGasPost<{ ok?: boolean; erro?: string }>(sheetsEndpoint, {
        acao: 'corrigir',
        id: part.origemId || '',
        nome: part.nome,
        setor: part.setor || '',
        fase: 'matamata',
        palpites: [
          {
            jogoId,
            mandante: placar.mandante,
            visitante: placar.visitante,
          },
        ],
      });
      if (!json.ok) throw new Error(json.erro || 'Resposta inválida do servidor.');
      setParticipantes((prev) =>
        prev.map((p) => {
          if (p.id !== participanteId) return p;
          return {
            ...p,
            palpites: {
              ...p.palpites,
              [jogoId]: { mandante: placar.mandante, visitante: placar.visitante },
            },
          };
        })
      );
      alert(
        `Palpite de "${part.nome}" registrado no servidor (${jogoId}: ${placar.mandante}×${placar.visitante}).`
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(`Não foi possível registrar.\n\n${msg}`);
      throw e;
    }
  };

  const autoPreencherParticipante = (participanteId: string, preservar: boolean) => {
    setParticipantes((prev) =>
      prev.map((part) => {
        if (part.id !== participanteId) return part;
        if (part.bloqueado) return part;
        if (!preservar) {
          const temAlgo = Object.keys(part.palpites).some((k) => placarValido(part.palpites[k]));
          if (temAlgo) {
            const ok = confirm(
              `Substituir TODOS os palpites de "${part.nome}" por palpites automáticos?`
            );
            if (!ok) return part;
          }
        }
        return { ...part, palpites: gerarPalpitesAutomaticos(part.palpites, preservar) };
      })
    );
  };

  /**
   * Marca o palpite como excluído na planilha do Google Sheets (Apps Script v5+).
   * Faz com que ele suma de "Ver palpites dos outros" no formulário público.
   * Se o Apps Script for v4 (sem essa ação), falha silenciosamente — o app
   * continua funcionando normalmente, só que o palpite seguirá visível pra
   * quem abrir o link público.
   */
  const marcarExcluidoNaPlanilha = (origemId: string): void => {
    if (!sheetsEndpoint || !origemId) return;
    try {
      const url = `${sheetsEndpoint}${
        sheetsEndpoint.includes('?') ? '&' : '?'
      }acao=excluir&id=${encodeURIComponent(origemId)}`;
      // Fire-and-forget: nao precisa esperar resposta.
      fetch(url, { method: 'GET', redirect: 'follow' }).catch(() => {
        /* ignora — versao antiga do Apps Script ou rede off */
      });
    } catch {
      /* ignore */
    }
  };

  /**
   * Impede que o participante volte ao clicar em "Atualizar palpites" (planilha Google).
   *
   * @param part      Participante sendo excluído.
   * @param permanente Se `true` (padrão), bloqueia o nome+setor para sempre — só volta via
   *                   "Permitir reenvio". Se `false`, apenas marca a data da exclusão: a
   *                   pessoa pode reenviar palpites com o mesmo nome a partir desse momento
   *                   e eles voltam automaticamente na próxima sincronização.
   */
  const registrarParticipanteExcluido = (
    part: ParticipanteCopa,
    permanente: boolean = true
  ): void => {
    const origemIdAtual = part.origemId || '';
    const ignoradoEm = new Date().toISOString();
    const entrada: IdIgnorado = {
      id: origemIdAtual || `manual-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
      ignoradoEm,
      nomeNorm: normalizarNome(part.nome),
      setorNorm: normalizarNome(part.setor || ''),
      nomeOriginal: part.nome,
      setorOriginal: part.setor || '',
      permanente,
    };
    const ignoradosAtuaisDisco = carregarIdsIgnorados();
    const ja = origemIdAtual
      ? ignoradosAtuaisDisco.some((x) => x.id === origemIdAtual)
      : ignoradosAtuaisDisco.some(
          (x) =>
            x.nomeNorm === entrada.nomeNorm &&
            (x.setorNorm || '') === (entrada.setorNorm || '')
        );
    const nextIgnorados = ja
      ? ignoradosAtuaisDisco.map((x) => {
          const igualPorId = origemIdAtual && x.id === origemIdAtual;
          const igualPorChave =
            x.nomeNorm === entrada.nomeNorm &&
            (x.setorNorm || '') === (entrada.setorNorm || '');
          if (igualPorId || igualPorChave) {
            return {
              ...x,
              id: origemIdAtual || x.id,
              ignoradoEm,
              nomeNorm: entrada.nomeNorm,
              setorNorm: entrada.setorNorm,
              nomeOriginal: entrada.nomeOriginal,
              setorOriginal: entrada.setorOriginal,
              permanente,
            };
          }
          return x;
        })
      : [...ignoradosAtuaisDisco, entrada];
    salvarIdsIgnoradosImediato(nextIgnorados);
    setIdsIgnorados(nextIgnorados);
    if (origemIdAtual) {
      setIdsRecebidos((prev) => prev.filter((id) => id !== origemIdAtual));
      marcarExcluidoNaPlanilha(origemIdAtual);
    }
  };

  /**
   * Exclui o participante PERMITINDO que ele reenvie palpites com o MESMO nome.
   * O palpite atual da planilha some, mas se a pessoa preencher de novo depois,
   * ele volta automaticamente na próxima sincronização.
   */
  const removerParticipanteSemBloquear = (id: string) => {
    const part = participantes.find((p) => p.id === id);
    if (!part) return;
    const veioDoSheets = Boolean(part.origemId);
    const aviso = veioDoSheets
      ? `\n\nO palpite atual de "${part.nome}" some da sua lista, mas:\n` +
        `• Se essa pessoa enviar um novo palpite com o mesmo nome, ele entra normalmente.\n` +
        `• O palpite ANTIGO (atual) não volta — só palpites enviados depois deste momento.\n` +
        `• Você pode "Permitir reenvio" na lista vermelha pra forçar a volta do atual.`
      : `\n\nEle some da lista. Se reenviar com o mesmo nome no link público, entra normalmente.`;
    const ok = confirm(
      `Excluir "${part.nome}"${part.setor ? ` (${part.setor})` : ''} e PERMITIR reenvio com o mesmo nome?${aviso}`
    );
    if (!ok) return;
    registrarParticipanteExcluido(part, false);
    setParticipantes((prev) => prev.filter((p) => p.id !== id));
    if (participanteAtivoId === id) setParticipanteAtivoId(null);
  };

  const limparPalpitesParticipante = (participanteId: string) => {
    const part = participantes.find((p) => p.id === participanteId);
    if (!part) return;
    const veioDoSheets = Boolean(part.origemId);

    if (part.bloqueado && !veioDoSheets) {
      alert(`Os palpites de "${part.nome}" estão travados e não podem ser apagados.`);
      return;
    }
    const temAlgo = Object.keys(part.palpites).some((k) => placarValido(part.palpites[k]));
    if (!temAlgo && !veioDoSheets) {
      alert(`"${part.nome}" não tem palpites para apagar.`);
      return;
    }
    const avisoSheets = veioDoSheets
      ? `\n\nObs.: veio do Google Sheets — não volta ao atualizar palpites.\n` +
        `Para aceitar de novo: "Permitir reenvio" na lista de excluídos (aba Participantes).`
      : '';
    const ok = confirm(
      `Apagar TODOS os palpites de "${part.nome}"?\n\nO participante também será removido da lista.${avisoSheets}`
    );
    if (!ok) return;
    registrarParticipanteExcluido(part);
    setParticipantes((prev) => prev.filter((p) => p.id !== participanteId));
    if (participanteAtivoId === participanteId) setParticipanteAtivoId(null);
  };

  /**
   * Monta a tabela atual em formato pronto para envio à planilha.
   * Inclui uma "linha mágica" ao final com `id === '__meta_bolao__'`
   * sinalizando se o bolão chegou ao fim. O Apps Script v6.3 guarda o
   * array como JSON sem alterar a forma — assim conseguimos mandar o
   * sinal sem atualizar o servidor.
   *
   *   pontos = 1  → bolão encerrado (todos os 104 jogos com resultado)
   *   pontos = 0  → bolão ainda em andamento
   *
   * O link público filtra essa linha (id começando com `__`) e usa o
   * sinal para mostrar a mensagem final personalizada.
   */
  const montarTabelaParaPublicar = useCallback(() => {
    // Sem statusEliminacao no link público — ranking sem rótulos ELIMINADO / SÓ 2º.
    const linhas = placarGeral.map((item, idx) => {
      const prem = premiacao[idx];
      return {
        id: item.participante.id,
        nome: item.participante.nome,
        setor: item.participante.setor || '',
        pontos: item.total,
        faltas: item.participante.faltas ?? 0,
        premioFinal: prem ? prem.premioFinal : 0,
        posicao: idx + 1,
      };
    });
    linhas.push({
      id: '__meta_bolao__',
      nome: '',
      setor: '',
      pontos: fimBolao.bolaoEncerrado ? 1 : 0,
      faltas: 0,
      premioFinal: 0,
      posicao: 0,
    });
    return linhas;
  }, [placarGeral, premiacao, fimBolao.bolaoEncerrado]);

  /**
   * Versão SILENCIOSA do publicar — sem alert, sem modal, sem barulho.
   * Usada pela publicação automática para manter o link público sempre
   * atualizado sem incomodar o organizador.
   */
  const publicarTabelaSilencioso = useCallback(async () => {
    if (!sheetsEndpoint) return false;
    if (placarGeral.length === 0) return false;
    const tabela = montarTabelaParaPublicar();
    try {
      const resp = await fetch(sheetsEndpoint, {
        method: 'POST',
        redirect: 'follow',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ acao: 'publicarTabela', tabela }),
      });
      if (!resp.ok) return false;
      const json = (await resp.json()) as {
        ok?: boolean;
        publicadoEm?: string;
        erro?: string;
      };
      if (!json.ok) return false;
      const iso = json.publicadoEm || new Date().toISOString();
      setTabelaPublicadaEm(iso);
      try {
        localStorage.setItem('copa-tabela-publicada-em-ade', iso);
      } catch {
        /* quota */
      }
      return true;
    } catch {
      return false;
    }
  }, [sheetsEndpoint, placarGeral, montarTabelaParaPublicar]);

  /**
   * Publica a tabela atual (pontos + faltas + prêmio) na planilha. Os
   * participantes verão isso em "Ver palpites dos outros" no link público.
   * Mostra alerta de sucesso/erro — para uso manual via botão.
   */
  const publicarTabelaNaPlanilha = useCallback(async () => {
    if (!sheetsEndpoint) {
      abrirModalSheets();
      return;
    }
    if (placarGeral.length === 0) {
      alert('Cadastre participantes e resultados antes de publicar a tabela.');
      return;
    }
    const tabela = montarTabelaParaPublicar();
    setPublicandoTabela(true);
    try {
      const resp = await fetch(sheetsEndpoint, {
        method: 'POST',
        redirect: 'follow',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ acao: 'publicarTabela', tabela }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = (await resp.json()) as { ok?: boolean; publicadoEm?: string; erro?: string };
      if (!json.ok) throw new Error(json.erro || 'Resposta inválida');
      const iso = json.publicadoEm || new Date().toISOString();
      setTabelaPublicadaEm(iso);
      try {
        localStorage.setItem('copa-tabela-publicada-em-ade', iso);
      } catch {
        /* quota */
      }
      alert(
        `Tabela publicada!\n\nAgora quem entrar no link público e clicar em "Ver palpites dos outros" vai ver:\n` +
          `• Pontuação de cada participante\n• Posição na tabela\n\nO valor do prêmio NÃO é exibido publicamente.`
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      const ehAcaoInvalida = /ação inválida/i.test(msg);
      alert(
        ehAcaoInvalida
          ? `Não foi possível publicar a tabela.\n\n` +
              `O Google ainda está com o script ANTIGO (ou v6 com bug).\n\n` +
              `Faça isto:\n` +
              `1. Planilha → Extensões → Apps Script\n` +
              `2. Cole o código NOVO do arquivo INSTRUCOES-GOOGLE-SHEETS.md (v6.1)\n` +
              `3. Salvar (disquete)\n` +
              `4. Implantar → Gerenciar implantações → lápis na URL que está no app\n` +
              `5. Versão = "Nova versão" → Implantar\n\n` +
              `Teste no navegador (cole no Chrome):\n` +
              `${sheetsEndpoint}${sheetsEndpoint.includes('?') ? '&' : '?'}acao=tabela\n\n` +
              `Deve aparecer {"ok":true,...} e NÃO "Ação inválida".`
          : `Não foi possível publicar a tabela.\n\nDetalhe: ${msg}`
      );
    } finally {
      setPublicandoTabela(false);
    }
  }, [sheetsEndpoint, placarGeral, montarTabelaParaPublicar]);

  /**
   * Auto-publicação da tabela: sempre que pontos, prêmios ou faltas mudarem,
   * dispara uma publicação silenciosa em 4 segundos. Se outra mudança vier
   * antes desse prazo, o timer reinicia (debounce). Assim o link público
   * sempre tem a tabela atualizada sem o organizador precisar clicar no
   * botão "Publicar tabela" toda vez.
   */
  const publicarTabelaSilenciosoRef = useRef(publicarTabelaSilencioso);
  useEffect(() => {
    publicarTabelaSilenciosoRef.current = publicarTabelaSilencioso;
  }, [publicarTabelaSilencioso]);
  /** Assinatura simplificada do estado: muda sempre que pontos/faltas/prêmio
   *  realmente mudam para algum participante. */
  const assinaturaTabela = useMemo(() => {
    return placarGeral
      .map((item, idx) => {
        const prem = premiacao[idx];
        return [
          item.participante.id,
          item.total,
          item.participante.faltas ?? 0,
          prem ? prem.premioFinal : 0,
        ].join(':');
      })
      .join('|');
  }, [placarGeral, premiacao]);
  useEffect(() => {
    if (!sheetsEndpoint) return;
    if (placarGeral.length === 0) return;
    const t = setTimeout(() => {
      void publicarTabelaSilenciosoRef.current();
    }, 4000);
    return () => clearTimeout(t);
  }, [assinaturaTabela, sheetsEndpoint, placarGeral.length]);

  /** Snapshot de posição + premiação após cada jogo encerrado (backup local). */
  const assinaturaHistorico = useMemo(() => {
    const idsGrupos = JOGOS_COPA_2026.filter((j) =>
      placarValido(resultadosReais[j.id])
    )
      .map((j) => j.id)
      .join(',');
    const idsMm = JOGOS_MATA_MATA.filter((j) => placarValido(resultadosReais[j.id]))
      .map((j) => {
        const pen = vencedoresPenaltisMM[j.id];
        return pen ? `${j.id}:${pen}` : j.id;
      })
      .join(',');
    return `${participantes.length}|${idsGrupos}|${idsMm}`;
  }, [resultadosReais, vencedoresPenaltisMM, participantes.length]);

  useEffect(() => {
    if (!prontoParaSalvar) return;
    if (participantes.length === 0) return;
    const qtd = sincronizarHistoricoBolao(
      participantes.map((p) => ({
        id: p.id,
        nome: p.nome,
        setor: p.setor,
        faltas: p.faltas,
        palpites: p.palpites,
      })),
      resultadosReais,
      vencedoresPenaltisMM
    );
    if (qtd > 0) setHistoricoBolao(carregarHistoricoBolao());
  }, [assinaturaHistorico, prontoParaSalvar, participantes, resultadosReais, vencedoresPenaltisMM]);

  /** Reativa o palpite na planilha (desmarca excluidoEm). Falha silenciosa em v4. */
  const desexcluirNaPlanilha = (origemId: string): void => {
    if (!sheetsEndpoint || !origemId) return;
    try {
      const url = `${sheetsEndpoint}${
        sheetsEndpoint.includes('?') ? '&' : '?'
      }acao=desexcluir&id=${encodeURIComponent(origemId)}`;
      fetch(url, { method: 'GET', redirect: 'follow' }).catch(() => {
        /* ignore */
      });
    } catch {
      /* ignore */
    }
  };

  const permitirReenvio = (entrada: IdIgnorado) => {
    const nomeDisplay = entrada.nomeOriginal || entrada.nomeNorm || entrada.id;
    const ok = confirm(
      `Permitir que "${nomeDisplay}" volte na lista?\n\n` +
        `Na próxima sincronização ("Atualizar palpites"), se houver esse nome na planilha, o participante será importado de novo.`
    );
    if (!ok) return;
    if (entrada.id && !entrada.id.startsWith('manual-')) {
      desexcluirNaPlanilha(entrada.id);
    }
    const ignoradosAtuaisDisco = carregarIdsIgnorados();
    const chave = entrada.nomeNorm
      ? `${entrada.nomeNorm}|${entrada.setorNorm || ''}`
      : '';
    const filtrado = ignoradosAtuaisDisco.filter((x) => {
      if (x.id === entrada.id) return false;
      if (chave && x.nomeNorm !== undefined) {
        return `${x.nomeNorm}|${x.setorNorm || ''}` !== chave;
      }
      return true;
    });
    salvarIdsIgnoradosImediato(filtrado);
    setIdsIgnorados(filtrado);
    setIdsRecebidos((prev) => prev.filter((id) => id !== entrada.id));
  };

  const limparHistoricoBloqueados = () => {
    const permanentes = idsIgnorados.filter((x) => x.permanente !== false);
    if (permanentes.length === 0) return;
    const ok = confirm(
      `Liberar TODOS os ${permanentes.length} nome(s) bloqueado(s)?\n\n` +
        `Na próxima sincronização, todos que estiverem na planilha voltarão para a lista.`
    );
    if (!ok) return;
    const restantes = idsIgnorados.filter((x) => x.permanente === false);
    salvarIdsIgnoradosImediato(restantes);
    setIdsIgnorados(restantes);
  };

  const destravarParticipante = (participanteId: string) => {
    const part = participantes.find((p) => p.id === participanteId);
    if (!part || !part.bloqueado) return;
    if (palpitesEncerrados()) {
      alert(
        `O prazo para correções já encerrou (${formatarPrazoBrt()}).\n\n` +
          `Não é mais possível destravar palpites — nem você nem os participantes podem alterar.`
      );
      return;
    }
    const ok = confirm(
      `Destravar os palpites de "${part.nome}"?\n\nDepois de destravar, você poderá editar os palpites manualmente. Use só em casos de erro/correção.\n\nO prazo de correção vai até ${formatarPrazoBrt()}.`
    );
    if (!ok) return;
    setParticipantes((prev) =>
      prev.map((p) => (p.id === participanteId ? { ...p, bloqueado: false } : p))
    );
  };

  const adicionarParticipante = () => {
    const nome = novoNome.trim();
    if (!nome) return;
    const novo: ParticipanteCopa = {
      id: criarId(),
      nome,
      palpites: {},
      criadoEm: new Date().toLocaleString('pt-BR'),
    };
    setParticipantes((prev) => [...prev, novo]);
    setParticipanteAtivoId(novo.id);
    setNovoNome('');
    setAba('palpites');
  };

  const limparTodosParticipantesAceitandoReenvios = () => {
    const qtd = participantes.length;
    if (qtd === 0 && idsIgnorados.length === 0) {
      alert('Lista já está vazia.');
      return;
    }
    const ok = confirm(
      `Recomeçar a lista de participantes?\n\n` +
        `• Remove TODOS os ${qtd} participantes da lista.\n` +
        `• Limpa também a lista vermelha de excluídos (nada fica bloqueado).\n` +
        `• Palpites antigos da planilha NÃO voltarão na próxima sincronização — só entrarão palpites novos, enviados a partir de agora.\n` +
        `• Os palpites continuam guardados na sua planilha do Google Sheets (não mexe lá).\n\n` +
        `Use isso quando o pessoal só testou e você quer começar do zero. Os mesmos nomes podem enviar de novo normalmente.`
    );
    if (!ok) return;
    const agora = new Date().toISOString();
    salvarDataCorte(agora);
    setDataCorte(agora);
    setParticipantes([]);
    setParticipanteAtivoId(null);
    setIdsRecebidos([]);
    try {
      localStorage.removeItem(STORAGE_KEY_RECEBIDOS);
    } catch {
      /* ignore */
    }
    salvarIdsIgnoradosImediato([]);
    setIdsIgnorados([]);
    alert(
      `Lista zerada. A partir de agora só entram palpites NOVOS (enviados após este momento).\n\n` +
        `Peça pro pessoal enviar de novo pelo link — qualquer nome é aceito, sem bloqueio.`
    );
  };

  const removerParticipante = (id: string) => {
    const part = participantes.find((p) => p.id === id);
    if (!part) return;
    const veioDoSheets = Boolean(part.origemId);
    const avisoSync = veioDoSheets
      ? `\n\nEste participante veio do formulário/planilha.\n` +
        `Ao excluir, ele NÃO volta quando você clicar em "Atualizar palpites".\n\n` +
        `Para trazer de volta depois: aba Participantes → lista vermelha → "Permitir reenvio".`
      : `\n\nSe no futuro ele existir na planilha com o mesmo nome e setor, também não voltará automaticamente.`;
    const ok = confirm(
      `Excluir o participante "${part.nome}"${part.setor ? ` (${part.setor})` : ''} e todos os palpites?${avisoSync}`
    );
    if (!ok) return;
    registrarParticipanteExcluido(part);
    setParticipantes((prev) => prev.filter((p) => p.id !== id));
    if (participanteAtivoId === id) setParticipanteAtivoId(null);
  };

  const imprimirFolhaPalpites = () => {
    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const gruposHtml = GRUPOS_COPA_2026.map((g) => {
      const jogos = jogosDoGrupoId(g.id);
      return `
        <section class="grupo">
          <h3>Grupo ${g.id}</h3>
          ${jogos
            .map(
              (j) => `
            <div class="jogo">
              <div class="confronto">
                ${htmlTimeImpressao(j.mandanteId, esc, 'mandante')}
                <span class="placar">____ &times; ____</span>
                ${htmlTimeImpressao(j.visitanteId, esc, 'visitante')}
              </div>
            </div>
          `
            )
            .join('')}
        </section>
      `;
    }).join('');

    const qrRodapeHtml = qrPagamento
      ? `<div class="qr-rodape">
          <img class="qr-img" src="${qrPagamento}" alt="QR Code de pagamento" />
          <div class="qr-legenda">
            <strong>Pagamento do palpite</strong><br/>
            Escaneie o QR Code ao lado para pagar.<br/>
            <span class="qr-favorecido">Titular do PIX: Ademilson José dos Santos Junior</span>
          </div>
        </div>`
      : `<div class="qr-rodape qr-rodape-vazio">
          <div class="qr-placeholder">Cole aqui o QR Code de pagamento</div>
          <div class="qr-legenda">
            <strong>Pagamento do palpite</strong><br/>
            <span class="qr-favorecido">Titular do PIX: Ademilson José dos Santos Junior</span>
          </div>
        </div>`;

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Palpites Copa 2026</title>
      <style>
        @page { size: A4 portrait; margin: 8mm; }
        * { box-sizing: border-box; }
        body {
          font-family: "Segoe UI", Arial, sans-serif;
          font-size: 12.5px;
          line-height: 1.3;
          margin: 0;
          padding: 4mm;
          color: #111;
        }
        h1 { font-size: 20px; margin: 0 0 4px; text-align: center; }
        h2 { font-size: 14px; color: #333; margin: 0 0 8px; text-align: center; font-weight: 600; }
        .cabecalho {
          margin-bottom: 8px;
        }
        .linha-preencher {
          border-bottom: 2px solid #000;
          min-width: 220px;
          display: inline-block;
          height: 22px;
          vertical-align: bottom;
        }
        .linha-preencher.curta {
          min-width: 120px;
        }
        .grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px 8px;
        }
        .grupo {
          border: 1.5px solid #009b3a;
          padding: 6px 8px;
          border-radius: 6px;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .grupo h3 {
          margin: 0 0 6px;
          font-size: 13px;
          font-weight: 800;
          background: #009b3a;
          color: #fff;
          padding: 4px 6px;
          border-radius: 4px;
          text-align: center;
        }
        .jogo {
          border-bottom: 1.5px solid #000;
          padding: 5px 0;
          margin: 0;
        }
        .jogo:last-child { border-bottom: none; padding-bottom: 0; }
        .confronto {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          gap: 5px;
          font-size: 10.5px;
        }
        .linha-time {
          flex: 1 1 0;
          min-width: 0;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-weight: 600;
          white-space: nowrap;
        }
        .linha-time.mandante {
          justify-content: flex-end;
          text-align: right;
        }
        .linha-time.visitante {
          justify-content: flex-start;
          text-align: left;
        }
        .flag {
          width: 22px;
          height: 15px;
          object-fit: cover;
          border: 1px solid #333;
          flex-shrink: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .nome-time {
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .placar {
          flex: 0 0 auto;
          font-family: Consolas, monospace;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 1px;
          white-space: nowrap;
          text-align: center;
          padding: 2px 6px;
          border: 1px solid #666;
          background: #f5f5f5;
        }
        .regras {
          margin-top: 10px;
          font-size: 11px;
          border-top: 1px solid #999;
          padding-top: 6px;
        }
        .qr-rodape {
          margin-top: 12px;
          padding: 8px 10px;
          border: 1.5px dashed #009b3a;
          border-radius: 6px;
          background: #fafffb;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .qr-rodape.qr-rodape-vazio {
          min-height: 100px;
        }
        .qr-img {
          width: 110px;
          height: 110px;
          object-fit: contain;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .qr-placeholder {
          width: 110px;
          height: 110px;
          border: 1.5px dashed #aaa;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          font-size: 10px;
          color: #888;
          padding: 4px;
          background: #fff;
        }
        .qr-legenda {
          font-size: 13px;
          line-height: 1.35;
          font-weight: 600;
          text-align: left;
        }
        .qr-favorecido {
          display: inline-block;
          margin-top: 4px;
          font-size: 12px;
          font-weight: 700;
          color: #006d28;
        }
        @media print {
          body { padding: 0; }
        }
      </style></head><body>
      <div class="cabecalho">
        <h1>Copa do Mundo 2026 — Palpites</h1>
        <h2>Nome: <span class="linha-preencher"></span> &nbsp; Setor: <span class="linha-preencher curta"></span></h2>
      </div>
      <div class="grid">${gruposHtml}</div>
      <div class="regras">
        <strong>Pontuação — fase de grupos:</strong> placar exato = 3 pts · vencedor ou empate = 1 pt.<br/>
        <strong>Mata-mata (multiplicador):</strong> 16 avos 6/2 · oitavas 9/3 · quartas 12/4 · semi 15/5 · final 18/6 pts.<br/>
        <strong>Premiação:</strong> 1º = R$ 2.400 · 2º = R$ 600 (sempre por pontuação atual).
        Cada falta no trabalho (1 dia = 9h) entre <strong>11/06 e 19/07</strong> tira 25% do
        prêmio — vale para o prêmio base e também para valores recebidos por repasse.
        Valor descontado é repassado para os próximos colocados (pulando o imediato), em
        partes iguais de no máximo R$ 600 cada (≤ R$ 75 vai inteiro). Faltantes também podem
        receber, mas o que entra pra eles sofre o mesmo desconto.<br/>
        <strong>Prazo de pagamento: até 10/06.</strong> Titular do PIX: Ademilson José dos Santos Junior.
      </div>
      ${qrRodapeHtml}
      <script>
        window.onload = function () {
          var imgs = Array.prototype.slice.call(document.images);
          if (!imgs.length) { setTimeout(function () { window.print(); }, 300); return; }
          var ok = 0;
          var imprimir = function () {
            if (++ok >= imgs.length) setTimeout(function () { window.print(); }, 400);
          };
          imgs.forEach(function (img) {
            if (img.complete) imprimir();
            else { img.onload = imprimir; img.onerror = imprimir; }
          });
        };
      </script>
    </body></html>`;

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
  };

  /** Imprime as REGRAS DA PREMIAÇÃO em uma página para colar em mural. */
  const imprimirRegrasPremiacao = (tamanhoBasePx?: number) => {
    const base = (() => {
      const n = Number(tamanhoBasePx);
      if (!Number.isFinite(n) || n < 6 || n > 60) return 14;
      return n;
    })();
    const agora = new Date();
    const dataImpressao = `${agora.toLocaleDateString('pt-BR')} às ${agora.toLocaleTimeString(
      'pt-BR',
      { hour: '2-digit', minute: '2-digit' }
    )}`;
    const w = window.open('', '_blank');
    if (!w) {
      alert('Não foi possível abrir a janela de impressão. Libere pop-ups deste site.');
      return;
    }
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Regras da Premiação — Bolão Copa 2026</title>
      <style>
        @page { size: A4 portrait; margin: 10mm; }
        * { box-sizing: border-box; }
        body {
          font-family: "Segoe UI", Arial, sans-serif;
          font-size: ${base}px;
          line-height: 1.4;
          margin: 0;
          padding: 0;
          color: #111;
        }
        p { margin: 0.33em 0; }
        header {
          text-align: center;
          border-bottom: 3px solid #009b3a;
          padding-bottom: 0.5em;
          margin-bottom: 0.8em;
        }
        header h1 { margin: 0; font-size: 1.65em; color: #006d28; }
        h2 {
          color: #fff;
          background: #009b3a;
          padding: 0.25em 0.6em;
          border-radius: 3px;
          font-size: 1.1em;
          margin: 0.8em 0 0.4em;
        }
        ul { margin: 0.25em 0 0.4em 1.2em; padding: 0; }
        li { margin-bottom: 0.15em; }
        .destaque {
          background: #f0fff4;
          border: 1px dashed #009b3a;
          border-radius: 4px;
          padding: 0.4em 0.65em;
          margin: 0.35em 0;
        }
        .premios {
          display: flex;
          gap: 0.65em;
          margin: 0.35em 0;
        }
        .premios .box {
          flex: 1;
          text-align: center;
          padding: 0.4em;
          border: 2px solid #009b3a;
          border-radius: 4px;
          background: #fff8d6;
        }
        .premios .box strong { display: block; font-size: 0.85em; color: #555; }
        .premios .box span { font-size: 1.35em; font-weight: 800; color: #006d28; }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.92em;
          margin: 0.35em 0;
        }
        th, td {
          border: 1px solid #999;
          padding: 0.15em 0.4em;
          text-align: center;
        }
        th { background: #009b3a; color: #fff; }
        tr:nth-child(even) td { background: #f5fbf6; }
        footer {
          margin-top: 0.8em;
          font-size: 1em;
          color: #333;
          text-align: center;
          border-top: 1px solid #ccc;
          padding-top: 0.45em;
          font-weight: 500;
        }
        .aviso-falta {
          background: #fff3cd;
          border-left: 4px solid #f0ad4e;
          padding: 0.4em 0.65em;
          margin: 0.35em 0;
          border-radius: 4px;
          font-size: 0.96em;
        }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      </style>
    </head><body>
      <header>
        <h1>Bolão Copa do Mundo 2026 — Regras da Premiação</h1>
      </header>

      <h2>Prêmios base</h2>
      <div class="premios">
        <div class="box">
          <strong>1º lugar</strong>
          <span>${formatarReais(2400)}</span>
        </div>
        <div class="box">
          <strong>2º lugar</strong>
          <span>${formatarReais(600)}</span>
        </div>
      </div>

      <h2>Ordem da tabela</h2>
      <p>Sempre por pontuação atual. Se alguém ultrapassar outro nos pontos, a tabela é
      recalculada na hora. O prêmio base de ${formatarReais(600)} segue com quem estiver em 2º
      naquela hora.</p>

      <h2>Punição por faltas no trabalho</h2>
      <div class="aviso-falta">
        Período considerado: <strong>11/06 a 19/07</strong>. Cada falta (1 dia = 9h) tira
        <strong>25%</strong> do prêmio. O desconto vale tanto sobre o prêmio base quanto
        sobre qualquer valor recebido por repasse.
        <br /><br />
        <strong>As 9h podem ser acumuladas.</strong> Exemplo: 1h num dia + 2h em outro + 6h
        em outro = 9h, que equivale a <strong>1 falta cheia</strong>. Vale qualquer
        combinação de horas perdidas no período — assim que somar 9h, conta como 1 falta
        e tira 25%. A próxima soma de 9h vira outra falta, e assim por diante.
      </div>

      <h2>Repasse — como o valor perdido é redistribuído</h2>
      <ul>
        <li>O valor perdido <strong>pula quem já tem prêmio</strong> (1º e 2º, ou empatados que dividiram esse prêmio base) e abre prêmio só para os colocados que ainda não ganharam nada.</li>
        <li>Se sobrar valor depois de descer toda a tabela, ele <strong>volta para o topo</strong> via sobra — aí sim o 2º (ou os já premiados) podem ganhar mais.</li>
        <li>Se o valor for de <strong>${formatarReais(75)} ou menos</strong>, vai inteiro
        para 1 só colocado.</li>
        <li>Se for maior, divide em partes iguais de no máximo
        <strong>${formatarReais(600)} cada</strong> (o valor do 2º) — abrindo quantos novos
        premiados forem necessários.</li>
      </ul>
      <table>
        <thead>
          <tr>
            <th>Faltas do 1º</th>
            <th>Nº de partes</th>
            <th>Valor de cada parte</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>1 falta</td><td>2 partes</td><td>${formatarReais(300)} cada</td></tr>
          <tr><td>2 faltas</td><td>2 partes</td><td>${formatarReais(600)} cada</td></tr>
          <tr><td>3 faltas</td><td>3 partes</td><td>${formatarReais(600)} cada</td></tr>
          <tr><td>4+ faltas</td><td>4 partes</td><td>${formatarReais(600)} cada</td></tr>
        </tbody>
      </table>

      <h2>Quem faltou também pode receber repasse?</h2>
      <div class="destaque">
        <strong>Quem faltou e ainda não recebeu também pode receber repasse</strong> — só que,
        sobre o que entrar pra ele, incide a mesma % de desconto, e essa parte volta a ser
        redistribuída. Quem faltou e já recebeu repasse uma vez não pode receber de novo:
        só entra na fila quem ainda não recebeu nada.
      </div>

      <h2>Outras regras</h2>
      <ul>
        <li><strong>Sobras</strong> (quando não tem mais quem premiar abaixo) voltam pro topo
        da tabela: vão pro não-faltante mais bem colocado.</li>
        <li><strong>Consistência entre quem não faltou:</strong> quem está em colocação
        melhor sempre recebe prêmio igual ou maior que quem está embaixo.</li>
      </ul>

      <footer>
        Documento gerado em ${dataImpressao} · Bolão Copa do Mundo 2026
      </footer>
      <script>window.onload = function(){ window.print(); };</script>
    </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
  };

  /** Imprime uma página por participante com os palpites JÁ PREENCHIDOS (xerox) */
  const imprimirPalpitesPreenchidosTodos = () => {
    if (participantes.length === 0) {
      alert('Nenhum participante para imprimir.');
      return;
    }
    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const fmtPlacar = (p?: Placar): string => {
      if (!p || !placarValido(p)) return '— x —';
      return `${p.mandante} x ${p.visitante}`;
    };

    const ordemAlfabetica = [...participantes].sort((a, b) =>
      a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' })
    );

    const agora = new Date();
    const dataImpressao = `${agora.toLocaleDateString('pt-BR')} às ${agora.toLocaleTimeString(
      'pt-BR',
      { hour: '2-digit', minute: '2-digit' }
    )}`;

    const paginasHtml = ordemAlfabetica
      .map((part, idx) => {
        const gruposHtml = GRUPOS_COPA_2026.map((g) => {
          const jogos = jogosDoGrupoId(g.id);
          return `
            <section class="grupo">
              <h3>Grupo ${g.id}</h3>
              ${jogos
                .map(
                  (j) => `
                <div class="jogo">
                  <div class="confronto">
                    ${htmlTimeImpressao(j.mandanteId, esc, 'mandante')}
                    <span class="placar">${esc(fmtPlacar(part.palpites[j.id]))}</span>
                    ${htmlTimeImpressao(j.visitanteId, esc, 'visitante')}
                  </div>
                </div>
              `
                )
                .join('')}
            </section>
          `;
        }).join('');

        const qtd = contarPalpitesGruposValidos(part.palpites);
        const dataEnvio = part.criadoEm || '';

        return `
          <article class="pagina-participante ${idx === 0 ? 'primeira' : ''}">
            <div class="cabecalho">
              <h1>Bolão Copa do Mundo 2026 — Palpites Oficiais</h1>
              <h2>
                <strong>Nome:</strong> ${esc(part.nome)}
                ${part.setor ? ` &nbsp;|&nbsp; <strong>Setor:</strong> ${esc(part.setor)}` : ''}
                ${dataEnvio ? ` &nbsp;|&nbsp; <strong>Enviado em:</strong> ${esc(dataEnvio)}` : ''}
                &nbsp;|&nbsp; ${qtd}/${JOGOS_COPA_2026.length} jogos
              </h2>
              <p class="aviso-xerox">
                Documento oficial — palpites travados e impressos para conferência (sem alterações posteriores).
              </p>
            </div>
            <div class="grid">${gruposHtml}</div>
            <div class="rodape-assinatura">
              <div class="linha-ass">
                <span>Assinatura do participante</span>
              </div>
              <div class="linha-ass">
                <span>Assinatura do organizador</span>
              </div>
            </div>
            <p class="data-impressao">Impresso em ${esc(dataImpressao)}</p>
          </article>
        `;
      })
      .join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Palpites Bolão Copa 2026 - todos preenchidos</title>
      <style>
        @page { size: A4 portrait; margin: 8mm; }
        * { box-sizing: border-box; }
        body {
          font-family: "Segoe UI", Arial, sans-serif;
          font-size: 12.5px;
          line-height: 1.3;
          margin: 0;
          padding: 0;
          color: #111;
        }
        .pagina-participante {
          padding: 4mm;
          page-break-after: always;
          break-after: page;
        }
        .pagina-participante:last-child {
          page-break-after: auto;
          break-after: auto;
        }
        h1 { font-size: 17px; margin: 0 0 4px; text-align: center; }
        h2 { font-size: 12px; color: #333; margin: 0 0 4px; text-align: center; font-weight: 500; }
        .aviso-xerox {
          text-align: center;
          font-size: 10px;
          color: #006d28;
          margin: 0 0 8px;
          padding: 3px 6px;
          background: #f0fff4;
          border: 1px dashed #009b3a;
          border-radius: 4px;
        }
        .grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5px 7px;
        }
        .grupo {
          border: 1.2px solid #009b3a;
          padding: 5px 7px;
          border-radius: 5px;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .grupo h3 {
          margin: 0 0 5px;
          font-size: 12px;
          font-weight: 800;
          background: #009b3a;
          color: #fff;
          padding: 3px 5px;
          border-radius: 3px;
          text-align: center;
        }
        .jogo {
          border-bottom: 1px solid #555;
          padding: 4px 0;
          margin: 0;
        }
        .jogo:last-child { border-bottom: none; padding-bottom: 0; }
        .confronto {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          gap: 5px;
          font-size: 10px;
        }
        .linha-time {
          flex: 1 1 0;
          min-width: 0;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-weight: 600;
          white-space: nowrap;
        }
        .linha-time.mandante {
          justify-content: flex-end;
          text-align: right;
        }
        .linha-time.visitante {
          justify-content: flex-start;
          text-align: left;
        }
        .flag {
          width: 20px;
          height: 14px;
          object-fit: cover;
          border: 1px solid #333;
          flex-shrink: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .nome-time {
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .placar {
          flex: 0 0 auto;
          font-family: Consolas, monospace;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 1px;
          white-space: nowrap;
          text-align: center;
          padding: 2px 7px;
          border: 1.5px solid #009b3a;
          background: #fefef0;
          color: #000;
          border-radius: 3px;
        }
        .rodape-assinatura {
          margin-top: 14px;
          display: flex;
          gap: 30px;
          justify-content: space-around;
          padding: 0 20px;
        }
        .data-impressao {
          margin-top: 10px;
          text-align: center;
          font-size: 9px;
          color: #666;
          font-style: italic;
        }
        .linha-ass {
          flex: 1;
          border-top: 1.5px solid #000;
          padding-top: 4px;
          text-align: center;
          font-size: 10px;
          color: #444;
          font-weight: 600;
        }
        @media print {
          body { padding: 0; }
        }
      </style></head><body>
      ${paginasHtml}
      <script>
        window.onload = function () {
          var imgs = Array.prototype.slice.call(document.images);
          if (!imgs.length) { setTimeout(function () { window.print(); }, 400); return; }
          var ok = 0;
          var total = imgs.length;
          var imprimir = function () {
            if (++ok >= total) setTimeout(function () { window.print(); }, 500);
          };
          imgs.forEach(function (img) {
            if (img.complete) imprimir();
            else { img.onload = imprimir; img.onerror = imprimir; }
          });
        };
      </script>
    </body></html>`;

    const w = window.open('', '_blank');
    if (!w) {
      alert('Não foi possível abrir a janela de impressão. Verifique se o navegador bloqueou pop-ups.');
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
  };

  const imprimirTabelaMataMata = () => {
    const esc = (s: string) =>
      String(s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[c] as string);

    const linhaJogoHTML = (jogo: JogoMataMata) => {
      const a = resolverLadoMataMata(jogo.id, 'A');
      const b = resolverLadoMataMata(jogo.id, 'B');
      const aHTML = a.timeId
        ? htmlTimeImpressao(a.timeId, esc, 'mandante')
        : `<span class="linha-time mandante slot">${esc(a.rotulo)}</span>`;
      const bHTML = b.timeId
        ? htmlTimeImpressao(b.timeId, esc, 'visitante')
        : `<span class="linha-time visitante slot">${esc(b.rotulo)}</span>`;
      const quando = formatarDataHoraIsoBrt(jogo.dataHoraBrt, { comAno: true });
      return `
        <div class="jogo-mm">
          <div class="ordem">${esc(quando)}</div>
          <div class="lado">${aHTML}</div>
          <div class="placar">_____ x _____</div>
          <div class="lado direita">${bHTML}</div>
        </div>`;
    };

    const blocosFase = FASES_MATA_MATA.map((fase) => {
      const jogos = jogosDaFase(fase);
      const pts = PONTUACAO_POR_FASE[fase];
      return `
        <div class="fase">
          <div class="fase-titulo">${esc(NOME_FASE[fase])} <span class="fase-pontos">(${pts.placarExato}/${pts.acertoVencedor})</span></div>
          <div class="jogos">
            ${jogos.map(linhaJogoHTML).join('')}
          </div>
        </div>`;
    }).join('');

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Mata-mata Copa 2026</title>
<style>
  @page { size: A4 portrait; margin: 10mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: Arial, sans-serif; font-size: 11px; color: #000; margin: 0; }
  h1 { font-size: 16px; margin: 0 0 4px; text-align: center; color: #009b3a; }
  .sub { text-align: center; font-size: 10px; color: #444; margin-bottom: 6px; }
  .nome-pessoa { border: 1px solid #000; padding: 4px 8px; margin: 6px 0 8px; font-size: 11px; }
  .regras { background: #f7faf3; border: 1px solid #009b3a55; padding: 5px 8px; font-size: 9.5px; margin-bottom: 8px; border-radius: 4px; line-height: 1.4; }
  .fase { break-inside: avoid; margin-bottom: 6px; }
  .fase-titulo { background: #009b3a; color: #fff; padding: 3px 6px; font-weight: bold; font-size: 11px; border-radius: 3px 3px 0 0; }
  .fase-pontos { font-weight: normal; font-size: 10px; opacity: 0.9; }
  .jogos { border: 1px solid #009b3a55; border-top: none; border-radius: 0 0 3px 3px; padding: 3px; }
  .jogo-mm { display: grid; grid-template-columns: 72px 1fr 80px 1fr; align-items: center; gap: 4px; padding: 2px 4px; border-bottom: 1px dashed #ccc; }
  .jogo-mm:last-child { border-bottom: none; }
  .ordem { font-weight: bold; text-align: left; color: #666; font-size: 9px; line-height: 1.2; }
  .lado { display: flex; align-items: center; gap: 4px; min-width: 0; }
  .lado.direita { justify-content: flex-start; }
  .linha-time { display: inline-flex; align-items: center; gap: 4px; min-width: 0; }
  .linha-time.slot { color: #888; font-style: italic; }
  .flag { width: 18px; height: 12px; object-fit: cover; border: 1px solid #ddd; border-radius: 2px; flex-shrink: 0; }
  .nome-time { font-weight: 600; }
  .placar { text-align: center; font-family: 'Courier New', monospace; font-weight: bold; font-size: 12px; letter-spacing: 0.5px; }
  .rodape { margin-top: 6px; font-size: 9px; color: #555; text-align: center; }
  .col2 { columns: 2; column-gap: 8px; }
</style></head>
<body>
  <h1>Mata-mata Copa do Mundo 2026 — Palpites</h1>
  <div class="sub">${esc(organizador.nome ? `Organizador: ${organizador.nome}` : 'Bolão')} · Devolver para ${esc(organizador.nome || 'o organizador')}</div>
  <div class="nome-pessoa"><strong>Nome:</strong> _______________________________ &nbsp; <strong>Setor:</strong> _______________</div>
  <div class="regras">
    <strong>Pontuação por fase (placar exato / acerto do vencedor):</strong>
    16 avos <strong>6/2</strong> · oitavas <strong>9/3</strong> · quartas <strong>12/4</strong> · semi <strong>15/5</strong> · 3º lugar e final <strong>18/6</strong>.<br>
    <strong>Como preencher:</strong> nos 16 avos os times já estão definidos. Nas fases seguintes, escreva o vencedor que avança e o placar que palpita para cada jogo.
  </div>
  <div class="col2">
    ${blocosFase}
  </div>
  <div class="rodape">Devolver até a data combinada — Bolão Copa 2026 · ${new Date().toLocaleDateString('pt-BR')}</div>
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) {
      alert('Permita pop-ups para imprimir.');
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
  };

  const imprimirPlacarGeral = () => {
    if (placarGeral.length === 0) {
      alert('Cadastre participantes para imprimir o placar.');
      return;
    }
    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const dataHoje = new Date().toLocaleDateString('pt-BR');

    const cardsHtml = placarGeral
      .map((item, idx) => {
        const lider = idx === 0 && item.total > 0;
        const pago = item.participante.pagou;
        return `
          <div class="card${lider ? ' lider' : ''}${pago ? ' pago' : ''}">
            <div class="card-topo">
              <span class="posicao">${idx + 1}º</span>
              <span class="nome">${esc(item.participante.nome)}${
                item.participante.setor
                  ? ` <span class="setor">(${esc(item.participante.setor)})</span>`
                  : ''
              }</span>
              ${pago ? '<span class="badge-pago">PAGO</span>' : ''}
              ${lider ? '<span class="badge-lider">LÍDER</span>' : ''}
            </div>
            <div class="pontos-total">${item.total} <span class="pts">pts</span></div>
            <ul class="detalhes">
              <li>${item.placarExato} placares exatos (3 pts cada)</li>
              <li>${item.resultado1x2} acertos de vencedor/empate (1 pt cada)</li>
            </ul>
          </div>`;
      })
      .join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Placar do Bolão — Copa 2026</title>
      <style>
        @page { size: A4 portrait; margin: 8mm; }
        * { box-sizing: border-box; }
        body {
          font-family: "Segoe UI", Arial, sans-serif;
          font-size: 12px;
          line-height: 1.3;
          margin: 0;
          padding: 4mm;
          color: #111;
        }
        h1 { font-size: 20px; margin: 0 0 4px; text-align: center; }
        h2 {
          font-size: 12px;
          color: #444;
          margin: 0 0 10px;
          text-align: center;
          font-weight: 600;
        }
        .grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px 10px;
        }
        .card {
          border: 1.5px solid #009b3a;
          border-radius: 6px;
          padding: 8px 10px;
          background: #fff;
          break-inside: avoid;
          page-break-inside: avoid;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .card.lider {
          border-color: #d4a300;
          background: #fffdf3;
        }
        .card.pago {
          background: #f3fbf4;
        }
        .card.lider.pago {
          background: linear-gradient(135deg, #fffdf3 0%, #f3fbf4 100%);
        }
        .card-topo {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          margin-bottom: 4px;
        }
        .posicao {
          background: #009b3a;
          color: #fff;
          font-weight: 800;
          font-size: 12px;
          border-radius: 4px;
          padding: 2px 6px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .card.lider .posicao {
          background: #d4a300;
        }
        .nome {
          font-weight: 700;
          font-size: 14px;
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .nome .setor {
          font-weight: 500;
          font-size: 11px;
          color: #009b3a;
        }
        .badge-pago {
          background: #c9f3d5;
          color: #006d28;
          border: 1px solid #6ab07f;
          font-weight: 800;
          font-size: 9px;
          letter-spacing: 0.5px;
          padding: 1px 5px;
          border-radius: 3px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .badge-lider {
          background: #fce8a4;
          color: #7a5300;
          border: 1px solid #d4a300;
          font-weight: 800;
          font-size: 9px;
          letter-spacing: 0.5px;
          padding: 1px 5px;
          border-radius: 3px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .pontos-total {
          font-size: 26px;
          font-weight: 900;
          color: #009b3a;
          line-height: 1;
          margin: 4px 0 4px;
        }
        .card.lider .pontos-total {
          color: #b48700;
        }
        .pts { font-size: 13px; font-weight: 700; }
        .detalhes {
          margin: 0;
          padding-left: 16px;
          font-size: 10.5px;
          color: #333;
        }
        .detalhes li { margin: 1px 0; }
        .resumo {
          margin-top: 10px;
          font-size: 11px;
          color: #444;
          text-align: center;
          border-top: 1px solid #ccc;
          padding-top: 4px;
        }
        @media print {
          body { padding: 0; }
        }
      </style></head><body>
      <h1>Placar do Bolão — Copa do Mundo 2026</h1>
      <h2>Impresso em ${dataHoje} · ${placarGeral.length} participante${placarGeral.length === 1 ? '' : 's'}</h2>
      <div class="grid">${cardsHtml}</div>
      <div class="resumo">
        Pontuação: placar exato = 3 pts · vencedor/empate = 1 pt.
        Mata-mata cresce: 16 avos 6/2 · oitavas 9/3 · quartas 12/4 · semi 15/5 · final 18/6 pts.
      </div>
      <script>
        window.onload = function () { setTimeout(function () { window.print(); }, 300); };
      </script>
    </body></html>`;

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
  };

  const abas: { id: AbaCopa; label: string; icon: typeof Trophy }[] = [
    { id: 'oficial', label: 'Resultados reais', icon: Flag },
    { id: 'participantes', label: 'Participantes', icon: Users },
    { id: 'palpites', label: 'Palpites / simulação', icon: ClipboardList },
    { id: 'matamata', label: 'Mata-mata', icon: Trophy },
    { id: 'placar', label: 'Placar do bolão', icon: Medal },
    { id: 'premiacao', label: 'Premiação', icon: Medal },
    { id: 'historico', label: 'Histórico', icon: History },
    { id: 'comparar', label: 'Comparar palpites', icon: ArrowLeftRight },
    { id: 'estatisticas', label: 'Estatísticas', icon: BarChart3 },
  ];

  return (
    <div className="space-y-4">
      <div className="ade-neu-card p-4 border border-[#009b3a]/20">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-black flex items-center gap-2 text-lg">
              <Trophy className="w-5 h-5 text-[#009b3a]" />
              Resultados da Copa
              <span className="text-[10px] font-mono font-bold text-neutral-400 align-middle">
                v{(typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '?')}
              </span>
            </h3>
            <p className="text-xs text-neutral-600 mt-1 max-w-2xl">
              Cadastre os <strong>resultados reais</strong>, depois os palpites de cada amigo. A classificação
              de cada grupo atualiza conforme os placares. <strong>Grupos:</strong> placar exato{' '}
              <strong>3</strong> · vencedor/empate <strong>1</strong>.{' '}
              <strong>Mata-mata</strong> cresce a cada fase (16 avos 6/2 · oitavas 9/3 ·
              quartas 12/4 · semi 15/5 · final 18/6).
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 border border-[#009b3a]/25 rounded-lg px-2 py-1 bg-white">
              {qrPagamento ? (
                <>
                  <img
                    src={qrPagamento}
                    alt="QR de pagamento"
                    className="w-10 h-10 object-contain rounded border border-neutral-300"
                  />
                  <span className="text-[11px] font-semibold text-neutral-700">
                    QR pagamento
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('Remover o QR Code de pagamento salvo?')) setQrPagamento('');
                    }}
                    className="text-[10px] font-bold text-red-600 hover:underline"
                    title="Remover QR Code"
                  >
                    remover
                  </button>
                </>
              ) : (
                <label className="text-[11px] font-semibold text-neutral-700 cursor-pointer inline-flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5" />
                  Adicionar QR pagamento
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      onSelecionarQrPagamento(e.target.files?.[0] ?? null);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={imprimirFolhaPalpites}
                className="px-3 py-2 ade-btn-gold relative z-10 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5"
              >
                <Printer className="w-4 h-4" />
                Imprimir folha em branco
              </button>
              <button
                type="button"
                onClick={imprimirPalpitesPreenchidosTodos}
                disabled={participantes.length === 0}
                className={`px-3 py-2 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 border-2 ${
                  participantes.length === 0
                    ? 'border-neutral-300 bg-neutral-100 text-neutral-400 cursor-not-allowed'
                    : 'border-[#009b3a] bg-white text-[#009b3a] hover:bg-[#009b3a]/10'
                }`}
                title="Imprime 1 folha por participante com os palpites preenchidos (estilo xerox oficial)"
              >
                <Printer className="w-4 h-4" />
                Imprimir palpites preenchidos ({participantes.length})
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {abas.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setAba(t.id)}
              className={`px-3 py-2 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition ${
                aba === t.id
                  ? 'bg-[#009b3a] text-white shadow'
                  : 'bg-[#e4e6ec] text-black hover:bg-[#dde0e6]'
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {aba === 'oficial' && (
        <div className="space-y-3">
          <p className="text-sm text-neutral-700">
            Informe aqui o que realmente aconteceu nos jogos. É a base para calcular os pontos de todos.
          </p>

          {/* Painel de simulação (apagar / preencher automático) — só pra teste */}
          <div className="ade-neu-card p-3 border-2 border-dashed border-amber-400 bg-amber-50/60">
            <div className="flex items-start gap-2 mb-2">
              <span className="text-base">🧪</span>
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-800">Modo simulação (apenas para teste)</p>
                <p className="text-[11px] text-amber-700">
                  Use estes botões para simular o final da Copa rapidamente. Em uso real, lance os
                  resultados manualmente conforme cada jogo termina.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  const temAlgo = JOGOS_COPA_2026.some((j) => placarValido(resultadosReais[j.id]));
                  if (temAlgo) {
                    const ok = confirm(
                      'Isto vai SOBRESCREVER todos os resultados reais já lançados na fase de grupos com placares aleatórios. Continuar?'
                    );
                    if (!ok) return;
                  }
                  const novos = gerarPalpitesAutomaticos({}, false);
                  // gera apenas para os 72 jogos de grupos; mantém mata-mata intacto
                  setResultadosReais((prev) => {
                    const next = { ...prev };
                    JOGOS_COPA_2026.forEach((j) => {
                      if (novos[j.id]) next[j.id] = novos[j.id];
                    });
                    return next;
                  });
                }}
                className="px-3 py-1.5 rounded-md text-xs font-bold bg-amber-600 text-white hover:bg-amber-700 inline-flex items-center gap-1.5"
                title="Preenche os 72 jogos da fase de grupos com placares aleatórios plausíveis"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Preencher 72 jogos automaticamente
              </button>
              <button
                type="button"
                onClick={() => {
                  // Preenche TODOS os jogos do mata-mata também usando os times resolvidos
                  // (útil para simular a Copa inteira de uma vez)
                  // Esse botão só aparece se a fase de grupos já está completa
                  const gruposOk = JOGOS_COPA_2026.every((j) => placarValido(resultadosReais[j.id]));
                  if (!gruposOk) {
                    alert(
                      'Preencha primeiro a fase de grupos (clique no botão de cima). ' +
                        'Os jogos do mata-mata só podem ser simulados depois que os grupos estão definidos.'
                    );
                    return;
                  }
                  const ok = confirm(
                    'Simular TODOS os 32 jogos do mata-mata com placares aleatórios? ' +
                      'Isso vai sobrescrever resultados de mata-mata já lançados. Continuar?'
                  );
                  if (!ok) return;
                  setResultadosReais((prev) => {
                    const next = { ...prev };
                    // Itera fase a fase para que cada jogo tenha os times resolvidos
                    // pelos resultados já gerados nas fases anteriores
                    FASES_MATA_MATA.forEach((fase) => {
                      const jogosFase = JOGOS_MATA_MATA.filter((j) => j.fase === fase);
                      jogosFase.forEach((j) => {
                        // Aplica antes de seguir pra próxima fase, então usa next
                        // (resolverLadoMataMata usa resultadosReais, mas como já está tudo
                        // acumulado em next, vamos resolver inline)
                        // Para simplificar: gera placar aleatório plausível 0-4 sem empates
                        const m = Math.floor(Math.random() * 4); // 0-3
                        let v = Math.floor(Math.random() * 4);
                        if (m === v) {
                          // Sem empate no mata-mata
                          if (Math.random() < 0.5) v = m + 1;
                          else v = Math.max(0, m - 1);
                          if (m === v) v = m + 1; // último ajuste
                        }
                        next[j.id] = { mandante: m, visitante: v };
                      });
                    });
                    return next;
                  });
                }}
                className="px-3 py-1.5 rounded-md text-xs font-bold bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-1.5"
                title="Preenche os 32 jogos do mata-mata com placares aleatórios (precisa ter os 72 grupos preenchidos primeiro)"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Simular mata-mata inteiro
              </button>
              <button
                type="button"
                onClick={() => {
                  const total = Object.keys(resultadosReais).filter((k) =>
                    placarValido(resultadosReais[k])
                  ).length;
                  if (total === 0) {
                    alert('Não há resultados para apagar.');
                    return;
                  }
                  const ok = confirm(
                    `Apagar TODOS os ${total} resultados reais lançados (grupos + mata-mata)?\n\n` +
                      'Esta ação é só para teste — em uso real seus participantes vão perder a pontuação até você relançar.'
                  );
                  if (!ok) return;
                  setResultadosReais({});
                  setVencedoresPenaltisMM({});
                }}
                className="px-3 py-1.5 rounded-md text-xs font-bold bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-1.5"
                title="Limpa TODOS os resultados reais (grupos e mata-mata)"
              >
                <Eraser className="w-3.5 h-3.5" />
                Apagar todos os resultados
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
            {GRUPOS_COPA_2026.map((g) => (
              <CardGrupo
                key={g.id}
                grupoId={g.id}
                placares={resultadosReais}
                onPlacarChange={setPlacarReal}
              />
            ))}
          </div>
        </div>
      )}

      {aba === 'participantes' && (
        <div className="space-y-4">
          <div className="ade-neu-card p-4 flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-semibold mb-1">Novo participante</label>
              <input
                type="text"
                value={novoNome}
                onChange={(e) => setNovoNome(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && adicionarParticipante()}
                placeholder="Nome do amigo"
                className="w-full px-3 py-2 rounded-lg border border-[#009b3a]/25"
              />
            </div>
            <button
              type="button"
              onClick={adicionarParticipante}
              className="px-4 py-2 ade-btn-gold relative z-10 rounded-lg text-sm font-semibold inline-flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Adicionar
            </button>
            {participantes.length > 0 && (
              <button
                type="button"
                onClick={limparTodosParticipantesAceitandoReenvios}
                className="px-3 py-2 rounded-lg text-xs font-semibold inline-flex items-center gap-1 border-2 border-orange-500 bg-orange-50 text-orange-800 hover:bg-orange-100"
                title="Apaga todos os participantes da lista sem bloquear ninguém. Útil quando o pessoal só testou o link."
              >
                <Trash2 className="w-3.5 h-3.5" />
                Limpar todos (sem bloquear)
              </button>
            )}
          </div>
          {dataCorte && (
            <div className="ade-neu-card p-3 border-2 border-orange-300 bg-orange-50">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-orange-800">
                  <strong>Importando só palpites novos.</strong> O app está ignorando palpites
                  enviados antes de{' '}
                  <strong>
                    {new Date(dataCorte).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </strong>{' '}
                  (você usou "Limpar todos"). Palpites enviados <em>depois</em> dessa hora entram
                  normalmente.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    const ok = confirm(
                      'Voltar a importar TODOS os palpites da planilha (incluindo os antigos)?'
                    );
                    if (!ok) return;
                    salvarDataCorte('');
                    setDataCorte('');
                  }}
                  className="text-[11px] font-semibold text-orange-700 underline hover:text-orange-900 whitespace-nowrap"
                >
                  desfazer
                </button>
              </div>
            </div>
          )}
          <div className="ade-neu-card p-4 border-2 border-[#25D366]/40 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-black flex items-center gap-1.5">
                  <MessageCircle className="w-4 h-4 text-[#25D366]" />
                  Link de palpites pelo WhatsApp
                </p>
                <p className="text-xs text-neutral-600 mt-0.5">
                  {organizador.whats ? (
                    <>
                      WhatsApp do organizador: <strong>+{organizador.whats}</strong>
                      {organizador.nome ? ` (${organizador.nome})` : ''}.
                      <button
                        type="button"
                        onClick={abrirModalOrganizador}
                        className="text-[#009b3a] underline ml-1"
                      >
                        alterar
                      </button>
                    </>
                  ) : (
                    'Configure seu nome e WhatsApp para identificar de onde os links chegam.'
                  )}
                </p>
                <p className="text-xs text-neutral-600 mt-1">
                  Modo de envio:{' '}
                  {sheetsEndpoint ? (
                    <span className="text-emerald-700 font-semibold">
                      Direto pro app (Google Sheets) — sem voltar pelo WhatsApp
                    </span>
                  ) : (
                    <span className="text-amber-700 font-semibold">
                      Volta pelo WhatsApp (você importa o link)
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={abrirModalSheets}
                    className="text-[#009b3a] underline ml-1"
                  >
                    configurar
                  </button>
                </p>
                {!obterBaseLinkPublico(organizador) && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mt-2">
                    Para o link ficar <strong>azul no WhatsApp</strong>, configure a{' '}
                    <strong>URL pública https</strong> em Configurar organizador (o app no
                    computador gera <code className="text-[10px]">file://</code>, que não
                    funciona no celular).
                  </p>
                )}
                {obterBaseLinkPublico(organizador) && (
                  <p className="text-[11px] text-[#009b3a] mt-1 truncate" title={linkPalpitesPublico()}>
                    Link: {linkPalpitesPublico() || '—'}
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                {!organizador.whats && (
                  <button
                    type="button"
                    onClick={abrirModalOrganizador}
                    className="px-3 py-2 rounded-lg text-xs font-semibold bg-neutral-100 border border-neutral-300 hover:bg-neutral-200 inline-flex items-center gap-1"
                  >
                    Configurar organizador
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => copiarParaWhatsApp(false)}
                  className="px-3 py-2 rounded-lg text-xs font-semibold bg-neutral-100 border border-neutral-300 hover:bg-neutral-200 inline-flex items-center gap-1"
                >
                  Copiar mensagem
                </button>
                <button
                  type="button"
                  onClick={() => copiarParaWhatsApp(true)}
                  className="px-3 py-2 rounded-lg text-xs font-semibold bg-neutral-100 border border-neutral-300 hover:bg-neutral-200 inline-flex items-center gap-1"
                  title="Só o endereço https (uma linha)"
                >
                  Copiar só o link
                </button>
                <button
                  type="button"
                  onClick={compartilharLinkPalpites}
                  className="px-3 py-2 rounded-lg text-xs font-semibold bg-[#25D366] hover:bg-[#1ebe57] text-white inline-flex items-center gap-1.5 shadow"
                >
                  <Share2 className="w-4 h-4" />
                  Compartilhar link
                </button>
              </div>
              <div className="mt-3 pt-3 border-t border-neutral-200">
                <p className="text-[11px] text-neutral-600 mb-1.5">
                  <strong>Precisa que alguém refaça do zero?</strong> Use este link especial — ao
                  abrir, o celular da pessoa apaga a cópia antiga e mostra o formulário em branco.
                </p>
                <button
                  type="button"
                  onClick={copiarLinkComResetCopia}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-amber-50 border-2 border-amber-300 text-amber-800 hover:bg-amber-100 inline-flex items-center gap-1"
                  title="Link com ?novo=1 — apaga a cópia local da pessoa quando ela abre"
                >
                  <Eraser className="w-3 h-3" />
                  Copiar link "começar do zero"
                </button>
              </div>
              <div className="mt-3 pt-3 border-t border-neutral-200">
                <p className="text-[11px] text-neutral-600 mb-1.5">
                  <strong>Liberar 1 envio extra (após o prazo)?</strong> Para um participante
                  específico reenviar a fase de grupos. Antes, rode a função{' '}
                  <strong>liberarPalpiteExtra</strong> no Apps Script da planilha (com o nome e
                  setor da pessoa). Trava de novo assim que ela enviar.
                </p>
                <button
                  type="button"
                  onClick={copiarLinkEnvioExtra}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-emerald-50 border-2 border-emerald-300 text-emerald-800 hover:bg-emerald-100 inline-flex items-center gap-1"
                  title="Link com &extra=1 — libera o formulário de grupos só para quem você liberou no Apps Script"
                >
                  <Send className="w-3 h-3" />
                  Copiar link de envio extra (1 vez)
                </button>
              </div>
            </div>

            <div className="border-t border-[#25D366]/30 pt-3">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="font-semibold text-black flex items-center gap-1.5 text-sm">
                  <UserPlus className="w-4 h-4 text-[#25D366]" />
                  Convidados ({convidados.length})
                </p>
                {convidados.length > 0 && (
                  <button
                    type="button"
                    onClick={enviarLinkParaTodos}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-[#009b3a] hover:bg-[#007a2e] text-white inline-flex items-center gap-1.5"
                  >
                    <Send className="w-3.5 h-3.5" />
                    Enviar pra todos
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 items-end mb-2">
                <div className="flex-1 min-w-[140px]">
                  <input
                    type="text"
                    value={novoConvidadoNome}
                    onChange={(e) => setNovoConvidadoNome(e.target.value)}
                    placeholder="Nome"
                    className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-[#009b3a]/25"
                  />
                </div>
                <div className="flex-1 min-w-[140px]">
                  <input
                    type="tel"
                    value={novoConvidadoWhats}
                    onChange={(e) => setNovoConvidadoWhats(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && adicionarConvidado()}
                    placeholder="WhatsApp com DDD"
                    className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-[#009b3a]/25"
                  />
                </div>
                <button
                  type="button"
                  onClick={adicionarConvidado}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#009b3a] text-white inline-flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Adicionar
                </button>
              </div>
              {convidados.length === 0 ? (
                <p className="text-xs text-neutral-500 italic">
                  Cadastre seus amigos uma vez aqui. Depois é só clicar em "Enviar link" pra
                  cada um e abrir direto no WhatsApp Web.
                </p>
              ) : (
                <ul className="grid gap-1.5 sm:grid-cols-2">
                  {convidados.map((c) => (
                    <li
                      key={c.id}
                      className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white rounded-lg border border-neutral-200"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-black truncate">{c.nome}</p>
                        <p className="text-[11px] text-neutral-500">+{c.whats}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => enviarLinkParaConvidado(c)}
                        className="px-2 py-1 rounded text-[11px] font-bold bg-[#25D366] hover:bg-[#1ebe57] text-white inline-flex items-center gap-1"
                        title="Enviar link pelo WhatsApp"
                      >
                        <Send className="w-3 h-3" />
                        Enviar
                      </button>
                      <button
                        type="button"
                        onClick={() => removerConvidado(c.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Remover convidado"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {sheetsEndpoint && (
              <div className="border-t border-[#25D366]/30 pt-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-black flex items-center gap-1.5 text-sm">
                      <Cloud className="w-4 h-4 text-[#009b3a]" />
                      Palpites recebidos pelo formulário
                    </p>
                    <p className="text-xs text-neutral-600 mt-0.5">
                      Os palpites enviados ficam guardados online e podem ser corrigidos pelo
                      próprio participante <strong>até {formatarPrazoBrt()}</strong>. Depois disso
                      ninguém mais consegue alterar.{' '}
                      {ultimaSync && (
                        <span className="text-neutral-500">
                          Última atualização às {ultimaSync}.
                        </span>
                      )}
                    </p>
                    {ultimaSyncResultados && (
                      <p className="text-[11px] text-emerald-700 mt-0.5 flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" />
                        Placares oficiais sincronizados automaticamente às{' '}
                        {ultimaSyncResultados}.
                      </p>
                    )}
                    {palpitesEncerrados() && (
                      <p className="text-[11px] text-amber-700 font-semibold mt-1 flex items-center gap-1">
                        <Lock className="w-3 h-3" />
                        Prazo encerrado. Palpites travados definitivamente.
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={sincronizarPalpitesRecebidos}
                    disabled={sincronizando}
                    className={`px-3 py-2 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 shadow ${
                      sincronizando
                        ? 'bg-neutral-300 text-neutral-600 cursor-wait'
                        : 'bg-[#009b3a] hover:bg-[#007a2e] text-white'
                    }`}
                  >
                    <RefreshCw className={`w-4 h-4 ${sincronizando ? 'animate-spin' : ''}`} />
                    {sincronizando ? 'Buscando...' : 'Atualizar palpites'}
                  </button>
                </div>
                {idsIgnorados.filter((x) => x.permanente !== false).length > 0 && (
                  <div className="mt-3 border border-red-200 bg-red-50 rounded-lg p-2.5">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <p className="text-xs font-bold text-red-800 inline-flex items-center gap-1">
                        <Trash2 className="w-3.5 h-3.5" />
                        Bloqueados ({idsIgnorados.filter((x) => x.permanente !== false).length})
                        — não voltam mais
                      </p>
                      <button
                        type="button"
                        onClick={limparHistoricoBloqueados}
                        className="text-[10px] font-semibold text-red-700 underline hover:text-red-900"
                        title="Permitir reenvio de todos"
                      >
                        liberar todos
                      </button>
                    </div>
                    <ul className="grid gap-1 sm:grid-cols-2">
                      {idsIgnorados
                        .filter((x) => x.permanente !== false)
                        .map((ig) => {
                          const nomeDisplay =
                            ig.nomeOriginal ||
                            (ig.nomeNorm
                              ? ig.nomeNorm.replace(/\b\w/g, (c) => c.toUpperCase())
                              : '(sem nome)');
                          const setorDisplay = ig.setorOriginal || ig.setorNorm || '';
                          return (
                            <li
                              key={ig.id}
                              className="flex items-center justify-between gap-2 bg-white border border-red-100 rounded px-2 py-1"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold text-black truncate">
                                  {nomeDisplay}
                                </p>
                                {setorDisplay && (
                                  <p className="text-[10px] text-neutral-600 truncate">
                                    Setor: {setorDisplay}
                                  </p>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => permitirReenvio(ig)}
                                className="text-[10px] font-bold text-emerald-700 hover:underline whitespace-nowrap"
                                title="Permitir que essa pessoa envie palpites de novo"
                              >
                                Permitir reenvio
                              </button>
                            </li>
                          );
                        })}
                    </ul>
                    <p className="text-[10px] text-red-700 mt-1.5 leading-snug">
                      Esses nomes foram bloqueados (lixeira vermelha). Não voltam nem se a
                      pessoa reenviar. Use <strong>Permitir reenvio</strong> se quiser liberar
                      alguém.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="ade-neu-card p-4 border-2 border-[#009b3a]/30 bg-gradient-to-br from-[#f5fff8] to-[#fafbfc] flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#009b3a]/10 flex items-center justify-center shrink-0">
                <Users className="w-6 h-6 text-[#009b3a]" />
              </div>
              <div>
                <p className="text-[11px] uppercase font-bold text-neutral-500 tracking-wide">
                  Total de participantes
                </p>
                <p className="text-3xl font-black text-[#009b3a] leading-tight">
                  {participantes.length}
                </p>
              </div>
            </div>
            {participantes.length > 0 && (
              <div className="text-right text-xs text-neutral-600 space-y-0.5">
                <p>
                  <strong className="text-emerald-700">
                    {participantes.filter((p) => p.pagou).length}
                  </strong>{' '}
                  pago(s)
                </p>
                <p>
                  <strong className="text-amber-700">
                    {participantes.filter((p) => !p.pagou).length}
                  </strong>{' '}
                  pendente(s)
                </p>
              </div>
            )}
          </div>
          {participantes.length === 0 ? (
            <p className="text-center text-neutral-600 py-8">Nenhum participante ainda.</p>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {participantesOrdemAlfabetica.map((p) => {
                const qtd = contarPalpitesGruposValidos(p.palpites);
                const contagem = calcularContagemAutoManual(p.palpites);
                const labelContagem = contagem ? rotuloContagem(contagem) : '';
                const corBadgeContagem = !contagem
                  ? ''
                  : contagem.manual === 0
                    ? 'bg-rose-100 text-rose-800 border border-rose-200'
                    : contagem.auto === 0
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-amber-100 text-amber-800 border border-amber-200';
                const tituloBadge = contagem ? tituloContagem(contagem) : '';
                const ehNovo = Boolean(
                  p.origemId && idsNovosParticipantes.includes(p.origemId)
                );
                return (
                  <li
                    key={p.id}
                    className={`ade-neu-card p-3 flex items-center justify-between gap-2 border ${
                      ehNovo
                        ? 'ade-card-novo-participante border-[var(--ade-gold-border)] ring-2 ring-[var(--ade-gold-border)]'
                        : p.pagou
                          ? 'border-emerald-400 bg-emerald-50/40'
                          : 'border-[#009b3a]/15'
                    }`}
                    style={
                      ehNovo
                        ? {
                            background: 'var(--ade-gold-gradient)',
                            borderColor: 'var(--ade-gold-border)',
                          }
                        : undefined
                    }
                    title={ehNovo ? 'Novo participante (entrou nesta atualização)' : undefined}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-semibold text-black truncate">{p.nome}</p>
                        {ehNovo && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[10px] font-black uppercase px-1.5 py-0.5 rounded border border-[var(--ade-gold-border)] bg-white/55 text-[var(--ade-gold-text)]"
                            title="Entrou nesta atualização"
                          >
                            <Sparkles className="w-2.5 h-2.5" />
                            Novo
                          </span>
                        )}
                        {p.bloqueado && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                            <Lock className="w-2.5 h-2.5" />
                            Travado
                          </span>
                        )}
                        {labelContagem && (
                          <span
                            className={`inline-flex items-center text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${corBadgeContagem}`}
                            title={tituloBadge}
                          >
                            {labelContagem}
                          </span>
                        )}
                      </div>
                      {p.setor && (
                        <p className="text-xs text-[#009b3a] font-medium truncate">
                          Setor: {p.setor}
                        </p>
                      )}
                      <p className="text-xs text-neutral-600">
                        {qtd}/{JOGOS_COPA_2026.length} jogos · {p.criadoEm}
                      </p>
                      <label className="mt-1.5 inline-flex items-center gap-1.5 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={Boolean(p.pagou)}
                          onChange={() => toggleParticipantePagou(p.id)}
                          className="w-4 h-4 accent-emerald-600"
                        />
                        <span
                          className={`text-xs font-semibold ${
                            p.pagou ? 'text-emerald-700' : 'text-neutral-600'
                          }`}
                        >
                          {p.pagou ? 'Pago' : 'Marcar como pago'}
                        </span>
                      </label>
                      <div className="mt-1.5 flex items-center gap-1.5">
                        <label className="text-[11px] font-semibold text-neutral-700">
                          Faltas:
                        </label>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={p.faltas ?? 0}
                          onChange={(e) =>
                            setFaltasParticipante(p.id, Number(e.target.value))
                          }
                          className="w-14 px-1.5 py-0.5 text-xs rounded border border-neutral-300 text-center font-bold"
                          title="Dias faltados no trabalho (cada 1 = -25% do prêmio)"
                        />
                        {(p.faltas ?? 0) > 0 && (
                          <span className="text-[10px] font-bold text-red-600">
                            -{Math.min(100, (p.faltas ?? 0) * 25)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setParticipanteAtivoId(p.id);
                          setAba('palpites');
                        }}
                        className="px-2 py-1 text-xs rounded-lg bg-[#009b3a] text-white font-semibold"
                      >
                        Palpites
                      </button>
                      <button
                        type="button"
                        onClick={() => removerParticipanteSemBloquear(p.id)}
                        className="p-1.5 text-amber-700 hover:bg-amber-50 rounded"
                        title="Excluir mas PERMITINDO que reenvie com o mesmo nome"
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removerParticipante(p.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Excluir e BLOQUEAR esse nome (não volta ao atualizar palpites)"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {aba === 'palpites' && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 border-b border-[#009b3a]/20 pb-3">
            <button
              type="button"
              onClick={() => setPalpitesSubVisao('grupos')}
              className={`px-3 py-2 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 transition ${
                palpitesSubVisao === 'grupos'
                  ? 'bg-[#009b3a] text-white shadow'
                  : 'bg-[#e4e6ec] text-black hover:bg-[#dde0e6]'
              }`}
            >
              <Flag className="w-3.5 h-3.5" />
              Fase de grupos
            </button>
            <button
              type="button"
              onClick={() => setPalpitesSubVisao('matamata')}
              className={`px-3 py-2 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 transition ${
                palpitesSubVisao === 'matamata'
                  ? 'bg-amber-600 text-white shadow'
                  : 'bg-[#e4e6ec] text-black hover:bg-[#dde0e6]'
              }`}
            >
              <Trophy className="w-3.5 h-3.5" />
              Mata-mata
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm font-medium">Participante:</label>
            <select
              value={participanteAtivoId ?? ''}
              onChange={(e) => {
                if (corrigindoServidorId) cancelarCorrecaoServidor();
                setParticipanteAtivoId(e.target.value || null);
              }}
              className="px-3 py-2 rounded-lg border border-[#009b3a]/25 text-sm min-w-[180px]"
            >
              <option value="">Selecione…</option>
              {participantesOrdemAlfabetica.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                  {palpitesSubVisao === 'matamata'
                    ? ` (${contarPalpitesMMValidos(p.palpites)} MM)`
                    : ` (${contarPalpitesGruposValidos(p.palpites)}/72)`}
                </option>
              ))}
            </select>
          </div>

          {palpitesSubVisao === 'matamata' && (
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setPalpitesMmFaseFiltro('todas')}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition ${
                  palpitesMmFaseFiltro === 'todas'
                    ? 'bg-amber-600 text-white border-amber-700'
                    : 'bg-white text-neutral-700 border-neutral-300 hover:bg-amber-50'
                }`}
              >
                Todas
              </button>
              {FASES_MATA_MATA.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setPalpitesMmFaseFiltro(f)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition ${
                    palpitesMmFaseFiltro === f
                      ? 'bg-amber-600 text-white border-amber-700'
                      : 'bg-white text-neutral-700 border-neutral-300 hover:bg-amber-50'
                  }`}
                >
                  {NOME_FASE[f]}
                </button>
              ))}
              <span className="w-px h-6 bg-neutral-300 mx-1 self-center" aria-hidden />
              {(
                [
                  ['todos', 'Todos'],
                  ['preenchidos', 'Com palpite'],
                  ['vazios', 'Sem palpite'],
                ] as const
              ).map(([val, rotulo]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setPalpitesMmStatusFiltro(val)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition ${
                    palpitesMmStatusFiltro === val
                      ? 'bg-[#009b3a] text-white border-[#007a2e]'
                      : 'bg-white text-neutral-700 border-neutral-300 hover:bg-[#009b3a]/10'
                  }`}
                >
                  {rotulo}
                </button>
              ))}
            </div>
          )}

          {palpitesSubVisao === 'matamata' && (
            <MelhoresTerceirosCard
              placaresGrupos={placaresParaMelhoresTerceiros}
              melhoresTerceiros={configMM.melhoresTerceiros}
              onMelhoresTerceirosChange={(mt) =>
                setConfigMM((prev) => ({ ...prev, melhoresTerceiros: mt }))
              }
              faseGruposCompleta={gruposProntosParaMelhoresTerceiros}
            />
          )}

          {!participanteAtivo ? (
            <p className="text-neutral-600 text-sm py-6 text-center">
              Escolha ou cadastre um participante para ver os palpites
              {palpitesSubVisao === 'matamata' ? ' do mata-mata' : ' da fase de grupos'}.
            </p>
          ) : palpitesSubVisao === 'grupos' ? (
            <>
              {participanteAtivo.bloqueado ? (
                corrigindoServidorId === participanteAtivo.id ? (
                  <div className="ade-neu-card p-3 border-2 border-blue-400 bg-blue-50 text-blue-900 text-xs space-y-2">
                    <div className="flex items-start gap-2">
                      <Pencil className="w-4 h-4 mt-0.5 shrink-0" />
                      <p>
                        <strong>Modo correção.</strong> Ajuste o placar errado nos grupos
                        abaixo e clique em <strong>Salvar correção no servidor</strong>. O valor
                        corrigido entra no cálculo da pontuação e aparece para todos no link.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      <button
                        type="button"
                        onClick={cancelarCorrecaoServidor}
                        disabled={salvandoCorrecao}
                        className="px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-neutral-100 hover:bg-neutral-200 text-neutral-700 border border-neutral-300 disabled:opacity-60"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => salvarCorrecaoNoServidor(participanteAtivo.id)}
                        disabled={salvandoCorrecao}
                        className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1 disabled:opacity-60"
                      >
                        {salvandoCorrecao ? 'Salvando…' : 'Salvar correção no servidor'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="ade-neu-card p-3 border-2 border-amber-300 bg-amber-50 text-amber-900 text-xs flex items-start gap-2 flex-wrap">
                    <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                    <p className="flex-1 min-w-[200px]">
                      Os palpites de <strong>{participanteAtivo.nome}</strong> chegaram pelo
                      formulário e estão <strong>travados</strong>. Para consertar um placar
                      digitado errado, use a correção abaixo.
                    </p>
                    <button
                      type="button"
                      onClick={() => iniciarCorrecaoServidor(participanteAtivo.id)}
                      className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1"
                      title="Conserta um placar digitado errado e grava no servidor"
                    >
                      <Pencil className="w-3 h-3" />
                      Corrigir placar (salvar no servidor)
                    </button>
                  </div>
                )
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-neutral-600 flex-1 min-w-[200px]">
                    Simulação de <strong>{participanteAtivo.nome}</strong> — use as setas em
                    cada grupo para ver as rodadas. Verde = classificados (1º e 2º) quando o
                    grupo estiver completo.
                  </p>
                  <div className="flex gap-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={() => autoPreencherParticipante(participanteAtivo.id, true)}
                      className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 inline-flex items-center gap-1"
                      title="Preenche só os jogos vazios deste participante"
                    >
                      Auto-preencher vazios
                    </button>
                    <button
                      type="button"
                      onClick={() => autoPreencherParticipante(participanteAtivo.id, false)}
                      className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-[#009b3a] hover:bg-[#007a2e] text-white inline-flex items-center gap-1"
                      title="Substitui TODOS os palpites deste participante"
                    >
                      Auto-preencher tudo
                    </button>
                    <button
                      type="button"
                      onClick={() => limparPalpitesParticipante(participanteAtivo.id)}
                      className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-red-100 hover:bg-red-200 text-red-800 border border-red-300 inline-flex items-center gap-1"
                      title="Apaga TODOS os palpites deste participante"
                    >
                      Apagar palpites
                    </button>
                  </div>
                </div>
              )}
              {participanteAtivo.bloqueado && (
                <div className="flex justify-end mt-1">
                  {palpitesEncerrados() ? (
                    <span className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-neutral-200 text-neutral-500 border border-neutral-300 inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Destravar bloqueado — prazo encerrou ({formatarPrazoBrt()})
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => destravarParticipante(participanteAtivo.id)}
                      className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-neutral-100 hover:bg-neutral-200 text-neutral-700 border border-neutral-300 inline-flex items-center gap-1"
                      title={`Permite editar os palpites travados até ${formatarPrazoBrt()}`}
                    >
                      Destravar palpites (correção até {formatarPrazoBrt().split(' às ')[0]})
                    </button>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                {GRUPOS_COPA_2026.map((g) => (
                  <CardGrupo
                    key={g.id}
                    grupoId={g.id}
                    placares={participanteAtivo.palpites}
                    somenteLeitura={
                      participanteAtivo.bloqueado &&
                      corrigindoServidorId !== participanteAtivo.id
                    }
                    onPlacarChange={
                      participanteAtivo.bloqueado &&
                      corrigindoServidorId !== participanteAtivo.id
                        ? undefined
                        : (jogoId, p) =>
                            setPalpiteParticipante(participanteAtivo.id, jogoId, p)
                    }
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="ade-neu-card p-3 border border-amber-200 bg-amber-50/50 text-xs text-neutral-700">
                <p>
                  Palpites de mata-mata de <strong>{participanteAtivo.nome}</strong>
                  {participanteAtivo.setor ? ` (${participanteAtivo.setor})` : ''} —{' '}
                  <strong>{contarPalpitesMMValidos(participanteAtivo.palpites)}</strong> de{' '}
                  {JOGOS_MATA_MATA.length} jogos preenchidos.
                  {participanteAtivo.bloqueado ? (
                    <>
                      {' '}
                      Enviados pelo link público — somente leitura.
                    </>
                  ) : (
                    <>
                      {' '}
                      Simulação local — você pode editar os placares abaixo.
                    </>
                  )}
                </p>
                {participanteAtivo.bloqueado &&
                  contarPalpitesMMValidos(participanteAtivo.palpites) === 0 && (
                    <p className="mt-2 text-amber-900 bg-amber-100 border border-amber-300 rounded px-2 py-1.5">
                      Nenhum palpite de mata-mata baixado ainda. Clique em{' '}
                      <strong>Atualizar palpites</strong> na aba Participantes para
                      sincronizar com a planilha.
                    </p>
                  )}
              </div>
              {participanteAtivo.bloqueado && (
                <LiberarPalpitesMMCard
                  participante={participanteAtivo}
                  sheetsEndpoint={sheetsEndpoint}
                  resolverLado={(jogoId, qual) =>
                    resolverLadoPalpitesParticipante(
                      jogoId,
                      qual,
                      participanteAtivo.palpites
                    )
                  }
                  onLiberar={(jogoIds) =>
                    liberarPalpitesMMNoServidor(participanteAtivo.id, jogoIds)
                  }
                  onAutorizarAtrasado={(jogoIds) =>
                    autorizarPalpiteMMAtrasadoNoServidor(participanteAtivo.id, jogoIds)
                  }
                  onRegistrarFaltante={(jogoId, placar) =>
                    registrarPalpiteMMFaltanteNoServidor(
                      participanteAtivo.id,
                      jogoId,
                      placar
                    )
                  }
                />
              )}
              {(palpitesMmFaseFiltro === 'todas' ? FASES_MATA_MATA : [palpitesMmFaseFiltro]).map(
                (fase) => {
                  const jogos = jogosDaFase(fase).filter((j) => {
                    const temPalpite = placarValido(participanteAtivo.palpites[j.id]);
                    if (palpitesMmStatusFiltro === 'preenchidos') return temPalpite;
                    if (palpitesMmStatusFiltro === 'vazios') return !temPalpite;
                    return true;
                  });
                  if (jogos.length === 0) return null;
                  const qtdFase = jogos.filter((j) =>
                    placarValido(participanteAtivo.palpites[j.id])
                  ).length;
                  return (
                    <div
                      key={fase}
                      className="ade-neu-card p-3 border border-[#009b3a]/15"
                    >
                      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                        <h4 className="font-bold text-black">{NOME_FASE[fase]}</h4>
                        <span className="text-xs text-neutral-600">
                          {qtdFase}/{jogosDaFase(fase).length} palpite{qtdFase === 1 ? '' : 's'}
                          {jogos.length !== jogosDaFase(fase).length
                            ? ` · ${jogos.length} visível${jogos.length === 1 ? '' : 'is'}`
                            : ''}{' '}
                          · placar exato <strong>{PONTUACAO_POR_FASE[fase].placarExato}</strong> · acerto{' '}
                          <strong>{PONTUACAO_POR_FASE[fase].acertoVencedor}</strong>
                        </span>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                        {jogos.map((j) => {
                          const a = resolverLadoPalpitesParticipante(
                            j.id,
                            'A',
                            participanteAtivo.palpites
                          );
                          const b = resolverLadoPalpitesParticipante(
                            j.id,
                            'B',
                            participanteAtivo.palpites
                          );
                          const palpite = participanteAtivo.palpites[j.id];
                          const real = resultadosReais[j.id];
                          const pts =
                            placarValido(palpite) && placarValido(real)
                              ? pontosJogoMataMata(
                                  palpite,
                                  real,
                                  j.fase,
                                  vencedoresPenaltisMM[j.id]
                                )
                              : null;
                          const somenteLeitura = Boolean(participanteAtivo.bloqueado);
                          return (
                            <div key={j.id} className="relative">
                              {pts != null && (
                                <span
                                  className={`absolute -top-1 -right-1 z-10 text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow ${
                                    pts >= PONTUACAO_POR_FASE[j.fase].placarExato
                                      ? 'bg-emerald-600 text-white'
                                      : pts > 0
                                        ? 'bg-amber-500 text-white'
                                        : 'bg-neutral-400 text-white'
                                  }`}
                                  title="Pontos neste jogo (resultado real já lançado)"
                                >
                                  +{pts}
                                </span>
                              )}
                              <JogoMataMataCard
                                jogo={j}
                                ladoAtimeId={a.timeId}
                                ladoArotulo={a.rotulo}
                                ladoBtimeId={b.timeId}
                                ladoBrotulo={b.rotulo}
                                placar={palpite}
                                onChange={(p) =>
                                  setPalpiteParticipante(participanteAtivo.id, j.id, p)
                                }
                                somenteLeitura={somenteLeitura}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
              )}
              {(palpitesMmFaseFiltro === 'todas' ? FASES_MATA_MATA : [palpitesMmFaseFiltro]).every(
                (fase) =>
                  !jogosDaFase(fase).some((j) => {
                    const temPalpite = placarValido(participanteAtivo.palpites[j.id]);
                    if (palpitesMmStatusFiltro === 'preenchidos') return temPalpite;
                    if (palpitesMmStatusFiltro === 'vazios') return !temPalpite;
                    return true;
                  })
              ) && (
                <p className="text-sm text-neutral-500 text-center py-6 ade-neu-card border border-neutral-200">
                  Nenhum jogo corresponde ao filtro selecionado.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {aba === 'placar' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-neutral-600">
              {placarGeral.length} participante{placarGeral.length === 1 ? '' : 's'} · ordenado por pontos.
            </p>
            <div className="flex flex-wrap gap-2">
              {placarGeral.length > 0 && sheetsEndpoint && (
                <button
                  type="button"
                  onClick={publicarTabelaNaPlanilha}
                  disabled={publicandoTabela}
                  className={`px-3 py-2 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 border-2 ${
                    publicandoTabela
                      ? 'border-neutral-300 bg-neutral-100 text-neutral-500 cursor-wait'
                      : 'border-[#1d4ed8] bg-white text-[#1d4ed8] hover:bg-[#1d4ed8]/10'
                  }`}
                  title="Publica pontos+prêmios na planilha. Quem entrar no link público vê em 'Ver palpites dos outros'."
                >
                  <Cloud className={`w-4 h-4 ${publicandoTabela ? 'animate-pulse' : ''}`} />
                  {publicandoTabela ? 'Publicando…' : 'Publicar tabela para o público'}
                </button>
              )}
              {placarGeral.length > 0 && (
                <button
                  type="button"
                  onClick={imprimirPlacarGeral}
                  className="px-3 py-2 ade-btn-gold relative z-10 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5"
                >
                  <Printer className="w-4 h-4" />
                  Imprimir placar
                </button>
              )}
            </div>
          </div>
          {tabelaPublicadaEm && sheetsEndpoint && (
            <p className="text-[11px] text-neutral-500 -mt-2">
              Última publicação:{' '}
              {(() => {
                try {
                  const d = new Date(tabelaPublicadaEm);
                  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}`;
                } catch {
                  return tabelaPublicadaEm;
                }
              })()}{' '}
              · publique de novo quando atualizar resultados ou faltas.
            </p>
          )}
          <TabelaClassificacaoBolao
            linhas={linhasClassificacao}
            storageKey="copa-classificacao-snapshot-org"
            publicadoEm={tabelaPublicadaEm || undefined}
            subtitulo={`${placarGeral.length} participante${placarGeral.length === 1 ? '' : 's'} · clique na linha para ver detalhes`}
            expandidoId={expandidoPlacarId}
            onToggleLinha={(id) =>
              setExpandidoPlacarId((atual) => (atual === id ? '' : id))
            }
          />
          {participantes.length === 0 && (
            <p className="text-center text-neutral-600 py-6">Cadastre participantes e resultados reais.</p>
          )}
          {participantes.length > 0 && placarGeral.every((x) => x.total === 0) && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Preencha os <strong>resultados reais</strong> e os palpites para começar a pontuar.
            </p>
          )}
        </div>
      )}

      {aba === 'matamata' && (
        <div className="space-y-4">
          {!gruposCompletos && (
            <div className="ade-neu-card p-3 border-2 border-amber-300 bg-amber-50/70 text-amber-900 space-y-1">
              <div className="flex items-start gap-2">
                <Lock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-bold text-sm">Fase de grupos incompleta</h4>
                  <p className="text-xs">
                    Faltam preencher{' '}
                    <strong>
                      {
                        GRUPOS_COPA_2026.flatMap((g) =>
                          jogosDoGrupoId(g.id).filter((j) => !placarValido(resultadosReais[j.id]))
                        ).length
                      }
                    </strong>{' '}
                    resultados de grupos. Você já pode <strong>publicar parcialmente</strong> os
                    confrontos do mata-mata cujos dois times já estão definidos (ex.: se grupos C
                    e F estão prontos, os jogos C1×F2 e F1×C2 das 16-avos já podem ser publicados
                    no link público). Conforme mais grupos terminam, é só clicar em
                    "Republicar" da fase para liberar os novos confrontos.
                  </p>
                </div>
              </div>
            </div>
          )}
          <>
              <div className="ade-neu-card p-3 border border-[#009b3a]/20 text-xs text-neutral-700">
                <p>
                  Aqui você lança os <strong>resultados reais</strong> do mata-mata conforme a
                  Copa acontece. A pontuação cresce a cada fase:
                </p>
                <p className="mt-1">
                  16 avos <strong>6/2</strong> · oitavas <strong>9/3</strong> · quartas{' '}
                  <strong>12/4</strong> · semi <strong>15/5</strong> · 3º lugar e final{' '}
                  <strong>18/6</strong> (placar exato / acerto do vencedor).
                </p>
              </div>

              <MelhoresTerceirosCard
                placaresGrupos={resultadosReais}
                melhoresTerceiros={configMM.melhoresTerceiros}
                onMelhoresTerceirosChange={(mt) =>
                  setConfigMM((prev) => ({ ...prev, melhoresTerceiros: mt }))
                }
                faseGruposCompleta={gruposCompletos}
              />

              <ReorganizarCruzamentosCard
                configMM={configMM}
                onChange={setConfigMM}
                resolverLado={resolverLadoMataMata}
              />

              <CompartilharMataMataCard
                configMM={configMM}
                organizador={organizador}
                resolverLado={resolverLadoMataMata}
                onImprimir={imprimirTabelaMataMata}
              />

              <div className="ade-neu-card p-3 border-2 border-amber-300 bg-amber-50/40">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-5 h-5 text-amber-600" />
                  <h4 className="font-bold text-black">Link público do mata-mata</h4>
                </div>
                <p className="text-xs text-neutral-700 mb-2">
                  Mande este link para os participantes assim que você publicar uma fase abaixo.
                  Cada jogo trava no horário oficial — depois disso, ninguém mais palpita aquele jogo.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => copiarMataMataParaWhatsApp(false)}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-bold text-xs hover:bg-emerald-700 inline-flex items-center gap-1.5"
                    title="Copiar mensagem completa para colar no WhatsApp"
                  >
                    Copiar mensagem mata-mata
                  </button>
                  <button
                    type="button"
                    onClick={() => copiarMataMataParaWhatsApp(true)}
                    className="px-3 py-1.5 rounded-lg bg-blue-600 text-white font-bold text-xs hover:bg-blue-700 inline-flex items-center gap-1.5"
                  >
                    Copiar só o link
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!sheetsEndpoint) {
                        alert('Configure a URL do Apps Script primeiro.');
                        return;
                      }
                      try {
                        const url =
                          sheetsEndpoint +
                          (sheetsEndpoint.indexOf('?') === -1 ? '?' : '&') +
                          'acao=jogosFaseMM&_t=' +
                          Date.now();
                        const resp = await fetch(url);
                        const txt = await resp.text();
                        let json: unknown;
                        try {
                          json = JSON.parse(txt);
                        } catch {
                          alert(
                            'Servidor respondeu, mas NÃO em JSON. Provavelmente o Apps Script não está na v6.3.\n\n' +
                              'Resposta crua (primeiros 500 chars):\n\n' +
                              txt.slice(0, 500)
                          );
                          return;
                        }
                        const j = json as {
                          ok?: boolean;
                          fases?: Array<{ fase?: string; jogos?: unknown[] }>;
                          erro?: string;
                        };
                        if (!j.ok) {
                          alert(
                            'Servidor respondeu, mas com erro:\n\n' +
                              JSON.stringify(j, null, 2)
                          );
                          return;
                        }
                        const fases = j.fases || [];
                        const linhas = fases.map(
                          (f) =>
                            '• ' +
                            String(f.fase || '?') +
                            ': ' +
                            (Array.isArray(f.jogos) ? f.jogos.length : 0) +
                            ' jogo(s)'
                        );
                        alert(
                          'API respondeu OK.\n\n' +
                            (fases.length === 0
                              ? '⚠️ Nenhuma fase publicada ainda no servidor. Clique em "Publicar no link público" abaixo para começar.'
                              : 'Fases publicadas no servidor:\n' + linhas.join('\n'))
                        );
                      } catch (e) {
                        const msg = e instanceof Error ? e.message : String(e);
                        alert(
                          'Falha ao consultar a API.\n\n' +
                            msg +
                            '\n\nVerifique se a URL termina em /exec e se o Apps Script foi reimplantado depois da última edição.'
                        );
                      }
                    }}
                    className="px-3 py-1.5 rounded-lg bg-neutral-700 text-white font-bold text-xs hover:bg-neutral-800 inline-flex items-center gap-1.5"
                    title="Mostra o que o servidor está respondendo agora — útil pra diagnosticar quando os participantes não veem a fase"
                  >
                    Diagnosticar API mata-mata
                  </button>
                </div>
              </div>

              {FASES_MATA_MATA.map((fase) => {
                const status = statusPublicacaoFaseMM[fase];
                return (
                  <div key={fase} className="ade-neu-card p-3 border border-[#009b3a]/15">
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                      <h4 className="font-bold text-black">{NOME_FASE[fase]}</h4>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-neutral-600">
                          Placar exato <strong>{PONTUACAO_POR_FASE[fase].placarExato}</strong> ·
                          acerto <strong>{PONTUACAO_POR_FASE[fase].acertoVencedor}</strong>
                        </span>
                        <button
                          type="button"
                          onClick={() => publicarFaseMataMata(fase)}
                          disabled={status.status === 'publicando' || !sheetsEndpoint}
                          className="px-3 py-1 rounded-md text-xs font-bold bg-amber-600 text-white hover:bg-amber-700 disabled:bg-neutral-300 disabled:cursor-not-allowed"
                          title={
                            sheetsEndpoint
                              ? 'Publica os confrontos desta fase no link público'
                              : 'Configure a URL do Apps Script em Participantes → configurar'
                          }
                        >
                          {status.status === 'publicando'
                            ? 'Publicando...'
                            : status.status === 'ok'
                            ? '✓ Publicado · Republicar'
                            : 'Publicar no link público'}
                        </button>
                        {status.status === 'ok' && (
                          <button
                            type="button"
                            onClick={() => despublicarFaseMataMata(fase)}
                            disabled={!sheetsEndpoint}
                            className="px-2.5 py-1 rounded-md text-xs font-bold bg-rose-100 text-rose-700 border border-rose-300 hover:bg-rose-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Limpa esta fase do link público (palpites já enviados ficam guardados)"
                          >
                            Despublicar
                          </button>
                        )}
                      </div>
                    </div>
                    {status.status === 'ok' && status.msg ? (
                      <p className="text-[11px] text-emerald-700 mb-2">
                        ✓ {status.msg}{' '}
                        {status.em
                          ? `· ${new Date(status.em).toLocaleString('pt-BR')}`
                          : ''}
                      </p>
                    ) : null}
                    {status.status === 'erro' && status.msg ? (
                      <p className="text-[11px] text-rose-700 mb-2 whitespace-pre-line">
                        ✗ {status.msg}
                      </p>
                    ) : null}
                    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                      {jogosDaFase(fase).map((j) => {
                        const a = resolverLadoMataMata(j.id, 'A');
                        const b = resolverLadoMataMata(j.id, 'B');
                        return (
                          <JogoMataMataCard
                            key={j.id}
                            jogo={j}
                            ladoAtimeId={a.timeId}
                            ladoArotulo={a.rotulo}
                            ladoBtimeId={b.timeId}
                            ladoBrotulo={b.rotulo}
                            placar={resultadosReais[j.id]}
                            onChange={(p) => setPlacarReal(j.id, p)}
                            vencedorPenaltis={vencedoresPenaltisMM[j.id]}
                            onVencedorPenaltisChange={(lado) =>
                              setVencedorPenaltisReal(j.id, lado)
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
        </div>
      )}

      {aba === 'premiacao' && (
        <div className="space-y-4">
          <div className="ade-neu-card p-4 border-2 border-amber-300 bg-amber-50/40">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="font-bold text-black flex items-center gap-2">
                <Medal className="w-5 h-5 text-amber-500" />
                Premiação do bolão
              </h4>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-1.5 text-xs font-semibold text-neutral-700">
                  Tamanho da fonte (px):
                  <input
                    type="text"
                    inputMode="decimal"
                    value={tamanhoFonteRegras}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
                      setTamanhoFonteRegras(v);
                    }}
                    placeholder="14"
                    title="Tamanho base da fonte na impressão (recomendado 12 a 18)"
                    className="w-16 px-2 py-1 rounded border border-neutral-300 text-center font-mono"
                    style={{
                      MozAppearance: 'textfield',
                      WebkitAppearance: 'none',
                      appearance: 'textfield',
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => imprimirRegrasPremiacao(Number(tamanhoFonteRegras) || 14)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 border-2 border-[#009b3a] bg-white text-[#009b3a] hover:bg-[#009b3a]/10"
                  title="Gera uma folha A4 com todas as regras para colar no mural (usa o tamanho de fonte do campo ao lado)"
                >
                  <Printer className="w-4 h-4" />
                  Imprimir regras (mural)
                </button>
              </div>
            </div>
            <p className="text-xs text-neutral-700 mt-1">
              <strong>Prêmio base:</strong> 1º lugar = {formatarReais(2400)} ·{' '}
              2º lugar = {formatarReais(600)}.<br />
              <strong>Ordem da tabela:</strong> sempre por pontuação atual. Se alguém
              ultrapassar outro nos pontos, a tabela é recalculada na hora — o prêmio base de
              R$ 600 segue com quem estiver em 2º naquela hora.<br />
              <strong>Punição por faltas no trabalho (11/06 a 19/07):</strong> cada falta
              (1 dia = 9h, podendo ser acumuladas — ex.: 1h num dia + 2h em outro, e assim
              por diante; ao somar 9h vira uma falta cheia) tira 25% do prêmio. O desconto
              vale tanto sobre o prêmio base quanto sobre qualquer valor recebido por
              repasse.<br />
              <strong>Repasse:</strong> o valor perdido <strong>pula quem já tem prêmio</strong>{' '}
              (1º, 2º, ou empatados que dividiram o prêmio base) e abre prêmio só para os
              colocados que ainda não ganharam nada. Os já premiados só voltam a receber via{' '}
              <strong>sobra</strong> (quando o valor cai no fim da tabela e volta para o topo). Se
              for <strong>R$ 75 ou menos</strong>, vai inteiro para 1 só colocado. Se for maior,
              divide em <strong>partes iguais de no máximo {formatarReais(600)} cada</strong>{' '}
              (o valor do 2º) — abrindo quantos novos premiados forem necessários: 1 falta
              do 1º = 2 partes (R$ 300 cada), 2 faltas = 2 partes (R$ 600 cada), 3 faltas = 3
              partes (R$ 600 cada), 4+ faltas = 4 partes (R$ 600 cada).<br />
              <strong>Quem faltou e ainda não recebeu também pode receber repasse</strong> —
              só que, sobre o que entrar pra ele, incide a mesma % de desconto, e essa
              parte volta a ser redistribuída. Quem faltou e já recebeu repasse uma vez
              não pode receber de novo: só entra na fila quem ainda não recebeu nada.<br />
              <strong>Sobras</strong> (quando não tem mais quem premiar abaixo) voltam pro
              topo da tabela: vão pro não-faltante mais bem colocado.<br />
              <strong>Consistência (entre quem não faltou):</strong> quem está em colocação
              melhor sempre recebe prêmio igual ou maior que quem está embaixo.
            </p>
          </div>

          {premiacao.length === 0 ? (
            <p className="text-center text-neutral-600 py-6">
              Cadastre participantes para calcular a premiação.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#009b3a] text-white">
                  <tr>
                    <th className="px-2 py-2 text-left">#</th>
                    <th className="px-2 py-2 text-left">Participante</th>
                    <th className="px-2 py-2 text-center">Pts</th>
                    <th className="px-2 py-2 text-center">Faltas</th>
                    <th className="px-2 py-2 text-right">Prêmio base</th>
                    <th className="px-2 py-2 text-right">Perdido</th>
                    <th className="px-2 py-2 text-right">Recebeu</th>
                    <th className="px-2 py-2 text-right">Prêmio final</th>
                  </tr>
                </thead>
                <tbody>
                  {premiacao.map((linha, i) => {
                    const placarLinha = placarGeral[i];
                    const pts = placarLinha ? placarLinha.total : 0;
                    return (
                      <tr
                        key={linha.id}
                        className={`border-b border-neutral-200 ${
                          linha.premioFinal > 0 ? 'bg-emerald-50/50' : ''
                        } ${linha.faltas > 0 ? 'text-red-700' : ''}`}
                      >
                        <td className="px-2 py-2 font-bold">{linha.posicao}º</td>
                        <td className="px-2 py-2 font-semibold text-black">
                          {linha.nome}
                        </td>
                        <td className="px-2 py-2 text-center font-mono">{pts}</td>
                        <td className="px-2 py-2 text-center">
                          {linha.faltas > 0 ? `${linha.faltas} (-${Math.min(100, linha.faltas * 25)}%)` : '—'}
                        </td>
                        <td className="px-2 py-2 text-right">
                          {linha.premioBase > 0 ? formatarReais(linha.premioBase) : '—'}
                        </td>
                        <td className="px-2 py-2 text-right text-red-600">
                          {linha.perdidoPorFalta > 0
                            ? `-${formatarReais(linha.perdidoPorFalta)}`
                            : '—'}
                        </td>
                        <td className="px-2 py-2 text-right text-emerald-700">
                          {linha.recebidoDeOutros > 0
                            ? `+${formatarReais(linha.recebidoDeOutros)}`
                            : '—'}
                        </td>
                        <td className="px-2 py-2 text-right font-black text-[#009b3a]">
                          {linha.premioFinal > 0 ? formatarReais(linha.premioFinal) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-neutral-50 font-bold border-t-2 border-[#009b3a]">
                    <td className="px-2 py-2" colSpan={7}>
                      Total a pagar
                    </td>
                    <td className="px-2 py-2 text-right text-[#009b3a]">
                      {formatarReais(
                        premiacao.reduce((s, x) => s + x.premioFinal, 0)
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {premiacao.some((p) => p.detalheRepasses.length > 0) && (
            <div className="ade-neu-card p-3 border border-[#009b3a]/15 text-xs text-neutral-700 space-y-1">
              <p className="font-semibold text-black">Detalhe dos repasses por falta:</p>
              {premiacao.map((linha) =>
                linha.detalheRepasses.length > 0 ? (
                  <p key={linha.id}>
                    <strong>{linha.nome}</strong> recebeu:{' '}
                    {linha.detalheRepasses
                      .map((r) => `${formatarReais(r.valor)} de ${r.de}`)
                      .join(' · ')}
                  </p>
                ) : null
              )}
            </div>
          )}

          <MensagensFinaisCard
            fimBolao={fimBolao}
            mensagens={mensagensFinais}
          />
        </div>
      )}

      {aba === 'historico' && (
        <HistoricoBolaoCopaPanel historico={historicoBolao} />
      )}

      {aba === 'comparar' && (
        <ComparacaoParticipantesCard
          participantes={participantes}
          resultadosReais={resultadosReais}
          vencedoresPenaltisMM={vencedoresPenaltisMM}
          resolverLadoMataMata={resolverLadoMataMata}
        />
      )}

      {aba === 'estatisticas' && (
        <EstatisticasCard
          estatisticas={dadosEstatisticas?.estatisticas ?? null}
          eliminacao={dadosEstatisticas?.eliminacao ?? null}
        />
      )}

      {modalOrgAberto && (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border-2 border-[#009b3a]/30">
            <h3 className="text-lg font-bold text-black flex items-center gap-2 mb-1">
              <MessageCircle className="w-5 h-5 text-[#25D366]" />
              Configurar organizador do bolão
            </h3>
            <p className="text-xs text-neutral-600 mb-4">
              Dados usados ao compartilhar o link. No app pelo computador (nw.exe), a URL pública é
              obrigatória para o link ficar azul no WhatsApp.
            </p>
            <label className="block text-xs font-semibold mb-1">
              URL pública do bolão (https) — obrigatório no PC
            </label>
            <input
              type="url"
              value={orgUrlPublicaInput}
              onChange={(e) => setOrgUrlPublicaInput(e.target.value)}
              placeholder={URL_PUBLICA_BOLAO_OFICIAL}
              className="w-full px-3 py-2 rounded-lg border border-[#009b3a]/25 mb-1 text-sm"
            />
            <button
              type="button"
              onClick={aplicarUrlOficialBolao}
              className="mb-2 text-[11px] font-semibold text-[#009b3a] underline hover:text-[#007a2e]"
            >
              Usar domínio oficial ({URL_PUBLICA_BOLAO_OFICIAL.replace('https://', '')})
            </button>
            <p className="text-[11px] text-neutral-500 mb-3">
              Endereço onde a pasta <strong>dist/</strong> do bolão está na internet (sem barra no
              final). O domínio oficial é <strong>detudoumpoucomais.com.br</strong>.
            </p>
            <label className="block text-xs font-semibold mb-1">Seu nome (aparece no link)</label>
            <input
              type="text"
              value={orgNomeInput}
              onChange={(e) => setOrgNomeInput(e.target.value)}
              placeholder="Ex.: Ademilson"
              className="w-full px-3 py-2 rounded-lg border border-[#009b3a]/25 mb-3"
            />
            <label className="block text-xs font-semibold mb-1">
              Seu WhatsApp (com DDD, só números)
            </label>
            <input
              type="tel"
              value={orgWhatsInput}
              onChange={(e) => setOrgWhatsInput(e.target.value.replace(/\D/g, ''))}
              placeholder="Ex.: 5562999998888"
              className="w-full px-3 py-2 rounded-lg border border-[#009b3a]/25 mb-1"
            />
            <p className="text-[11px] text-neutral-500 mb-4">
              Use o formato internacional: 55 (Brasil) + DDD + número. Ex.: 5562999998888.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setModalOrgAberto(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-neutral-100 hover:bg-neutral-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={salvarOrganizador}
                className="px-4 py-2 rounded-lg text-sm font-bold bg-[#009b3a] text-white hover:bg-[#007a2e]"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {modalSheetsAberto && (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 border-2 border-[#009b3a]/30 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-black flex items-center gap-2 mb-1">
              <Cloud className="w-5 h-5 text-[#009b3a]" />
              Envio direto via Google Sheets
            </h3>
            <p className="text-xs text-neutral-600 mb-3">
              Quando configurado, os amigos clicam em "Enviar" no formulário e o palpite chega
              direto aqui — sem voltar pelo WhatsApp e <strong>sem poder ser editado depois</strong>.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 mb-3 space-y-2">
              <p className="font-bold">Como configurar (1 vez só, ~5 min):</p>
              <ol className="list-decimal pl-4 space-y-1">
                <li>
                  Abra{' '}
                  <a
                    href="https://script.google.com/home"
                    target="_blank"
                    rel="noreferrer"
                    className="underline font-semibold"
                  >
                    script.google.com
                  </a>{' '}
                  e crie um novo projeto.
                </li>
                <li>
                  Cole o código do Apps Script que está no arquivo{' '}
                  <code className="bg-white px-1 rounded">INSTRUCOES-GOOGLE-SHEETS.md</code> (na
                  raiz do app).
                </li>
                <li>
                  Clique em <strong>Implantar &gt; Nova implantação</strong>, escolha "App da
                  Web", acesso "Qualquer pessoa".
                </li>
                <li>Copie a URL gerada (termina em <code>/exec</code>) e cole abaixo.</li>
              </ol>
            </div>
            <label className="block text-xs font-semibold mb-1">
              URL do Apps Script
            </label>
            <input
              type="url"
              value={sheetsEndpointInput}
              onChange={(e) => setSheetsEndpointInput(e.target.value)}
              placeholder="https://script.google.com/macros/s/.../exec"
              className="w-full px-3 py-2 rounded-lg border border-[#009b3a]/25 mb-1 text-sm"
            />
            <p className="text-[11px] text-neutral-500 mb-4">
              Deixe vazio para voltar ao envio pelo WhatsApp.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setModalSheetsAberto(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-neutral-100 hover:bg-neutral-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={salvarSheetsEndpoint}
                className="px-4 py-2 rounded-lg text-sm font-bold bg-[#009b3a] text-white hover:bg-[#007a2e]"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
